import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import {
  AccountModel,
  AccountRequestV2Contribution,
  AccountRequestV2Distribution,
  AccountRequestV2FundingSleeve,
  AccountRequestV2ModelBase,
  AccountRequestV2ModelAllocation,
  AccountRequestV2Payee,
  AccountRequestsV2Client,
  AccountRequestV2Snapshot,
} from 'src/app/core/clients/generated/client';
import { ToastClassEnum } from 'src/app/core/services/snackbar/snackbar.models';
import { SnackbarService } from 'src/app/core/services/snackbar/snackbar.service';
import {
  AllocationRow,
  BuyRow,
  BuyRowView,
  AllocationRowView,
  AllocationTotals,
  ReallocationV2Data,
  ManagedModelRowView,
  ModelAllocationSelection,
  ModelOption,
  ResultingHolding,
  SecurityOption,
  SleeveAllocation,
  SleeveAmountRow,
  SleeveAmountRowView,
  PositionView,
} from '../models/reallocation-v2.models';
import {
  buildModelLookup,
  OTHER_STANDING_INSTRUCTION_KEY,
  ReallocationV2DataService,
  ReallocationV2LoadError,
} from '../services/reallocation-v2-data.service';

/**
 * Reactive-forms group backing a single CASH row's inputs (step 1).
 *
 * ⚠️ Deliberately not a {@link PositionForm}. A cash row has no share count and no price -- Orion
 * reports both as null -- so the sell machinery cannot express "use $5,000 of it": `soldValue`
 * falls back to the whole market value and `isSellAll` is forced true. Cash needs a DOLLAR
 * control, which is the one thing `sharesToSell` is not.
 */
/**
 * Whether a cash row was filled by its button.
 *
 * ⚠️ ONE CONTROL, NOT TWO BOOLEANS. The buttons are mutually exclusive by construction -- a row
 * is filled from what it can give or from what is outstanding, never both -- and a pair of flags
 * makes the impossible state representable and then reachable.
 *
 * 🔴 WHICH BUTTON A ROW OFFERS IS TYPE-SWITCHED, and the two are not alternatives on one screen:
 * 'remaining' is DISTRIBUTION-ONLY and 'available' is everything else. A distribution is paying
 * money OUT, so cash is a last resort offered only where it settles the request; a reallocation
 * is not spending the cash at all, only moving it into the pool to be placed, so there is nothing
 * to discourage and USE AVAILABLE is simply the fast path (AndyP, 2026-09-25).
 */
export type CashFillMode = 'none' | 'remaining' | 'available';

export type CashUseForm = FormGroup<{
  use: FormControl<boolean>;
  /**
   * Which of the two fill buttons is currently pressed, or 'none'.
   *
   * 🔴 CLEARED BY ANY MANUAL EDIT, in the amount subscription. A pressed button asserts "this
   * figure is the row's whole balance" or "this figure covers what is outstanding"; the moment
   * the advisor types over it, neither is necessarily true and a button still lit is a lie.
   *
   * ⚠️ The buttons therefore set this AFTER writing the amount, not before -- the write fires
   * the subscription that clears it.
   */
  fill: FormControl<CashFillMode>;
  /**
   * Dollars of this row the advisor is releasing toward the request.
   *
   * Nullable for the same reason `sharesToSell` is: Angular reports a cleared number input as
   * null, and coercing that to 0 refills a box the advisor just emptied. Every reader treats
   * null as 0.
   */
  amount: FormControl<number | null>;
}>;

/** Reactive-forms group backing a single position's editable inputs (step 1). */
/**
 * Which button filled a position row, if either. The positions counterpart of
 * {@link CashFillMode}, and deliberately the same shape.
 *
 * ⚠️ ONE CONTROL, NOT TWO BOOLEANS -- see the note on `CashFillMode`. It replaced a plain
 * `sellAll: boolean`; `isRowSellAll` still exists and still answers the same question, so
 * everything reading that is unaffected.
 *
 * 🔴 'one' IS 'remaining' WITH A DIFFERENT LABEL, and it is stored rather than re-derived so the
 * button does not rename itself under the cursor. Both are the same action -- cover what is
 * outstanding -- but when a single share costs MORE than the remainder, "SELL FOR REMAINING"
 * describes something the row cannot do. Recording which one was pressed keeps the label honest
 * afterwards, when the arithmetic that chose it has been consumed by the press itself.
 */
export type PositionFillMode = 'none' | 'all' | 'remaining' | 'one';

export type PositionForm = FormGroup<{
  selected: FormControl<boolean>;
  /**
   * The row is selling its WHOLE position, by the advisor's explicit say-so.
   *
   * ⚠️ STORED, not derived from `sharesToSell >= shares`. The derived version cannot tell an
   * advisor who typed the full lot from one who pressed the button, and it would be permanently
   * true on a reallocation -- where rows REST at the full lot -- which would lock every share
   * box on a piloted screen.
   *
   * 🟢 `sellsEntirePosition` in {@link positions} still reads the VALUE, so a hand-typed full lot
   * is still filed as Sell All. This flag decides the control, not the instruction.
   */
  fill: FormControl<PositionFillMode>;
  /**
   * Shares the user wants to sell (defaults to the full share count).
   *
   * Nullable because clearing the box has to leave it cleared: Angular reports an empty
   * number input as `null`, and coercing that to 0 refills a field the advisor just
   * emptied. Every reader treats null as 0 -- see `positions`.
   */
  sharesToSell: FormControl<number | null>;
}>;

/** Floating-point tolerance used for all "totals must equal 100 / X" checks. */
const EPS = 0.005;

/**
 * Release Distribution % and Contribution % back onto the target.
 *
 * Spread into a row whenever its TARGET is stated -- by `editTarget`, or by `editValue`, which
 * is a target edit in dollars. Clearing both flags IS the whole operation: with neither set,
 * `effectiveDistribution` reads the target directly, so there is no figure left to disagree
 * with it.
 *
 * ⚠️ It DISCARDS a manual figure, which is a deliberate reversal (AndyP, ACMTECH-969). The old
 * rule kept one alive forever -- "a typed figure is a deliberate instruction and outlives a
 * target edit" -- and left two columns stating a split the advisor had just moved away from.
 * Changing the mix is now taken as the later instruction; typing the splits again afterwards is
 * how they get overridden.
 *
 * A function rather than a shared constant so no caller can spread one object twice and find it
 * was mutated in between.
 */
function clearDerivedSplits(): {
  distributionManual: false;
  distributionSeeded: false;
  contributionManual: false;
  contributionSeeded: false;
} {
  return {
    distributionManual: false,
    distributionSeeded: false,
    contributionManual: false,
    contributionSeeded: false,
  };
}

/**
 * The parts of an allocation row that constitute a *change* the advisor made.
 *
 * Deliberately the **effective** percentages rather than the raw `AllocationRow`, whose
 * `targetExact` and `touched` are internal bookkeeping — comparing those
 * would report a change when someone merely clicked into a field and back out.
 */
/**
 * What has to happen before a model selection can be committed to a row.
 *
 * A three-way answer rather than a boolean, because the two refusals are different: one
 * needs the advisor's confirmation and one cannot be confirmed at all. `message` is
 * advisor-facing copy, built in the store — see {@link ReallocationV2Store.smaSelectionCheck}.
 */
export type SmaSelectionCheck =
  /** Not an SMA. Commit it. */
  | { kind: 'none' }
  /** An SMA this account cannot go into. Revert the selection and say why. */
  | { kind: 'blocked'; message: string }
  /** An SMA this account qualifies for. Ask, then commit or revert. */
  | { kind: 'confirm'; message: string; question: string };

/**
 * A question worth asking before an action is carried out (ACMTECH-1039).
 *
 * The confirm half of {@link SmaSelectionCheck} with the blocked half left off, and the
 * same division of labour: the STORE decides and writes the words, the COMPONENT owns
 * the dialog. Neither user of this can refuse -- the advisor may always proceed, so
 * there is no `blocked` to express.
 */
export type ConfirmCheck =
  /** Nothing to ask. Carry on. */
  | { kind: 'proceed' }
  /** Ask first, and carry on only if they say yes. */
  | { kind: 'confirm'; message: string; question: string };

interface AllocationFingerprint {
  modelId: string;
  target: number;
  distribution: number;
  contribution: number;
  /**
   * The dollars, which since ACMTECH-944 can move on their own.
   *
   * ⚠️ Percentages alone are not enough any more. Values used to be derived from them, so a
   * dollar amount could not change without a percentage changing too. Now values are what the
   * advisor sets and what gets pinned — and on a single-model account, moving $50,000 into the
   * model leaves it at 100% of the mix either way. The request changed; the fingerprint did
   * not, and Submit reported "no changes to submit".
   *
   * `allocatedValue`, the derived figure, so clicking into the box and back
   * out without editing still compares equal.
   */
  allocatedValue: number;
}

function fingerprintAllocation(row: AllocationRowView): AllocationFingerprint {
  return {
    modelId: row.modelId,
    target: row.effectiveTarget,
    distribution: row.effectiveDistribution,
    contribution: row.effectiveContribution,
    allocatedValue: row.allocatedValue,
  };
}

/**
 * Identity for a security across the wizard — kept positions, sold positions and buys.
 *
 * Prefers Eclipse's `securityId` and falls back to a normalised ticker. One function so
 * the merge on Do Not Trade and the sell/buy conflict check cannot disagree about
 * whether two rows are the same security.
 */
function holdingKey(securityId: number | null | undefined, ticker: string): string {
  return securityId != null ? `id:${securityId}` : `ticker:${ticker.trim().toUpperCase()}`;
}

function sameAllocation(a: AllocationFingerprint, b: AllocationFingerprint): boolean {
  return (
    a.modelId === b.modelId
    && Math.abs(a.target - b.target) < EPS
    && Math.abs(a.distribution - b.distribution) < EPS
    && Math.abs(a.contribution - b.contribution) < EPS
    // A cent, not EPS: EPS is a half-cent tolerance for percentage columns answering to 100,
    // and dollars need a threshold that survives the cent of rounding drift the last row absorbs.
    && Math.abs(a.allocatedValue - b.allocatedValue) < 0.01
  );
}

/**
 * Feature flag for the "Attach Files" step, which sits between Allocate to Models and
 * Review & Submit.
 *
 * On, the wizard is 6 steps and Review is step 6. Off, the step is absent entirely
 * and Review is step 5 again. Nothing else needs changing either way: the step list
 * below is the single source of step numbering, so the indicator, the body switch
 * and the navigation guards all follow from it.
 *
 * Attachments submitted while this is on are persisted by CRM against the request's
 * task, and surface in both the portal request detail and the CRM modal's FILES
 * panel. Turning it on without that server-side support would discard them silently.
 *
 * 🟢 ON (AndyP, 2026-10-01), for all four request types. That server-side support was
 * confirmed before flipping it rather than assumed: `CreateAccountRequestV2Async` writes each
 * part to disk, adds an `ORM.Investment.File` row against the account's directory, ties them to
 * the task through an `InvestmentAccountUpload`, and leaves an internal-only note recording the
 * count. It also resolves the directory, request type and recipient BEFORE saving anything, so a
 * misconfigured account fails the whole submit instead of filing a request whose attachments
 * silently went nowhere — which is the failure this comment was warning about.
 */
export const REALLOCATION_V2_FILE_UPLOAD_STEP_ENABLED = true;

/**
 * Feature flag for the request-notes box, which shares the same step as the file
 * upload. Independent of {@link REALLOCATION_V2_FILE_UPLOAD_STEP_ENABLED}: either
 * flag alone is enough for the step to appear, and it is hidden only when both are
 * off. The step's label follows whichever sections are on.
 *
 * The note is persisted as the request task's description, which is what the
 * "Request Notes" detail reads on both the portal tracker and the CRM modal.
 *
 * 🔴 STAYS OFF while uploads go on (AndyP, 2026-10-01) — asked for explicitly, not overlooked.
 * The two are flagged separately for exactly this, and the step label follows: it reads
 * "Attach Files" rather than "Files & Notes" with only this one off.
 */
export const REALLOCATION_V2_REQUEST_NOTES_ENABLED = false;

/**
 * Feature flag for step 1's **Unrealized G/L** column.
 *
 * Off by decision, not by defect: the figure is correct and the pipeline behind it stays
 * wired, but it was cut from Raise Cash for both request types before the pilot. It
 * may come back, so the column is switched off rather than deleted.
 *
 * Nothing else is gated on this. `reportedGainLoss` still loads from Orion, `gainLoss` is
 * still derived, and `buildRequest()` still sends it — so turning this back on restores the
 * column with live values and needs no other change. Total Cost is deliberately NOT covered
 * by it; that column stays.
 *
 * /!\ The service still treats the account-scoped holdings call as unusable partly BECAUSE
 * it nulls the gain/loss fields (see `loadPositions`). That reasoning predates this flag and
 * is not conditional on it — the same call also nulls `costAmount`, which Total Cost needs.
 */
export const REALLOCATION_V2_UNREALIZED_GAIN_LOSS_COLUMN_ENABLED = false;

/**
 * Whether SELL ALL POSITIONS is offered above the securities table **on a distribution**.
 *
 * 🔴 OFF, and only for that type. It almost never applies there: it makes sense only when every
 * sellable position TOGETHER still would not cover what is being distributed, which on any
 * account large enough to distribute from is rare. The rest of the time it offers to liquidate
 * the whole account to raise a fraction of it, and the per-row buttons already cover the useful
 * cases better (AndyP, 2026-09-24).
 *
 * 🟢 IT IS ON EVERYWHERE ELSE, and not through this flag -- see
 * {@link ReallocationV2Store.showSellAllPositionsButton}. A reallocation frees the account up and
 * then places it, so "sell everything" is a real opening move; it is also what replaced ticking
 * the header checkbox (AndyP, 2026-09-25).
 *
 * ⚠️ If it does come back on a distribution, gate it on the condition above rather than simply
 * flipping this -- offering it unconditionally is what made it wrong there.
 */
export const REALLOCATION_V2_SELL_ALL_POSITIONS_ENABLED = false;

/**
 * The rule an SMA imposes, in the words four surfaces already share.
 *
 * ⚠️ A CONSTANT because two branches of {@link ReallocationV2Store.smaLockMessage} now open with
 * it and only one of them continues. The add dialog, the remove dialog and the Review panel carry
 * the same sentence in their own templates; pulling those in here was not done as part of
 * ACMTECH-994 and is worth doing when one of them next changes.
 */
/**
 * What a destination with nothing on file obliges, said where the advisor is standing.
 *
 * 🔴 **THE METHOD NAMES ITSELF** (AndyP, 2026-11-02). These were four fixed sentences opening
 * *Third-party wire instructions required*, which was wrong twice over once the recipient
 * question became the New/Other row: the trigger is no longer that the payee is a third party —
 * a client's own new bank account has no standing authorization either — and it is no longer
 * only a wire. `req.DeliveryMethod.NewRecipientRequiresDocument` decides which methods ask, so
 * ACH and Journal Cash can be switched on as a row edit, and the sentence has to read correctly
 * when they are.
 *
 * ⚠️ The name comes from the row, so it is whatever the lookup says — *Journal Cash to Receiving
 * Account #* included. Long, and correct, which is the trade worth making: an advisor reading
 * *Wire Transfer instructions required* on a request they filed as a journal would be right to
 * distrust the rest of the screen.
 *
 * Three surfaces, differing only in the last clause, because it depends on where the reader is:
 * naming the Documents step is a place to go, pointing at the upload is a control to use.
 */
export const instructionsToDocumentsStep = (method: string): string =>
  `${method} instructions required -- please attach instructions to the Documents step.`;

/**
 * The same obligation as the DOCUMENTS STEP says it.
 *
 * ⚠️ It points at the control below it rather than naming the step, because the reader is already
 * standing on it — the shared wording sent them to the Documents step while the drop zone sat six
 * inches underneath (AndyP, 2026-11-02).
 *
 * Replaced by a confirmation once a file lands: this is the one surface where the advisor can
 * act, so it is the one that changes state under their hand.
 */
export const instructionsOnThisStep = (method: string): string =>
  `${method} instructions required -- please attach instructions using the file upload below.`;

/**
 * The same obligation for the world where the Documents step is switched off.
 *
 * 🔴 NOT COSMETIC. The other two name a step that would not exist, and tell the advisor to do
 * something before submitting that they cannot do at all. With the upload flag off nothing blocks
 * either — see {@link ReallocationV2Store.newRecipientDocumentMissing} — so an instruction
 * phrased as a gate would be false as well as unactionable.
 */
export const instructionsOffline = (method: string): string =>
  `${method} instructions required. The signed instructions must reach the trade team separately.`;

/**
 * What a cheque obliges instead: an address, typed here.
 *
 * ⚠️ Said *a check sent to a third party* until 2026-11-02. The advisor may well be posting it to
 * the client themselves at an address Schwab does not hold, and calling that a third party put a
 * claim in front of them that they had not made and might not agree with.
 */
export const NEW_RECIPIENT_PAYEE_REQUIRED =
  'Payee details required. A check sent to an address not on record needs the payee’s name and '
  + 'mailing address.';

const SMA_COMMINGLE_RULE =
  'Separately Managed Accounts (SMAs) are 100% of the account and cannot be commingled '
  + 'with other models or assets. All buys and sells are executed by an external manager.';

/** True when the shared Files/Notes step has at least one section to show. */
export const REALLOCATION_V2_ATTACHMENTS_STEP_ENABLED =
  REALLOCATION_V2_FILE_UPLOAD_STEP_ENABLED || REALLOCATION_V2_REQUEST_NOTES_ENABLED;

/**
 * Feature flag for the Buy Additional Securities step, which sits between Review/Sell
 * Positions and Do Not Trade.
 *
 * Off, the wizard is exactly what it was before buys existed. That is not just a hidden
 * step: `buys()` returns an empty list, so the investable arithmetic, the Do Not Trade
 * aggregation, Review & Submit and the change detection all fall back automatically.
 * Everything downstream reads `buys()` rather than checking this flag, so no derivation
 * can drift out of step with it.
 *
 * On, because nothing in this wizard can submit yet — `submit()` is deliberately inert
 * until ACMTECH-908 — and the tile itself is gated behind a permission nobody holds. So
 * the step carries no more risk than the rest of the workflow, and being on means its
 * arithmetic is exercised by the suite rather than sitting untested behind a `false`.
 *
 * ✅ **Safe to flip in either direction.** The submit payload always carries a `buys`
 * collection, empty or not, so the flag never changes the request's shape — only whether
 * the advisor can put anything in it. An earlier draft had the payload omit the field
 * entirely when unused, which would have made turning this on before slice 8 silently
 * drop an advisor's buys behind a success message.
 */
export const REALLOCATION_V2_BUY_STEP_ENABLED = true;

/**
 * Whether a DISTRIBUTION refuses to sell a position whose proceeds are held for review.
 *
 * ⚠️ A DISTRIBUTION ONLY, and that is the whole justification. `SELLABLE_SECURITY_TYPE_IDS` was
 * removed on purpose (2026-09-04): an advisor holding a bond can perfectly well liquidate it, and
 * locking the checkbox told them they could not. That reasoning still stands for a reallocation,
 * where the sale is legitimate and only the PROCEEDS are restricted -- they cannot fund a buy or
 * a model inside the same request, which is a limit on the money's destination, not on the trade.
 *
 * A distribution is the case where those two collapse into one. The request's only purpose is to
 * send money out; proceeds that cannot fund it accomplish nothing, so selling the position is not
 * a restricted action but a pointless one. The row's own tooltip already says the proceeds are
 * "not immediately available to fund this distribution" -- this stops the wizard accepting them
 * as a source in the same breath (AndyP, 2026-09-22).
 *
 * 🛑 IT IS ALSO FIXING A REAL CONTRADICTION, not only tidying the UI. A distribution's
 * `unappliedValue` subtracts `totalSoldValue`, which counts EVERY sell including the reviewable
 * ones -- so before this an advisor could source a distribution from a note sale, watch the
 * outstanding figure fall to zero, and file a request whose money cannot actually go out.
 *
 * 🟢 A FLAG rather than a hardcoded rule, matching `BUYABLE_SECURITY_TYPE_IDS`' pause lever
 * (AndyP, 2026-09-22). This is the third revision of what may be sold and the second reversal,
 * so the next change should be one edit rather than an archaeology exercise. Set it false and
 * every reviewable position is selectable again exactly as it is on a reallocation; nothing else
 * has to be touched, because the lock is applied in one place.
 */
export const REALLOCATION_V2_DISTRIBUTION_LOCKS_REVIEWABLE_SELLS = true;

/**
 * Share of the Custom Holdings sleeve that must stay in cash.
 *
 * A firm policy, not a preference: a request may not leave the sleeve with less than this in
 * cash, so step 1 caps what the advisor can release.
 *
 * 🛑 1%, AND THE SCREEN SAYS 0.5% IS THE ENFORCED MINIMUM. Neither is a mistake, and it is
 * the first thing a reader will try to "fix". Step 1's heading icon carries the firm's general
 * policy -- target 1%, enforce 0.5% -- while THIS is what a REQUEST may leave behind, and the
 * trade team's instruction is that a request holds to the 1% target rather than the 0.5% floor
 * (AndyP, 2026-09-18). The floor exists for accounts that drift; it is not a budget a request
 * is allowed to spend into.
 *
 * Dropping this to 0.005 to match the tooltip would let every request leave the sleeve at half
 * the intended cash, and nothing downstream would object.
 *
 * 🔴 A DISTRIBUTION IS EXEMPT ENTIRELY -- see {@link ReallocationV2Store.cashTargetHold}, which
 * returns 0 for one. That is a deliberate narrowing of the paragraph above rather than a
 * contradiction of it: the target governs how a request RESTRUCTURES a sleeve, not how much of
 * their own money a client may withdraw. A close account already releases the whole balance, so
 * the exemption makes an ordinary distribution consistent with the extreme case.
 *
 * 🟢 THE TRADE TEAM HAS CONFIRMED IT (AndyP, 2026-09-25), so this is their instruction now
 * rather than a relaxation of it awaiting sign-off. Their 2026-09-18 wording -- a request holds
 * to the 1% target -- was given about RESTRUCTURING requests and stands unchanged for those; the
 * distribution exemption is a separate answer to a question they had not been asked.
 *
 * ⚠️ SO DO NOT REOPEN IT AS AN OVERSIGHT. The case that was put, and accepted: the hold
 * makes the wizard SELL SECURITIES to reach cash the account already holds. See `cashTargetHold`
 * for the worked example.
 *
 * ⚠️ APPLIED AS `basis / 99`, NOT `basis * 0.01` -- see {@link ReallocationV2Store.cashTargetHold}.
 * The cash is itself part of the sleeve, so the requirement is self-referential and solving it
 * moves the divisor. `* 0.01` under-holds by about 1% of the hold, which is small enough to pass
 * review and still fail the policy it exists to enforce.
 */
export const CASH_TARGET_HOLD_RATE = 0.01;

/**
 * What share of a model sleeve has to stay invested when the model carries no
 * investment minimum on record -- so Amount to Use on Raise Cash caps at 99%.
 *
 * ⚠️ The same 0.01 as {@link CASH_TARGET_HOLD_RATE} TODAY, and deliberately its own
 * constant rather than a reference to it (AndyP, 2026-10-28). Two reasons, both from
 * that one's doc block:
 *
 *   - It carries a dated trade-team sign-off about how much CASH a sleeve retains.
 *     Reading it here would make that approval appear to cover a rule the trade team
 *     has never been asked about.
 *   - It is applied as `basis / 99`, not `basis * 0.01`, because the held cash is
 *     itself part of the sleeve and the requirement is self-referential. This floor
 *     has no such recursion: it is a flat share of what the sleeve holds. Sharing the
 *     symbol invites sharing the formula, and that formula is wrong here.
 *
 * Expected to move independently of the cash target.
 */
export const MODEL_DRAW_FLOOR_RATE = 0.01;

/**
 * The custodian's primary cash balance, held before any other cash row -- see
 * {@link ReallocationV2Store.cashRowHolds}.
 *
 * ⚠️ NOT a test for "is this cash". That is Orion's `isCash` flag, which the loader already
 * splits on, and it covers money-market and sweep vehicles (SWGXX, FDRXX, SchwabCash...) that
 * count toward the target exactly as this one does. This constant only orders the hold.
 *
 * ⚠️ The portal's own mock still tickers this row `CASH`, which is what Orion sends for the
 * synthetic lines rather than for a real holding. Demo mode therefore exercises the fallback
 * ordering rather than this rule.
 */
export const CUSTODIAL_CASH_TICKER = 'CUSTODIAL_CASH';

/**
 * Feature flag for the Do Not Trade step.
 *
 * Off, and not because the step is unfinished: ACMTECH-893 could not settle on how an
 * advisor should see and set per-position preferences in either request type, so the
 * surface is withdrawn while the logic behind it is kept whole.
 *
 * ✅ **Safe to flip in either direction, and inert while off.** `dnt` starts empty and is
 * written only by `toggleDnt` / `toggleDntAll`, whose one production caller is the step
 * itself; nothing seeds it from Orion. So with the step off nothing can be marked,
 * `markedDoNotTrade()` stays empty, and `dntConfirmationRequired()` is permanently false --
 * the attestation gate, the footer message and the Back title all go quiet on their own
 * rather than needing this flag checked. The payload still carries `isDoNotTrade` on every
 * custom holding, so the request shape does not change either.
 *
 * ⚠️ Flipping this back on does NOT restore everything. The wizard comes back on its own --
 * the step, and the review-submit table column, which is gated on this flag via
 * `showDoNotTrade`. Two read-only display surfaces were stripped by hand instead of flagged
 * and have to be re-added deliberately:
 *   1. the CRM request-tracker request-details DO NOT TRADE column (both request types), and
 *   2. the Do Not Trade row in the portal request tracker Custom Holdings section
 *      (`request-tracker/modals/request-tracker-detail-modal/...component.html`) -- left
 *      unflagged to keep the tracker free of a dependency on this store.
 */
export const REALLOCATION_V2_DO_NOT_TRADE_STEP_ENABLED = false;

/**
 * `req.AccountRequest.AccountRequestTypeID` — which kind of request is being filed.
 *
 * Named rather than left as bare 1 / 2 because the value is written to a database column,
 * read back by CRM, and decides which task queue the trade team sees it in. It is also
 * the single field that makes an Initial Account request distinguishable from a
 * Reallocation once both are filed through the same writer, and getting it wrong fails
 * silently — the POST succeeds either way.
 *
 * ⚠️ Close Account is NOT a fifth type. It is a Distribution carrying
 * `req.Distribution.IsCloseAccount`, so it shares this type, its task category excepted.
 */
export enum AccountRequestType {
  InitialAccount = 1,
  Reallocation = 2,
  Contribution = 3,
  Distribution = 4,
}

/**
 * 🔴 **There are no transaction-type literals in this file any more**, and their absence is the
 * point of ACMTECH-1036.
 *
 * `CHECK_TRANSACTION_TYPE` and `WIRE_TRANSACTION_TYPE` lived here and answered questions they had
 * no business answering: *is overnight delivery offered*, *does a bank account apply*, *is a
 * document required*. Those are facts about a delivery method, and they are now columns on
 * `req.DeliveryMethod` — `usesBankAccount`, `usesAddressBook`, `allowsOvernightDelivery`,
 * `newRecipientRequiresDocument`, `newRecipientRequiresPayee`.
 *
 * What made it urgent: the codes were derived upstream from the method's NAME, so relabelling the
 * wire row switched the whole chain off silently and requests filed with no delivery instruction
 * at all. Deriving behaviour from an editable label is the fault; moving the derivation to a
 * sturdier place in the client would only have hidden it.
 *
 * `transactionType` still arrives on the row and is still used — to match a method against the
 * account's Schwab rows, which is a join key and not a rule. See `filteredStandingInstructions`.
 */

/** Identifies a wizard step independently of its position, which the flag can shift. */
export type ReallocationV2StepId =
  | 'funding'
  | 'sleeveAmounts'
  | 'positions'
  | 'buys'
  | 'doNotTrade'
  | 'protectedCash'
  | 'allocation'
  | 'files'
  | 'review';

/**
 * Signal store for the Reallocation workflow (V2, ACMTECH-902).
 *
 * Ownership split (see feature plan):
 *  - **Reactive forms** are the input layer for the independently-edited fields:
 *    the positions grid (`positionsForm`) and the custodial-cash field
 *    (`cashControl`). They funnel raw user input.
 *  - **Signals** are the single source of truth for everything derived — % of
 *    account, gain/loss, kept shares/value, Custom Holdings value, investable
 *    amount, and the allocation grid (whose three interdependent % columns are
 *    derived with pure methods rather than through a form, exactly as the mockup does).
 *
 * Provided at the root workflow component so its lifetime matches one modal open.
 */
@Injectable()
export class ReallocationV2Store {
  private readonly dataService = inject(ReallocationV2DataService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly snackbar = inject(SnackbarService);
  /**
   * The one client this wizard POSTs to, for BOTH request types — the type is carried in the
   * payload, not chosen by picking a different endpoint.
   *
   * The separate Initial Account wizard and its `createInitialAccountRequest` endpoint were
   * removed in ACMTECH-918. Worth stating because the failure mode if a second per-type client
   * ever reappears is silent: the POST succeeds and the request is simply filed under the wrong
   * AccountRequestTypeID, so it lands on the wrong CRM task and the wrong tracker category.
   */
  private readonly requestClient = inject(AccountRequestsV2Client);

  // ---- Reactive-forms input layer -----------------------------------------
  /** One group per position; built in `initialize()` once data arrives. */
  readonly positionsForm = new FormArray<PositionForm>([]);
  /**
   * One group per CASH row; built in `initialize()` alongside `positionsForm`.
   *
   * ⚠️ A SEPARATE array, index-paired with `data.cashPositions`, rather than cash rows joining
   * `data.positions`. Row-to-control pairing in this wizard is by INDEX and nothing else --
   * `positions()` maps `data.positions[i]` to `positionsForm.at(i)`, and `positionGroup()` and
   * `toggleSellAll()` do the same. Appending cash to that array would shift every index and
   * rewire every share input; the invariant is spelled out on `orderForStepOne` in the data
   * service. Two arrays, two invariants, neither touching the other.
   */
  readonly cashForm = new FormArray<CashUseForm>([]);
  /** Custodial cash dollar amount set aside (step 3); becomes the CASH holding. */
  /**
   * Custodial cash to set aside.
   *
   * ⚠️ `updateOn: 'blur'`, not the default 'change'. A reactive control emits on every
   * keystroke, so the strip's INVESTABLE figure recalculated per character: an advisor
   * replacing 50,000 with 500,000 watched it swing through 5, 50, 500 and flash a block on
   * the way past, and {@link normalizeCash} rewrote the box underneath them while they were
   * still typing in it. Committing on blur means the figure moves once, when the number is
   * actually a number.
   *
   * The DOM input is untouched between blurs, so the advisor still sees what they type — only
   * the model waits. Programmatic `setValue` is unaffected and emits immediately, which is
   * what {@link commitToSma} and the specs rely on.
   */
  readonly cashControl = new FormControl<number>(0, { nonNullable: true, updateOn: 'blur' });

  /**
   * Step 1 of a Contribution or Distribution: the amount, and how the money travels.
   *
   * Unused by types 1 and 2, which have no funding step — the account itself is the pool.
   *
   * ⚠️ The money fields commit on **blur**, matching {@link cashControl} and for a sharper
   * version of the same reason: the gross/net conversion writes back into the sibling box, so
   * on every keystroke it would rewrite the field the advisor is typing in. The selects and
   * checkboxes commit on change, because a discrete choice has no half-typed state.
   */
  readonly fundingForm = new FormGroup({
    /** What the advisor typed. Gross or net per {@link entryMode}; always gross for a close. */
    amount: new FormControl<number | null>(null, { updateOn: 'blur' }),
    /**
     * Whether {@link amount} is the figure leaving the account or the figure the client
     * receives. Distribution only — a contribution has no withholding, so nothing to net.
     */
    entryMode: new FormControl<'gross' | 'net'>('gross', { nonNullable: true }),
    federalTaxPct: new FormControl<number | null>(null, { updateOn: 'blur' }),
    stateTaxPct: new FormControl<number | null>(null, { updateOn: 'blur' }),
    /** Distribution only: liquidate everything and close the account. Collapses the step list. */
    isCloseAccount: new FormControl<boolean>(false, { nonNullable: true }),
    /**
     * Whether to bill the account on its close date. Only meaningful while
     * {@link isCloseAccount} is set, and defaults to **true** to match the v1 close-account
     * workflow's `billAccountOnClosing`, so moving to V2 does not silently reverse the answer.
     */
    isCloseDateBilled: new FormControl<boolean>(true, { nonNullable: true }),
    deliveryMethodId: new FormControl<number | null>(null),
    deliveryFrequencyId: new FormControl<number | null>(null),
    /**
     * Send the check overnight. Only meaningful for a check-delivered method.
     *
     * `false` rather than null when it does not apply, because the column is `bit NOT NULL` on
     * both `req.Contribution` and `req.Distribution`. That is readable only because the delivery
     * method sits beside it: on an ACH request `false` means the question never arose, not that
     * the advisor declined overnight delivery.
     */
    isOvernightDelivery: new FormControl<boolean>(false, { nonNullable: true }),
    /**
     * The chosen standing authorization, keyed by its `displayValue`.
     *
     * A key rather than the object: `StandingInstruction` arrives from Schwab with no id of
     * its own, and `displayValue` is what the request stores in
     * `req.Contribution/Distribution.DeliveryInstruction` anyway — so the form holds exactly
     * what is submitted, and there is no second representation to keep in step.
     */
    standingInstructionKey: new FormControl<string | null>(null),
    startDate: new FormControl<Date | null>(null),
    /**
     * Who the money is going to — First Party or Third Party.
     *
     * 🔴 **NO DEFAULT.** Two radios, neither pre-selected, and the step will not move on until
     * one is chosen. A pre-ticked First Party would be the form declaring on the advisor's
     * behalf that money is not leaving the family, which is the one thing this question exists
     * to establish.
     *
     * ⚠️ On the form ROOT, not inside `payee`. Asked by any method whose `asksRecipientType`
     * flag is set — Check **and Wire Transfer** — and a wire collects no payee fields at all, so
     * a control nested in that group would be unreachable for half the methods that ask.
     * `resetRecipientForMethod` clears it with the payee, because both are assertions about a
     * destination that has just changed.
     */
    deliveryRecipientTypeId: new FormControl<number | null>(null),
    /**
     * Where a third-party check is posted. Collected ONLY for Check + Third Party.
     *
     * A nested group rather than six more flat controls, so "forget the payee" is one `reset()`
     * at the one place the question stops applying, rather than six assignments that can drift
     * apart. ⚠️ A WIRE to a third party fills none of this — it carries an uploaded instruction
     * document instead, and the two paths ask for exactly one thing each.
     *
     * Every field but `addressContinued` is required while the block is shown; none of them is
     * validated against anything, because nothing here is looked up — it is stored and shown as
     * the advisor typed it.
     *
     * ⚠️ `deliveryRecipientTypeId` is NOT in this group. It was for one day; a WIRE asks the
     * same question and collects none of these fields, so a control living inside the payee
     * group could not be reached for a wire at all. It sits on the form root and is cleared by
     * `resetRecipientForMethod` alongside this group.
     */
    payee: new FormGroup({
      payeeName: new FormControl<string>('', { nonNullable: true }),
      streetAddress: new FormControl<string>('', { nonNullable: true }),
      addressContinued: new FormControl<string>('', { nonNullable: true }),
      city: new FormControl<string>('', { nonNullable: true }),
      state: new FormControl<string>('', { nonNullable: true }),
      zip: new FormControl<string>('', { nonNullable: true }),
    }),
  });

  // `cashTakenControl` stood here: one total for the cash a distribution draws, written by a
  // panel above the positions table. Both went on 2026-09-17 -- a distribution now uses the
  // per-position release rows (`cashForm`) the reallocation wizard already had, so there is one
  // control for custodial cash instead of two shapes offering the same money.
  //
  // /!\ `cashControl` -- step 4's set-aside -- is NOT the same thing and stays. The old doc here
  // spelled out why, and it still holds: that one is money the advisor PROTECTS from being
  // invested, this was money they SPEND. Sharing a control would have meant one number with two
  // signs. What replaced it is not that control; it is the release rows, which carry the same
  // opposite intent per position.

  // ---- Canonical signal state ----------------------------------------------
  readonly isLoading = signal(false);
  /**
   * Which wave of the initial load is in flight, so the wizard can say what it is
   * waiting on rather than showing an unlabelled spinner. Three sequential waves —
   * see {@link ReallocationV2LoadStage}.
   */
  readonly loadStage = computed(() => this.dataService.loadStage());
  /** True when the initial load failed; the template shows an error state. */
  readonly loadError = signal(false);
  /** User-facing message for the load-error state. */
  readonly loadErrorMessage = signal('There was an error loading the account setup form. Please try again.');
  readonly data = signal<ReallocationV2Data | null>(null);
  /**
   * When the account data was read from Orion — NOT when Submit is pressed.
   *
   * The wizard can sit open: loaded 09:00, submitted 09:40, and every figure in it
   * describes 09:00. This is the timestamp the audit snapshot and each row's `priceAsOf`
   * carry, because ACMTECH-903's staleness check needs the moment the picture was taken.
   * Conflating it with the submit time makes every request look fresher than it is.
   */
  readonly loadedAt = signal<Date | null>(null);
  readonly currentStep = signal(1);
  /** Tickers marked "Do Not Trade" (step 2). */
  readonly dnt = signal<ReadonlySet<string>>(new Set<string>());
  /** Raw allocation-row state (step 4); displayed values are derived below. */
  readonly allocationRows = signal<AllocationRow[]>([]);
  /**
   * Dollars being freed out of the model sleeves on Raise Cash (ACMTECH-1039).
   *
   * 🔴 ONE AMOUNT FOR ALL THE MODELS, raised across them IN PROPORTION TO THEIR TARGETS
   * (trade team, 2026-10-29). It was a box per sleeve, taking the money out of the model it
   * was typed against -- which forced the target column to move with it, so a 20/80 account
   * giving up $5,000 from each sleeve arrived at Allocate to Models reading 17.94/82.06, a
   * split nobody chose.
   *
   * The two cannot both hold: taking money out of ONE model and nowhere else IS a change to
   * the mix. Targets are the account's investment policy, so the raise yields to them --
   * the percentages stand and the dollars come out by share.
   *
   * ⚠️ So WHICH sleeve gives the money up is no longer something the advisor states; it
   * falls out of the split. {@link cashRaisedByConnectAccountId} works it out per sleeve for the
   * payload and the review screens, and it is the figure each sleeve's value actually
   * falls by -- which the per-sleeve version could not promise.
   *
   * ⚠️ REALLOCATION ONLY. A distribution raises money from its sleeves through
   * {@link sleeveAmountRows} on a step of its own, and the two must not meet: this one is
   * written only by {@link setModelFreeAmount}, which is inert for a move-money request.
   *
   * Held as TYPED. The cap bites when it is applied -- see {@link totalDrawnFromModels}.
   */
  private readonly modelFreeAmountSignal = signal(0);
  /** What the advisor typed, which may exceed {@link modelFreeCap} and block the step. */
  readonly modelFreeAmount = this.modelFreeAmountSignal.asReadonly();
  /**
   * Whether the advisor has stated a model's dollars directly on Allocate to Models.
   *
   * 🔴 THE TWO WAYS OF RAISING CASH LOCK EACH OTHER OUT (AndyP, 2026-10-29), and this flag is
   * one half of it -- {@link modelFreeAmount} being non-zero is the other. Either control can
   * start, and the one that starts owns the pot until it is cleared.
   *
   * ⚠️ WITHOUT THE LOCK THE SECOND ONE SILENTLY SWALLOWS THE FIRST. `applyModelFreeAmount`
   * re-states the pot as `Σheld − F`, which is the gross holdings less the box -- so typing in
   * the box after a value edit would discard whatever that edit had released, with nothing on
   * screen saying so. Mutual exclusion is cheaper than making them compose: the alternative
   * was pinning each stated row against the pro-rata scaling, which reintroduces the per-row
   * dollars `allocation()` records as a dead bug family.
   *
   * Cleared only by a wholesale re-state -- both Resets, a fresh load, `commitToSma`,
   * `useAllInvestable` -- because those are the points where the grid stops being the one the
   * advisor typed into. Reset is therefore the way back to the pro-rata box, and the
   * disabled-box tooltip says so.
   */
  private readonly modelValuesEditedSignal = signal(false);
  /** True once a dollar figure has been typed into a model row; locks the pro-rata box. */
  readonly modelValuesEdited = this.modelValuesEditedSignal.asReadonly();
  /**
   * Raw per-sleeve amounts for a Contribution / Distribution (their step 2).
   *
   * Empty for types 1 and 2, which state a target allocation in {@link allocationRows}
   * instead — see {@link SleeveAmountRow} for why these are not the same grid.
   */
  readonly sleeveAmountRows = signal<readonly SleeveAmountRow[]>([]);

  /**
   * True once the sleeve grid has been apportioned for the advisor on arrival — see
   * {@link prefillSleeveAmountsOnce}.
   *
   * 🔴 A LATCH, and it has to be. Without it the prefill is not a convenience but a trap: the
   * advisor clears the grid, or zeroes one sleeve on purpose, steps forward to check something
   * and comes back to find their decision overwritten by the same apportionment they just undid.
   * "Fill it if it is empty" alone cannot tell a grid nobody has touched from one deliberately
   * emptied, and those need opposite answers.
   *
   * 🟢 Not reset by {@link clearSleeveAmounts} for exactly that reason — pressing Clear is the
   * case this exists to respect.
   */
  private readonly sleeveAmountsPrefilled = signal(false);

  /**
   * True once the advisor has confirmed a switch to a Separately Managed Account.
   *
   * A latch, not a derived value. It cannot be computed from "the allocation holds an SMA
   * model", because that is also true of a selection the advisor is still looking at, and
   * this flag is what locks them on Review — deriving it would spring the lock the instant
   * the dropdown changed, before the confirmation was answered.
   */
  readonly smaCommitted = signal(false);
  /** Bumped whenever the reactive forms change, so computeds recompute. */
  private readonly formVersion = signal(0);
  private rowSeq = 0;
  private buyRowSeq = 0;

  // ---- Request metadata ----------------------------------------------------
  /** CRM investment account id, passed straight through to the payload. */
  readonly investmentAccountId = signal<number | null>(null);
  /** "Who initiated this request" — passed straight through to the payload. */
  readonly isSolicited = signal(false);

  // ---- Submission state (no backend yet) -----------------------------------
  readonly isSubmitting = signal(false);
  readonly isSubmitted = signal(false);
  readonly isComplete = signal(false);

  /**
   * Which kind of request this wizard is filing.
   *
   * One wizard serves both: an initial account **is** a reallocation with an empty
   * current allocation, which the type-1/type-2 `req.Allocation` split already models.
   * Set once by the host component from the nav tile the advisor came in through, before
   * {@link initialize}, so the step list is right on the first render.
   *
   * Defaults to Reallocation: this wizard's own tile is the common entry point, and a
   * caller that forgets to set it gets the behaviour it had before the modes existed.
   */
  readonly requestType = signal<AccountRequestType>(AccountRequestType.Reallocation);

  /** True when the wizard is standing in for the Initial Account Request. */
  readonly isInitialAccount = computed(() => this.requestType() === AccountRequestType.InitialAccount);

  /** True when money is coming INTO the account (ACMTECH-887). */
  readonly isContribution = computed(() => this.requestType() === AccountRequestType.Contribution);

  /** True when money is going OUT of the account, closing it or not (ACMTECH-888). */
  readonly isDistribution = computed(() => this.requestType() === AccountRequestType.Distribution);

  /**
   * True for the two request types that move money across the account boundary.
   *
   * The useful distinction is not "which type" but **where the pool comes from**. Types 1
   * and 2 rearrange what the account already holds, so their pool is derived from the
   * account. Types 3 and 4 are given a figure by the advisor on step 1, and every later
   * step spends or sources against that figure instead.
   *
   * Read this rather than `isContribution() || isDistribution()`: the money math branches
   * on the pool's origin, so a third money-moving type would want the same treatment
   * without every call site being revisited.
   */
  readonly isMoveMoney = computed(() => this.isContribution() || this.isDistribution());

  /**
   * True when this distribution closes the account outright.
   *
   * Collapses the step list to funding + review: with the whole account being liquidated
   * there is nothing to distribute *from* selectively, no positions to choose between, and
   * no advisor-directed holdings to keep. Those steps are excluded rather than shown empty.
   *
   * ⚠️ Not a request type of its own — see {@link AccountRequestType}.
   */
  readonly isCloseAccount = computed(() => {
    this.formVersion();
    return this.isDistribution() && this.fundingForm.controls.isCloseAccount.value === true;
  });

  /**
   * The wizard's steps, in order. Every index the wizard uses is derived from this
   * list, so toggling {@link REALLOCATION_V2_FILE_UPLOAD_STEP_ENABLED} — or switching
   * request type — moves the step numbers, the indicator, the body switch and the
   * navigation guards together.
   *
   * **Buying is excluded from an Initial Account request.** The workflow it replaces
   * could only sell, and the CRM and portal screens that render an Initial Account were
   * built against that shape — they may show nothing at all for buys. Enabling the step
   * for type 1 means checking those screens first.
   *
   * 🔴 **The move-money lists are shorter, and the difference is not cosmetic.** A
   * contribution has nothing to sell and a distribution nothing to buy, so those steps are
   * absent rather than empty — an empty sell step on a contribution would invite the advisor
   * to raise cash the request does not need. Neither writes a Requested allocation, so
   * neither shows the allocation GRID; they get {@link 'sleeveAmounts'} instead, which
   * apportions one figure across the sleeves the account already has.
   */
  readonly steps = computed<readonly ReallocationV2StepId[]>(() => {
    if (this.isMoveMoney()) return this.moveMoneySteps();

    return [
      'positions',
      // ACMTECH-945: allocation comes SECOND, immediately after selling.
      //
      // The advisor states what the models should hold before deciding what to buy or hold
      // back as cash, so the model figure is the thing they own and every custom holding
      // afterwards reads as a deduction from it rather than as a fresh decision.
      //
      // ⚠️ This only works because the grid opens BLANK (see buildAllocationRows). Opening it
      // fully funded here would hand the advisor a pool already spent, and the steps below
      // would spend it again.
      'allocation',
      ...(REALLOCATION_V2_BUY_STEP_ENABLED && !this.isInitialAccount() ? (['buys'] as const) : []),
      // ⚠️ Must stay AFTER buys: it merges bought securities into its candidate list, so it
      // cannot run before the buys are known. Flag is off today; do not strand it above.
      ...(REALLOCATION_V2_DO_NOT_TRADE_STEP_ENABLED ? (['doNotTrade'] as const) : []),
      'protectedCash',
      ...(REALLOCATION_V2_ATTACHMENTS_STEP_ENABLED ? (['files'] as const) : []),
      'review',
    ];
  });

  /**
   * Step list for a Contribution or Distribution.
   *
   * Split out of {@link steps} rather than expressed as more spread-conditionals inside it:
   * the two shapes share only their first and last steps, and interleaving them produced a
   * list where no single request type's order could be read off the page.
   */
  private moveMoneySteps(): readonly ReallocationV2StepId[] {
    // Closing the account: the amount is the whole account and everything in it is sold, so
    // there is nothing to apportion and nothing to choose. Funding carries the two
    // checkboxes and the delivery details; Review states what will happen.
    if (this.isCloseAccount()) {
      return [
        'funding',
        ...(REALLOCATION_V2_ATTACHMENTS_STEP_ENABLED ? (['files'] as const) : []),
        'review',
      ];
    }

    if (this.isContribution()) {
      return [
        'funding',
        'sleeveAmounts',
        // New money can buy, and the buy step's own gate measures against the contribution
        // amount rather than the account's value once `sourcePool` is type-aware.
        ...(REALLOCATION_V2_BUY_STEP_ENABLED ? (['buys'] as const) : []),
        // Whatever the advisor does not place in a model or a security stays as cash, so this
        // step is where a contribution lands its remainder.
        'protectedCash',
        ...(REALLOCATION_V2_ATTACHMENTS_STEP_ENABLED ? (['files'] as const) : []),
        'review',
      ];
    }

    return [
      'funding',
      'sleeveAmounts',
      // Reused wholesale from the reallocation wizard, plus a pinned cash row: the Custom
      // Holdings sleeve is where a distribution raises what the model sleeves do not cover.
      'positions',
      ...(REALLOCATION_V2_ATTACHMENTS_STEP_ENABLED ? (['files'] as const) : []),
      'review',
    ];
  }

  /**
   * Whether this request can buy at all.
   *
   * For TEMPLATES, which cannot ask `buys()` a question about a step that does not exist:
   * an empty buy list and an absent buy step look identical from a component, but they are
   * not the same thing. An advisor who bought nothing should still see the Purchases term;
   * an Initial Account, which cannot buy, should not be shown a term that can only ever
   * read $0.00, nor a Ticker/CUSIP column no ticker can reach.
   *
   * Read off `steps()` rather than re-testing the flag and the request type. That pairing
   * is written down once, above, and this is the rule the rest of the store already
   * follows -- see {@link buys}, which keys off the same list for the same reason.
   */
  readonly hasBuyStep = computed(() => this.steps().includes('buys'));

  /**
   * Whether the account arrived holding any model sleeves.
   *
   * Gates the read-only CURRENT group on the allocation grid. An Initial Account never has
   * sleeves — every row is one the advisor added — so those four columns would be nothing but
   * em dashes, and four empty columns read as missing data rather than as "there is no before".
   *
   * ⚠️ Keyed on the LOADED data, not on the rows. Removing every pre-loaded row must not make
   * the columns disappear underneath the advisor mid-edit: the account still had a before, and
   * the remaining rows still need it to compare against.
   */
  /**
   * Whether the advisor has changed WHICH models the account holds -- added one, or removed one.
   *
   * Distinct from {@link allocationChanged}, which is true for any edit at all. This is only
   * about the SET of models, because that is what invalidates {@link AllocationRow.sleeveDrift}:
   * a sleeve's drift is measured against the pot and the split of the arrangement that loaded,
   * so once the advisor is restructuring rather than rebalancing, the remaining sleeves' drift
   * describes an arrangement they have decided to leave. Still arithmetically true, no longer
   * anything they are working toward -- and it reads as "you have not fixed this yet".
   *
   * ⚠️ A freshly added row with NO model selected does not count. Clicking Add Model and
   * thinking better of it should not wipe the context off every other row, and the same
   * reasoning already governs change detection: an empty row asks for nothing.
   *
   * A model swap cannot arise. A model the account already holds is read-only, so changing one
   * is Remove then Add -- both of which this catches.
   */
  readonly modelSetChanged = computed(() => {
    const sleeves = this.data()?.sleeves ?? [];
    if (sleeves.length === 0) return false;

    const rows = this.allocationRows();
    const added = rows.some((r) => r.orionConnectAccountId == null && r.modelId !== '');
    const keptSleeves = rows.filter((r) => r.orionConnectAccountId != null).length;
    return added || keptSleeves !== sleeves.length;
  });

  readonly hasCurrentModelSleeves = computed(() => (this.data()?.sleeves ?? []).length > 0);

  /**
   * Whether an allocation row is a sleeve that does not exist in Orion yet.
   *
   * ⚠️ A METHOD rather than the test written inline in the template, for two reasons. The rule
   * -- "no Connect account id means the advisor added this row" -- is the same one
   * {@link modelSetChanged}, {@link removedSleeves} and `buildRequest`'s `isNew` all use, and it
   * belongs in one place so a badge cannot end up disagreeing with what is filed. And `== null`
   * is the right test (the field is `number | null | undefined`) but the template linter rejects
   * loose equality, so writing it there costs a two-clause comparison that reads worse than it
   * means. CRM's `IsNewSleeve` is the same predicate.
   */
  isNewSleeveRow(row: { orionConnectAccountId?: number | null }): boolean {
    return row.orionConnectAccountId == null;
  }

  /**
   * Sleeves the account holds today that this request does not carry forward.
   *
   * ⚠️ Keyed on the CONNECT ACCOUNT ID disappearing, never on the model. A sleeve the advisor
   * pointed at a different model keeps its account id, so it is a change rather than a removal —
   * keying on the model would report it here as a phantom closure AND leave it in the list
   * above, which is the same sleeve claimed twice.
   *
   * ⚠️ A CLOSURE THE READER HAS TO INFER FROM AN ABSENCE IS ONE THEY WILL MISS. That is the
   * whole reason this exists rather than letting the row simply not be there: removing a sleeve
   * is an instruction to the trade team, and it is the only instruction in this request with no
   * row of its own to carry it.
   *
   * 🟢 Carries no percentages and no value, and must never be added to a total: those figures
   * describe the REQUESTED allocation, and this sleeve is not in it.
   */
  readonly removedSleeves = computed(() => {
    const kept = new Set(
      this.allocationRows()
        .map((r) => r.orionConnectAccountId)
        .filter((id): id is number => id != null),
    );
    return (this.data()?.sleeves ?? []).filter((s) => s.accountId != null && !kept.has(s.accountId));
  });

  get totalSteps(): number {
    return this.steps().length;
  }

  /** Which step the user is on, as an id rather than a position. */
  readonly currentStepId = computed<ReallocationV2StepId>(() => this.steps()[this.currentStep() - 1]);

  /** 1-based position of a step, or 0 when it isn't in the current configuration. */
  stepIndex(id: ReallocationV2StepId): number {
    return this.steps().indexOf(id) + 1;
  }

  /** Raw attachments from the Attach Files step. Read {@link filesForPayload} to submit. */
  readonly files = signal<readonly File[]>([]);

  /**
   * Attachments as the submit payload should carry them.
   *
   * **Always sent**, empty included — the same rule as `buys` and `notes`. The request's
   * shape must not depend on which feature flags happen to be on, or flipping one
   * silently changes the contract the server receives. An empty collection simply means
   * nothing is written.
   *
   * Empty when the upload section is off, so a hidden control cannot smuggle files that
   * the advisor has no way to see or remove.
   */
  readonly filesForPayload = computed<readonly File[]>(() =>
    REALLOCATION_V2_FILE_UPLOAD_STEP_ENABLED ? this.files() : [],
  );

  addFiles(added: readonly File[]): void {
    // de-duped on name+size so re-picking the same file doesn't attach it twice
    this.files.update((existing) => {
      const seen = new Set(existing.map((f) => `${f.name}:${f.size}`));
      return [...existing, ...added.filter((f) => !seen.has(`${f.name}:${f.size}`))];
    });
  }

  removeFile(file: File): void {
    this.files.update((existing) => existing.filter((f) => f !== file));
  }

  /**
   * Free-text note the advisor can leave with the request. Persisted as
   * req.AccountRequest.Notes and surfaced as "Request Notes" on the tracker detail
   * and the CRM modal — a request-level detail, not a timeline entry.
   */
  readonly requestNotes = signal('');

  /**
   * Trimmed note, or null when blank, ready for the submit payload.
   *
   * **Always sent**, null included, rather than omitted when empty — same rule as
   * `buys` and `files`. A field whose presence depends on a feature flag makes the
   * request's shape a function of the UI configuration, and the day a flag is flipped
   * the server starts receiving a different contract without anything saying so.
   * Null is a value; absent is a different message.
   */
  readonly requestNotesForPayload = computed(() => {
    // Blank when the notes section is flagged off, so the field is present and empty
    // rather than carrying whatever a hidden control happened to hold.
    if (!REALLOCATION_V2_REQUEST_NOTES_ENABLED) return null;
    const trimmed = this.requestNotes().trim();
    return trimmed.length ? trimmed : null;
  });

  /** Human-readable attachment size, shared by the Attach Files and Review steps. */
  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  /**
   * Total value of the WHOLE account — every sleeve, not just Custom Holdings — on
   * the pending basis, including cash. Every % / investable figure keys off this.
   */
  readonly totalValue = computed(() => this.data()?.account.totalValue ?? 0);

  /**
   * Cash in the Custom Holdings sleeve, summed from the split-out cash positions.
   * Pre-fills the step-3 cash input and rides through as the Custodial Cash custom
   * holding. Sleeve-scoped, so an account holding cash in a model sleeve reports
   * less here than its account-wide cash.
   */
  readonly totalCash = computed(() =>
    (this.data()?.cashPositions ?? []).reduce((sum, p) => sum + p.marketValue, 0),
  );

  /**
   * The sleeve, less its own cash, as STEP 1 leaves it -- what the target is measured against.
   *
   * ⚠️ BUILT FROM ITS PARTS, and it must stay that way. Writing this as
   * `customHoldingsValue() - cashKept()` is the same number and a hard runtime failure: that
   * path runs back through `cashReleased` into this computed, and Angular throws
   * `Detected cycle in computations` when step 1 loads.
   *
   * May read: `sleevePositions`, `reviewSoldValue`, `totalCash`, `smaCommitted`.
   * May NEVER read: `customHoldingsValue`, `cashKept`, `cashReleased`, `totalSleeveCash`,
   * `keptCashRows`, `investable`, `netInvestable`, `unappliedValue`.
   *
   * ⚠️ PURCHASES AND STEP-4 SET-ASIDE ARE EXCLUDED, deliberately, though both end up in the
   * sleeve. Including them would let a later step move a step-1 cap, and all three consequences
   * are silent: the buy budget becomes unreachable by exactly 1% (release the maximum, buy
   * precisely that, and the hold rises to claw it back); `applyRemainingToCash` stops landing on
   * zero; and releasing $10,000 then setting aside $10,000 raises the hold though the sleeve's
   * cash has not changed in substance.
   *
   * The price is that held cash ends slightly under 1% of the FINAL sleeve -- short by
   * `(buys + setAside)/99`, about $101 on a $10,000 buy. Accepted (AndyP, 2026-09-18) against
   * three defects that would each surface as a support call.
   *
   * 🟢 Net of sells by construction: `sleevePositions` carries kept value, so selling lowers the
   * basis and frees cash without anything here having to know that selling happened.
   */
  private readonly cashTargetHoldBasis = computed(() =>
    this.nonCashCustomHoldingsValue() + this.reviewSoldValue(),
  );

  /**
   * Cash that must stay in the Custom Holdings sleeve to satisfy the 1% policy.
   *
   * ⚠️ `basis / 99`, NOT `basis * CASH_TARGET_HOLD_RATE`. The held cash is itself part of the
   * sleeve it is measured against, so the requirement is self-referential:
   *
   *     kept >= 0.01 * (basis + kept)   =>   kept >= basis / 99
   *
   * Written from the rate so the two cannot drift: `r / (1 - r)` is `1/99` at 1%.
   *
   * ⚠️ Clamped at `totalCash`. An account can arrive already below target -- the sleeve holds
   * less cash than 1% -- and step 1 cannot fix that. Everything is held, nothing is releasable,
   * and no error is raised: the advisor did not cause it and selling is what resolves it.
   *
   * 🟢 Zero under an SMA, explicitly. `commitToSma` releases the whole balance because an SMA is
   * 100% of the account and nothing stays in Custom Holdings -- there is no sleeve left to hold
   * cash for. It happens to call `toggleUseAllCash(true)` BEFORE clearing buys and cash, which is
   * safe only because this is a lazy computed read after the fact; an imperative write-back would
   * compute against the old sleeve and strand the cash permanently.
   *
   * 🔴 AND ZERO ON A DISTRIBUTION, which is the money leaving rather than being rearranged.
   * Holding 1% back of a sleeve the client is deliberately drawing down stops them taking their
   * own money, and a close account -- a distribution with a flag -- already releases everything.
   *
   * 🔴 AND IT FORCES UNNECESSARY SALES, which is the argument that survives. On the mock account
   * the sleeve holds $3,777.53 of cash and the target withholds $331.09. Distribute $3,600 and the
   * account plainly has it -- but the advisor is $153.56 short and must SELL A POSITION to raise
   * money already sitting there in cash. A real ticket, possibly a real tax consequence, to reach
   * cash the request was not allowed to touch. Worse, shares are granular, so that sale overshoots
   * and the account ends with the $331 withheld PLUS a fresh surplus: it held cash back and then
   * made more.
   *
   * ⚠️ THE ORIGINAL REASON WAS DIFFERENT AND NO LONGER HOLDS. The exemption shipped because a
   * distribution had to land EXACTLY, and cash was the only continuous source -- withhold a slice
   * and an account with no models could not reach the figure at all. Over-raising and SELL 1 SHARE
   * have since made every gap closable without cash, so that argument is gone. Recorded because it
   * is the first thing a reader will check, and finding a dead justification is how a live rule
   * gets reverted (AndyP asked exactly that, 2026-09-24).
   *
   * ⚠️ The rule reached distributions BY ACCIDENT, through `cashTaken` delegating to
   * `cashReleased`, and was accepted at the time rather than chosen -- the move-money spec says
   * so where it asserted the clamped figure. This makes the choice deliberately, the other way.
   */
  readonly cashTargetHold = computed(() => {
    if (this.smaCommitted()) return 0;
    if (this.isDistribution()) return 0;
    const r = CASH_TARGET_HOLD_RATE;
    return round2(Math.min(this.totalCash(), Math.max(0, this.cashTargetHoldBasis()) * (r / (1 - r))));
  });

  /** Sleeve cash the advisor may actually release, once the target is held back. */
  readonly cashAvailableToRelease = computed(() =>
    round2(Math.max(0, this.totalCash() - this.cashTargetHold())),
  );

  /**
   * How much of the target each cash row carries, index-paired to `data().cashPositions`.
   *
   * The hold is filled in a fixed order rather than spread evenly, so the advisor is left with
   * whole usable balances instead of a little held back everywhere:
   *
   *   1. CUSTODIAL_CASH first -- the custodian's primary cash balance
   *   2. then the largest balance
   *   3. ties broken by ticker
   *
   * ⚠️ TIES MUST BREAK DETERMINISTICALLY. Two rows of equal value with no tie-break would swap
   * between renders and move which row is disabled for no reason the advisor could see.
   *
   * ⚠️ THE FILL ORDER IS NOT THE DISPLAY ORDER. The table stays sorted by ticker -- the loader
   * documents that cash must present in one order everywhere -- so the hierarchy is legible from
   * the HELD figure on each row rather than from where the row sits. Anyone reordering the table
   * to match this is solving the wrong half.
   */
  private readonly cashRowHolds = computed<number[]>(() => {
    const rows = this.data()?.cashPositions ?? [];
    const holds = rows.map(() => 0);
    let remaining = this.cashTargetHold();
    if (remaining <= 0) return holds;

    const order = rows
      .map((p, i) => ({ i, p }))
      .sort((a, b) => {
        const ac = a.p.ticker === CUSTODIAL_CASH_TICKER ? 0 : 1;
        const bc = b.p.ticker === CUSTODIAL_CASH_TICKER ? 0 : 1;
        if (ac !== bc) return ac - bc;
        if (b.p.marketValue !== a.p.marketValue) return b.p.marketValue - a.p.marketValue;
        return (a.p.ticker ?? '').localeCompare(b.p.ticker ?? '');
      });

    for (const { i, p } of order) {
      if (remaining <= 0) break;
      const take = round2(Math.min(p.marketValue, remaining));
      holds[i] = take;
      remaining = round2(remaining - take);
    }
    return holds;
  });

  /**
   * What each cash row can still be released for -- its balance less whatever the target holds.
   *
   * 🟢 THE SINGLE CEILING. Every reader of a released amount clamps to this rather than to
   * `marketValue`, which is what keeps `released + kept = marketValue` true per row and stops the
   * payload and the screen disagreeing about the same position.
   */
  readonly cashRowCeilings = computed<number[]>(() => {
    const holds = this.cashRowHolds();
    return (this.data()?.cashPositions ?? []).map(
      (p, i) => round2(Math.max(0, p.marketValue - (holds[i] ?? 0))),
    );
  });

  /**
   * Cash the advisor has RELEASED from the Custom Holdings sleeve toward this request.
   *
   * ⚠️ This is the figure `investable` counts, and the whole point of the change that introduced
   * it. Custodial cash used to be added to the investable pool in full, automatically -- the
   * wizard spent the advisor's cash for them and the only way back was the Protected Cash step.
   * Cash is now opt-in: it reaches the pool only for rows the advisor ticked, only up to the
   * amount they typed.
   *
   * ⚠️ NOT the same thing as {@link cashInput}, which is step 4's **structured** cash from
   * outside today's sleeve. One releases cash the account already holds; the other parks cash
   * that is not in the sleeve. They are independent and must never be netted against each other.
   *
   * Reads through `formVersion` because a FormArray is not a signal.
   */
  readonly cashReleased = computed(() => {
    this.formVersion();
    const rows = this.data()?.cashPositions ?? [];
    const ceilings = this.cashRowCeilings();
    return round2(
      rows.reduce((sum, p, i) => {
        const group = this.cashForm.at(i) as CashUseForm | undefined;
        if (!group || !group.controls.use.value) return sum;
        const raw = group.controls.amount.value;
        // Blank reads as 0 -- the box is allowed to sit empty while it is being retyped.
        const amount = Number.isFinite(raw) ? (raw as number) : 0;
        return sum + Math.min(Math.max(0, amount), ceilings[i] ?? p.marketValue);
      }, 0),
    );
  });

  /**
   * Cash still held in the sleeve -- what the advisor did not release.
   *
   * 🟢 `cashKept + cashReleased === totalCash` by construction, which is what lets the strip's
   * terms still reconcile to the account total. Derived rather than summed independently so the
   * two halves cannot drift apart, the same discipline `keptValue` follows against `soldValue`.
   */
  readonly cashKept = computed(() => round2(this.totalCash() - this.cashReleased()));

  /**
   * The cash lines still held, at what is left of each -- for Review & Submit and the payload.
   *
   * ⚠️ ONE source for both, so the page the advisor checks and the request the trade team reads
   * cannot disagree about which cash stayed. `buildRequest` maps this rather than repeating the
   * per-row arithmetic.
   *
   * A row released in full is omitted: a $0.00 holding is not a holding, and the sleeve no longer
   * contains it. Empty entirely once an SMA is committed -- the manager takes the whole account,
   * which is why `commitToSma` releases the cash.
   */
  readonly keptCashRows = computed(() => {
    this.formVersion();
    if (this.smaCommitted()) return [];
    const ceilings = this.cashRowCeilings();
    return (this.data()?.cashPositions ?? []).flatMap((c, i) => {
      const group = this.cashForm.at(i) as CashUseForm | undefined;
      const raw = group?.controls.use.value ? group.controls.amount.value : 0;
      const released = Number.isFinite(raw) ? (raw as number) : 0;
      // ⚠️ Clamped to the row's CEILING, not its balance: whatever the cash target holds back is
      // kept by definition, so it has to land on this side of the split.
      const kept = round2(c.marketValue - Math.min(Math.max(0, released), ceilings[i] ?? c.marketValue));
      return kept > 0
        ? [{
          ticker: c.ticker,
          description: c.description,
          securityId: c.securityId,
          securityTypeId: c.securityTypeId,
          kept,
        }]
        : [];
    });
  });

  /**
   * Each cash position as the review page draws it: what the account HOLDS, and how much of it
   * this request RELEASES.
   *
   * ⚠️ FOR DISPLAY ONLY, and it stays that way even though released cash IS filed now
   * (ACMTECH-981). {@link releasedCashRows} is the half that files; this one carries the HELD
   * figure so the band reconciles on screen, and a held figure in the payload would tell CRM the
   * request frees the whole position. The two must not be merged however similar they look.
   *
   * ⚠️ It carries the HELD figure, not the kept one, and that is what makes the band add up.
   * `keptCashRows` emits only the remainder, so the page showed `$27,817.08`, then a deduction
   * of `$3,481.00`, then a total of `$27,817.08` — three figures that do not reconcile, with the
   * deduction floating free. Held minus released IS the total, so the arithmetic now lands the
   * way the sells table's does: before, change, after.
   *
   * ⚠️ One row per position, never filtered. `keptCashRows` drops a row once nothing is left, so
   * releasing a whole position erased it from the page — no trace the account ever held it.
   *
   * 🟢 ACMTECH-970 decided against a released-cash line, reasoning that the trade team has
   * nothing to trade for it and the instruction is carried by Custom Holdings being lower. That
   * is right about the trade team and wrong about the advisor, who is the other reader of this
   * page and the one who has to check their own figures.
   */
  readonly cashBandRows = computed(() => {
    this.formVersion();
    if (this.smaCommitted()) return [];
    const ceilings = this.cashRowCeilings();
    return (this.data()?.cashPositions ?? []).map((c, i) => {
      const group = this.cashForm.at(i) as CashUseForm | undefined;
      const raw = group?.controls.use.value ? group.controls.amount.value : 0;
      const entered = Number.isFinite(raw) ? (raw as number) : 0;
      // Clamped exactly as `keptCashRows` clamps it, so `held - released` is always that row's
      // kept figure and the column reconciles to the band total whatever was typed.
      const released = round2(Math.min(Math.max(0, entered), ceilings[i] ?? c.marketValue));
      return {
        ticker: c.ticker,
        description: c.description,
        held: round2(c.marketValue),
        released,
      };
    });
  });

  /**
   * Cash the advisor freed on step 1, as the request FILES it -- one row per position drawn on,
   * at the amount released.
   *
   * ⚠️ THE PAYLOAD HALF of the pair. {@link cashBandRows} is the display half and carries the
   * HELD figure instead; sending that would claim the whole position was freed. Same clamp on
   * both so the figure a row files is exactly the one the review page showed.
   *
   * ⚠️ Rows releasing nothing are DROPPED rather than filed at zero. Each of these becomes a
   * trade order in CRM, and a $0.00 one is a row the trade team has to read and then ignore.
   *
   * 🟢 It carries the position's REAL Eclipse security id and type, which for custodial cash is
   * normally 1 (Mutual Fund) -- Orion types sweep vehicles that way. Forwarding the true type is
   * safe only because CRM files these under their own TradeAction (4, Cash): the action says what
   * the row is, so the type does not have to lie to say it.
   *
   * ⚠️ Empty once an SMA is committed, matching {@link keptCashRows} -- the manager takes the
   * whole account, and `commitToSma` releases the cash into the SMA rather than into this request.
   */
  readonly releasedCashRows = computed(() => {
    this.formVersion();
    if (this.smaCommitted()) return [];
    const ceilings = this.cashRowCeilings();
    return (this.data()?.cashPositions ?? []).flatMap((c, i) => {
      const group = this.cashForm.at(i) as CashUseForm | undefined;
      const raw = group?.controls.use.value ? group.controls.amount.value : 0;
      const entered = Number.isFinite(raw) ? (raw as number) : 0;
      // The row's ceiling, so a cash-target hold never reaches the payload as a release.
      const released = round2(Math.min(Math.max(0, entered), ceilings[i] ?? c.marketValue));
      return released > 0
        ? [{
          ticker: c.ticker,
          description: c.description,
          securityId: c.securityId,
          securityTypeId: c.securityTypeId,
          released,
        }]
        : [];
    });
  });

  /**
   * Summed value of the non-cash positions shown in the step-1 list (raw, before any
   * sell selections) — i.e. the Custom Holdings sleeve minus its cash. This is NOT
   * `totalValue − totalCash` once the account has model sleeves too; that difference
   * is the value already under model management.
   */
  readonly nonCashPositionsValue = computed(() =>
    (this.data()?.positions ?? []).reduce((sum, p) => sum + p.marketValue, 0),
  );

  // ---- Funding (Contribution / Distribution only) --------------------------
  /** The lookups and account facts step 1 binds to; absent for types 1 and 2. */
  readonly funding = computed(() => this.data()?.funding ?? null);

  /** Every delivery method on record, before the per-request-type filter below. */
  private readonly allDeliveryMethods = computed(() => this.funding()?.deliveryMethods ?? []);

  /**
   * The methods to offer, filtered by what this request type is allowed to use.
   *
   * 🔴 **The rule is on the row now, not here.** It used to be `isContribution() ? [the ACH row]
   * : everything`, against an `isAch` flag that existed for that one rule and was read nowhere
   * else — an identity standing in for a policy, named after the row that happened to satisfy it.
   * `req.DeliveryMethod` carries `AllowedForContribution` and `AllowedForDistribution`, so
   * offering contributions a second method is a row edit.
   *
   * ⚠️ This partly reverses ACMTECH-887, which concluded the database had no business knowing
   * about request types. That was right about a SINGLE boolean — the same row is offered for one
   * request and not another, which one flag cannot say. A column per request type can.
   *
   * Note this returns the list to PUT IN THE DROPDOWN, so a request with one allowed method has
   * exactly one entry; {@link deliveryMethodLocked} is what makes that read as settled rather
   * than as a choice the advisor is failing to make.
   */
  readonly deliveryMethods = computed(() => {
    const contribution = this.isContribution();
    return this.allDeliveryMethods().filter(
      (m) => (contribution ? m.allowedForContribution : m.allowedForDistribution),
    );
  });

  /**
   * Whether the delivery method is fixed rather than chosen.
   *
   * 🟢 **Derived from the list, not from the request type.** It was `isContribution()`, which
   * welded the greyed-out treatment to one request type and would have gone on lying the moment
   * contributions were allowed a second method. Asking *is there only one option* is the actual
   * question, and it answers itself as the data changes.
   *
   * When locked the step renders V1's treatment — a disabled select showing the one method, with
   * the reason on a tooltip beside the label — rather than a one-item dropdown.
   *
   * ⚠️ False while the list is EMPTY, which is the load state. Nothing is settled before the
   * lookups arrive, and a select that renders locked and empty reads as a broken control rather
   * than a pending one.
   */
  readonly deliveryMethodLocked = computed(() => this.deliveryMethods().length === 1);

  /** Frequencies the portal offers — the lookup is already filtered to those. */
  readonly deliveryFrequencies = computed(() => this.funding()?.deliveryFrequencies ?? []);

  /**
   * First Party / Third Party, for the radios inside the payee panel.
   *
   * ⚠️ Offered in the lookup's own order and **not sorted here**. The rows are First Party then
   * Third Party by id, which is the order they read in; imposing an order in the client would be
   * a second opinion about a list the database already orders.
   *
   * 🟢 This list was loaded and read by nobody for a day — see {@link DeliveryRecipientTypeOption}
   * — which is exactly why it still exists to be read now.
   */
  readonly deliveryRecipientTypes = computed(() => this.funding()?.deliveryRecipientTypes ?? []);

  /**
   * Whether this request asks who the money is going to.
   *
   * 🔴 **A FLAG ON THE METHOD, not a list of method names.** Check asked it alone until Wire was
   * added on 2026-11-03; the next change is a row in `req.DeliveryMethod` rather than a release.
   *
   * ⚠️ Gated on {@link isNewRecipient} as well. Choosing a destination the custodian already
   * holds poses no question — the custodian has established whose it is — so the radios appear
   * only alongside the New/Other row.
   */
  readonly recipientTypeApplies = computed(
    () => this.selectedDeliveryMethod()?.asksRecipientType === true && this.isNewRecipient(),
  );

  /**
   * The chosen recipient type, or null before one is picked.
   *
   * Resolved against the loaded list rather than trusted from the form, so a stored id the lookup
   * no longer offers reads as unanswered rather than as a label nobody can see.
   */
  readonly selectedRecipientType = computed(() => {
    this.formVersion();
    const id = this.fundingForm.controls.deliveryRecipientTypeId.value;
    return this.deliveryRecipientTypes().find((r) => r.id === id) ?? null;
  });

  /** True while the question applies and has not been answered. */
  readonly recipientTypeMissing = computed(
    () => this.recipientTypeApplies() && this.selectedRecipientType() === null,
  );

  /**
   * The chosen delivery method, or null before one is picked.
   *
   * ⚠️ Resolved against **every** method on record, not the offered subset. Resolving against
   * the offered list would make a stored-but-not-offered id read as "no method chosen", so a
   * contribution carrying Check would be refused with *"choose how the money should be
   * delivered"* — which describes the wrong problem and leaves the specific guard in
   * {@link fundingStepBlockedReason} permanently unreachable.
   */
  readonly selectedDeliveryMethod = computed(() => {
    this.formVersion();
    const id = this.fundingForm.controls.deliveryMethodId.value;
    return this.allDeliveryMethods().find((m) => m.id === id) ?? null;
  });

  /** The chosen frequency, or null before one is picked. */
  readonly selectedDeliveryFrequency = computed(() => {
    this.formVersion();
    const id = this.fundingForm.controls.deliveryFrequencyId.value;
    return this.deliveryFrequencies().find((f) => f.id === id) ?? null;
  });

  /**
   * Bank authorizations that can carry the chosen delivery method.
   *
   * Filtered on the method's own `transactionType` rather than on a code the client maps from
   * the method id — that mapping now arrives with the row (see `DeliveryMethodOption`). `ALL`
   * matches every method, and the synthetic "Other (New Account)" entry carries it so it is
   * always offered.
   *
   * Empty until a method is chosen: the list is a property of the method, and showing every
   * instruction beforehand would offer accounts that cannot receive the transfer.
   */
  readonly filteredStandingInstructions = computed(() => {
    const code = this.selectedDeliveryMethod()?.transactionType ?? '';
    if (code === '') return [];
    return (this.funding()?.standingInstructions ?? []).filter(
      (s) => s.transactionType === code || s.transactionType === 'ALL',
    );
  });

  /**
   * The bank authorizations to offer, or empty when this method does not use one.
   *
   * Two of the six keep the money inside the custodian — *Leave in Cash for Outgoing Transfer*
   * and *Move to Retail* — and a third, **Check**, is posted to an address rather than sent to
   * an account. `req.DeliveryMethod.UsesBankAccount` says which, so this needs no list of its own
   * and no opinion about Schwab's codes.
   *
   * ⚠️ Load-bearing for the gate, not just for hiding a control: demanding a bank account from a
   * method that cannot use one makes that method unfileable. V1 draws the same distinction, in
   * its own way, as `displayDeliveryInstructions`.
   */
  readonly bankAccountOptions = computed(
    () => (this.usesBankAccount() ? this.filteredStandingInstructions() : []),
  );

  /**
   * The addresses to offer for a cheque, or empty when this method is not posted anywhere.
   *
   * 🔴 THE SAME SCHWAB LIST AS THE BANK ACCOUNTS. A standing instruction names a counterparty,
   * and a type-`C` counterparty is a mailing address with no account number in it. Until
   * 2026-11-02 our own API discarded exactly those rows, so a cheque looked like an account with
   * no standing instructions at all — the advisor met an empty dropdown, a custodial-paperwork
   * warning they could do nothing about, and a forced pick that filed *Other (New Account)* as
   * the delivery instruction on every cheque.
   *
   * ⚠️ The *Other (New Account)* row is excluded explicitly. It carries `transactionType: 'ALL'`
   * so it rides every bank list, and `'ALL'` matches `'C'` too — without this it would appear in
   * the address list as a second, wrong escape hatch beside *New (Other Address)*. Matched on the
   * exported constant, which is this module's own row rather than anything a user can edit.
   */
  readonly addressOptions = computed(
    () => (this.usesAddressBook()
      ? this.filteredStandingInstructions().filter(
        (o) => o.key !== OTHER_STANDING_INSTRUCTION_KEY,
      )
      : []),
  );

  /** Whether this method is paid to one of the account's bank authorizations. */
  readonly usesBankAccount = computed(
    () => this.selectedDeliveryMethod()?.usesBankAccount === true,
  );

  /** Whether this method is posted to an address instead — Check alone. */
  readonly usesAddressBook = computed(
    () => this.selectedDeliveryMethod()?.usesAddressBook === true,
  );

  /**
   * Whether overnight delivery can be offered.
   *
   * ⚠️ Was `transactionType === 'C'`. The question is about the method, not about Schwab's
   * taxonomy, and `req.DeliveryMethod` answers it directly now.
   */
  readonly allowsOvernightDelivery = computed(
    () => this.selectedDeliveryMethod()?.allowsOvernightDelivery === true,
  );

  /**
   * Whether the advisor has chosen the synthetic *New/Other* row — on either list.
   *
   * 🔴 **THE FACT EVERY EXTRA REQUIREMENT HANGS OFF**, and it replaces the *Address on Record /
   * Third Party* question this wizard asked for a day (ACMTECH-1036, re-scoped 2026-11-02).
   *
   * The two are not the same claim, and this one is better. *Third party* was the advisor
   * asserting a relationship; this is **nothing is on file at the custodian**, which is a fact we
   * hold. It is also the fact that actually obliges the extra thing: a client's own new bank
   * account has no standing authorization either, and Schwab wants signed instructions for it
   * just the same. One question instead of two, and the one the advisor has already answered by
   * picking from the list.
   *
   * ⚠️ Reads `isOther` on the row, never the label. Both labels are copy and both have been
   * reworded; a string test here would switch two requirements off at once and silently.
   */
  readonly isNewRecipient = computed(
    () => this.selectedStandingInstruction()?.isOther === true,
  );

  /**
   * Whether this request owes an uploaded instruction at all — **regardless of whether one has
   * been attached**. Wire alone, by `req.DeliveryMethod.NewRecipientRequiresDocument`.
   *
   * Separate from {@link newRecipientDocumentMissing} because the Documents step needs both: this
   * decides whether to say anything, and that one decides whether what it says is a demand or a
   * confirmation. Folding them would leave the step unable to tell *satisfied* from *not asked*,
   * and the advisor with no acknowledgement that the thing they just attached was the thing.
   */
  readonly newRecipientDocumentApplies = computed(
    () => this.selectedDeliveryMethod()?.newRecipientRequiresDocument === true
      && this.isNewRecipient(),
  );

  /**
   * A method that demands an uploaded instruction, sent somewhere nothing is on file for, with
   * nothing attached. Wire alone, by `req.DeliveryMethod.NewRecipientRequiresDocument`.
   *
   * 🔴 GUARDED ON THE UPLOAD STEP EXISTING (AndyP, 2026-11-02). With
   * {@link REALLOCATION_V2_FILE_UPLOAD_STEP_ENABLED} off there is nowhere to attach anything, so
   * a block here would be a wall with no door — the advisor could not file the request at all.
   * The copy softens in that state instead ({@link THIRD_PARTY_DOCUMENT_OFFLINE}) and the
   * obligation moves off the portal rather than disappearing.
   *
   * Any file counts. Nothing here can tell a signed instruction from a statement, and pretending
   * otherwise would be a check that only looks like one.
   */
  readonly newRecipientDocumentMissing = computed(
    () => this.selectedDeliveryMethod()?.newRecipientRequiresDocument === true
      && this.isNewRecipient()
      && REALLOCATION_V2_FILE_UPLOAD_STEP_ENABLED
      && this.files().length === 0,
  );

  /**
   * Whether the payee block is being asked for: a cheque going to an address Schwab does not
   * hold. Check alone, by `req.DeliveryMethod.NewRecipientRequiresPayee`.
   *
   * ⚠️ Unlike the document rule above, this has no feature-flag escape. The fields live on the
   * funding step, which every Contribution and Distribution has, so the question can always be
   * answered and there is no state where demanding it strands anyone.
   *
   * ⚠️ The two obligations are strictly separate and never both apply: a wire carries a document,
   * a cheque carries an address. Neither is a weaker form of the other.
   */
  readonly payeeRequired = computed(
    () => this.selectedDeliveryMethod()?.newRecipientRequiresPayee === true
      && this.isNewRecipient(),
  );

  /**
   * What reaches `req.Contribution/Distribution.DeliveryInstruction` — the one line the trade
   * team reads to know where the money goes.
   *
   * 🔴 **THE FILED VALUE AND THE DROPDOWN LABEL DIVERGE HERE**, which the form deliberately
   * avoids everywhere else — the control holds a key precisely so that what is shown is what is
   * submitted. A cheque to an address Schwab already holds is the exception (AndyP, 2026-11-02):
   * the label is the payee's NAME, because that is what distinguishes one address on file from
   * another in a 320px select, and a four-line option would be unreadable. But the name alone
   * tells the trade team nothing they can post a cheque with.
   *
   * So the address rides along. `varchar(max)`, so there is no width to overflow, and CRM renders
   * it in the Payee block for a request that carries no typed payee.
   *
   * ⚠️ NEWLINE-SEPARATED, not comma-separated. Addresses contain commas, so a reader splitting on
   * one would break a line in the wrong place; a newline is unambiguous and CRM sets
   * `white-space: pre-line` over it. Surfaces that print it inline collapse the breaks to spaces,
   * which is harmless.
   *
   * ⚠️ NOT written to `req.AccountRequestPayee`, which would let CRM's existing panel read it
   * with no change at all. Schwab files three free-text lines and no city/state/zip, so filling
   * those columns means parsing line 3 — guesswork, stored as fact, in a table whose whole point
   * is to hold what the advisor actually typed.
   */
  readonly filedDeliveryInstruction = computed(() => {
    const chosen = this.selectedStandingInstruction();
    if (chosen === null) return undefined;
    const lines = this.selectedAddressLines();
    if (lines.length === 0) return chosen.displayValue;
    return [chosen.name ?? chosen.displayValue, ...lines].join('\n');
  });

  /**
   * The instruction sentence for the DELIVERY panel, or for the Documents step.
   *
   * On the store rather than on each component because the wording now depends on store state --
   * which method is chosen -- so a component holding a constant would be holding a stale one.
   *
   * ⚠️ 'Delivery' as the fallback subject, which can only appear if the method vanished between
   * the panel rendering and this reading. Naming no method is better than naming the wrong one.
   */
  readonly newRecipientInstructionNote = computed(() => {
    const method = this.selectedDeliveryMethod()?.name ?? 'Delivery';
    return REALLOCATION_V2_FILE_UPLOAD_STEP_ENABLED
      ? instructionsToDocumentsStep(method)
      : instructionsOffline(method);
  });

  /** The same, pointing at the drop zone instead of naming the step. */
  readonly newRecipientInstructionNoteOnDocuments = computed(
    () => instructionsOnThisStep(this.selectedDeliveryMethod()?.name ?? 'Delivery'),
  );

  /**
   * The chosen address, as Schwab's lines — empty for a bank account, for the synthetic row, and
   * for a method that is not posted anywhere.
   *
   * ⚠️ Shown read-only and never copied into the payee fields. The address already lives at the
   * custodian; a duplicate in CRM could only go stale, and the request files the chosen row's
   * label so the trade team can say which address was meant. That is the whole reason the payee
   * table is only written when the advisor types one.
   */
  readonly selectedAddressLines = computed<readonly string[]>(
    () => (this.usesAddressBook() ? this.selectedStandingInstruction()?.addressLines ?? [] : []),
  );

  /**
   * Whether the account has no addresses on file at all, so the only row on offer is the
   * synthetic one.
   *
   * Drives a one-line note beside the payee panel. ⚠️ NOT a warning and emphatically not the
   * custodial-paperwork sentence the bank list shows: typing an address is a complete, ordinary
   * answer for a cheque, so there is nothing here for the advisor to go and fix.
   */
  readonly addressBookEmpty = computed(
    () => this.usesAddressBook() && this.addressOptions().length <= 1,
  );

  /**
   * Which payee fields are still empty, in the order they appear on screen.
   *
   * A LIST rather than a boolean, so the step can mark each offending input and the blocker can
   * say how many are outstanding — one computed serving both, instead of a predicate here and a
   * second pass over the same controls in the template.
   *
   * `addressContinued` is absent by design: it is the apartment line, and most addresses do not
   * have one. Trimmed, so a space is not an answer.
   *
   * ⚠️ `deliveryRecipientTypeId` is NOT in this list. It is asked by Wire too, which has no
   * payee at all, so it blocks through {@link recipientTypeMissing} instead — one condition
   * covering both methods rather than a payee-shaped one covering half of them.
   */
  readonly payeeMissingFields = computed<readonly string[]>(() => {
    this.formVersion();
    if (!this.payeeRequired()) return [];
    const p = this.fundingForm.controls.payee.controls;
    const required: readonly [string, string][] = [
      ['payeeName', p.payeeName.value],
      ['streetAddress', p.streetAddress.value],
      ['city', p.city.value],
      ['state', p.state.value],
      ['zip', p.zip.value],
    ];
    return required.filter(([, v]) => v.trim().length === 0).map(([name]) => name);
  });

  /** True while any required payee field is blank. */
  readonly payeeIncomplete = computed(() => this.payeeMissingFields().length > 0);

  /**
   * Forgets the payee block, and fills in the lone address when that is all there is.
   *
   * 🔴 CALLED WHEN THE METHOD CHANGES, and the forgetting is not tidiness. A payee typed against
   * Check survives a switch to ACH otherwise and reaches the payload — a posting address filed
   * against a transfer with no cheque to post, which the trade team would read as an instruction.
   * `onMethod` in the funding step clears the destination and the overnight flag for the reason.
   *
   * ⚠️ THE AUTO-SELECT APPLIES TO THE ADDRESS LIST ONLY, and only when the account has nothing on
   * file so the single row is *New (Other Address)*. Making an advisor pick from a list of one is
   * the forced non-answer this card exists to remove — it is what filed *Other (New Account)* as
   * the delivery instruction on every cheque. The payee panel opening is the real answer, and the
   * note beside it says why.
   *
   * ⚠️ The BANK list is deliberately not treated this way, even when it has one row. Picking a
   * destination for a client's money is a decision, and a one-item list is still a decision; it
   * keeps its placeholder and the advisor chooses. (AndyP, 2026-11-02.)
   */
  resetRecipientForMethod(): void {
    // No formVersion bump: `fundingForm.valueChanges` is already subscribed and does it, and
    // every write below emits. Bumping here as well would just recompute everything twice.
    this.fundingForm.controls.payee.reset();
    // Cleared with the payee, and for the same reason: it is an assertion about a destination
    // that has just changed. Carried into a new method it would answer a question about money
    // going somewhere else entirely.
    this.fundingForm.controls.deliveryRecipientTypeId.setValue(null);

    const addresses = this.addressOptions();
    if (this.usesAddressBook() && addresses.length === 1) {
      this.fundingForm.controls.standingInstructionKey.setValue(addresses[0].key);
    }
  }

  /** The chosen bank authorization, or null. */
  readonly selectedStandingInstruction = computed(() => {
    this.formVersion();
    const key = this.fundingForm.controls.standingInstructionKey.value;
    return this.filteredStandingInstructions().find((s) => s.key === key) ?? null;
  });

  /**
   * Whether the frequency is fixed rather than chosen — true on a close.
   *
   * A CLOSE IS A SINGLE PAYOUT, by definition: the account is liquidated and the proceeds go out
   * once, so there is no second transfer for a schedule to describe and a recurring close is not
   * a thing that exists.
   *
   * ⚠️ LOCKED, NOT HIDDEN, and it was briefly hidden (AndyP, 2026-09-23). Hiding it left the
   * advisor unable to see what the request would file — the frequency still reaches CRM and the
   * trade team reads it, so a control that vanishes turns a stated answer into an invisible one.
   * Showing One Time greyed out says the same thing and says it where the answer will be.
   *
   * 🟢 Same treatment as {@link deliveryMethodLocked}, deliberately: a greyed select sitting on
   * the only valid value, with the reason beside it. The step now has two locked selects and they
   * should not look like two different kinds of thing.
   */
  readonly deliveryFrequencyLocked = computed(() => this.isCloseAccount());

  /**
   * One Time, found by its own `oneTime` flag and never by name or id.
   *
   * Shared by the load-time default and by {@link buildDelivery}: both need "the frequency that
   * does not repeat", and two spellings of that would be two places to get it wrong.
   */
  readonly oneTimeFrequency = computed(
    () => this.deliveryFrequencies().find((f) => f.oneTime) ?? null,
  );

  /**
   * True when a start date is needed: a schedule repeats, so it has to begin somewhere.
   *
   * Keyed on the frequency's own `oneTime` flag, never on its name or id.
   *
   * ⚠️ AND on the frequency not being locked. Ticking Close Account rewrites the control to One
   * Time (see `onCloseAccountChanged`), which is what drops the date control on its own — but that
   * write is a no-op if the frequency list has not loaded, and this is the test that holds either
   * way. Without it, an advisor who picked Monthly and set a start date would tick Close Account
   * and leave a date behind for a schedule the request no longer has.
   */
  readonly startDateRequired = computed(() => {
    if (this.deliveryFrequencyLocked()) return false;
    const frequency = this.selectedDeliveryFrequency();
    return frequency != null && !frequency.oneTime;
  });

  /**
   * Earliest permitted start date.
   *
   * A distribution cannot start today — V1's rule (`setMinDateDistribution`), kept because it
   * reflects settlement rather than a UI preference: money has to be raised before it can leave.
   * A contribution can, since the money is arriving.
   */
  readonly minStartDate = computed(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    if (this.isDistribution()) date.setDate(date.getDate() + 1);
    return date;
  });

  /** Federal withholding % as the advisor has it (distribution only). */
  readonly federalTaxPct = computed(() => {
    this.formVersion();
    return this.isDistribution() ? this.fundingForm.controls.federalTaxPct.value ?? 0 : 0;
  });

  /** State withholding % as the advisor has it (distribution only). */
  readonly stateTaxPct = computed(() => {
    this.formVersion();
    return this.isDistribution() ? this.fundingForm.controls.stateTaxPct.value ?? 0 : 0;
  });

  /**
   * Combined withholding, as a fraction.
   *
   * ⚠️ Clamped below 1. At 100% withholding the net-to-gross conversion divides by zero, and
   * above it the sign flips and a net entry would produce a negative gross — a distribution
   * that pays money in. The clamp makes the conversion return 0 instead, which the step's own
   * gate then reports as "enter an amount" rather than filing an inverted request.
   */
  readonly withholdingRate = computed(() =>
    Math.min(0.9999, Math.max(0, (this.federalTaxPct() + this.stateTaxPct()) / 100)),
  );

  /** Whether the advisor changed the account's withholding elections on this request. */
  readonly taxWithholdingChanged = computed(() => {
    if (!this.isDistribution()) return false;
    const onFile = this.funding();
    // 🔴 A GUARD WAS HERE AND IS GONE (AndyP, 2026-10-29). It returned false whenever nothing
    // was on file, reasoning that an unknown baseline cannot substantiate a claim about the
    // past. That is true of the CRM tracker, which reports one -- and false of this notice,
    // which warns about the FUTURE: "remains in effect on all distributions until you change or
    // revoke it" is MOST true when an advisor is setting a first election on an account that had
    // none, and that was the single case it stayed silent for. ACMTECH-1031 made the CRM flag
    // that request red, and a notice the advisor never saw against a red the trade team did is
    // the two surfaces disagreeing about one event.
    //
    // ⚠️ `?? 0` treats a missing election as nothing elected FOR THE COMPARISON ONLY. Null still
    // travels to CRM as null, which is what keeps "no election on file" distinct from "0%
    // elected" at the far end.
    return (
      Math.abs(this.federalTaxPct() - (onFile?.federalTaxWithholding ?? 0)) >= EPS
      || Math.abs(this.stateTaxPct() - (onFile?.stateTaxWithholding ?? 0)) >= EPS
    );
  });

  /** Whether the advisor is entering the amount the client receives rather than the amount leaving. */
  readonly isNetEntry = computed(() => {
    this.formVersion();
    return this.isDistribution() && this.fundingForm.controls.entryMode.value === 'net';
  });

  /** Exactly what the advisor typed into the amount box. */
  private readonly enteredAmount = computed(() => {
    this.formVersion();
    return this.fundingForm.controls.amount.value ?? 0;
  });

  /**
   * The figure this request is measured against — **always the amount leaving or entering the
   * account**, never the amount the client receives.
   *
   * Three sources, in order of precedence:
   *  1. **Closing the account** — the whole account, by definition. The advisor types nothing.
   *  2. **A net entry** — converted up, because withholding comes out of what leaves.
   *  3. Otherwise, what was typed.
   *
   * 🔴 The gross/net toggle is a convenience for the advisor and nothing more. `req.Distribution`
   * stores ONE amount, the trade team acts on what leaves the account, and every later step
   * apportions against this. Storing the net figure would silently under-fund every request by
   * the withholding.
   */
  readonly fundingAmount = computed(() => {
    if (this.isCloseAccount()) return this.totalValue();
    const entered = this.enteredAmount();
    if (entered <= 0) return 0;
    if (!this.isNetEntry()) return round2(entered);
    return round2(entered / (1 - this.withholdingRate()));
  });

  /**
   * What the client actually receives after withholding — display only.
   *
   * Never sent, and never the basis for anything: it is the one figure in this step the trade
   * team does not act on, and the reason the toggle exists at all is that advisors are asked for
   * it by clients.
   */
  readonly netAmount = computed(() =>
    this.isDistribution() ? round2(this.fundingAmount() * (1 - this.withholdingRate())) : this.fundingAmount(),
  );

  // ---- Sleeve amounts (Contribution / Distribution step 2) -----------------
  /**
   * The sleeve rows as the step renders them: the account's own sleeves, joined to whatever the
   * advisor has put against each.
   *
   * ⚠️ Driven from `data().sleeves`, with `sleeveAmountRows` supplying only the amounts. The
   * sleeves are fixed for the life of the request — neither type can add or remove a model, so
   * unlike the allocation grid there is no row identity to maintain and nothing to reconcile.
   */
  readonly sleeveAmounts = computed(() => {
    const data = this.data();
    const rows = this.sleeveAmountRows();
    const pool = this.fundingAmount();
    if (!data) return { rows: [] as SleeveAmountRowView[], total: 0, delta: 0, valid: false };

    const modelFor = buildModelLookup(data.models, data.allModels);
    const amountFor = new Map(rows.map((r) => [r.sleeveKey, r.amount] as const));
    const distributing = this.isDistribution();

    const viewRows: SleeveAmountRowView[] = (data.sleeves ?? [])
      .filter((s) => s.accountId != null)
      .map((s) => {
        const sleeveKey = String(s.accountId);
        const amount = amountFor.get(sleeveKey) ?? 0;
        // The account's standing instruction for money moving in this direction. Contribution
        // and distribution percentages are INDEPENDENT in Orion -- neither is derived from the
        // other, nor from the target -- so each type reads its own column.
        const orionPercent = distributing ? s.distribution : s.contribution;

        // Only a distribution can overdraw a sleeve: it can take at most what the sleeve holds.
        // A contribution has no such ceiling -- adding money to a sleeve is always possible.
        const error =
          distributing && amount - s.currentValue > EPS
            ? `Exceeds this sleeve's value of ${formatMoney(s.currentValue)}`
            : null;

        return {
          sleeveKey,
          modelName: modelFor(s)?.name ?? s.modelAggName,
          currentValue: s.currentValue,
          orionPercent,
          amount,
          percentOfFunding: pool > 0 ? round2((amount / pool) * 100) : 0,
          error,
        };
      });

    const total = round2(viewRows.reduce((sum, r) => sum + r.amount, 0));
    return {
      rows: viewRows,
      total,
      /** Signed: positive means more has been committed to sleeves than the request carries. */
      delta: round2(total - pool),
      valid: viewRows.every((r) => r.error === null) && total - pool <= EPS,
    };
  });

  /**
   * Whether the quick-apply button can divide the amount by Orion's own percentages.
   *
   * Requires the percentages to total 100. They routinely do not — one live account carries
   * 5/20 against targets of 45/55 — and apportioning by a column summing to 25% would silently
   * place a quarter of the money and leave the rest looking unaccounted for. The same test the
   * allocation grid applies to its stated columns before seeding from them.
   */
  readonly sleeveAmountsCanApplyOrionPercent = computed(() => {
    const rows = this.sleeveAmounts().rows;
    if (rows.length === 0 || this.fundingAmount() <= 0) return false;
    return Math.abs(rows.reduce((sum, r) => sum + r.orionPercent, 0) - 100) < EPS;
  });

  /**
   * Whether there is anything for the quick-apply control to do.
   *
   * Separate from {@link sleeveAmountsCanApplyOrionPercent}, which asks whether the percentages
   * are usable at all. This asks whether there is a balance outstanding — and the distinction
   * matters for the UI: percentages that do not total 100 deserve an explanation, whereas
   * nothing left to place just means the button has no work and should not be offered.
   */
  readonly hasRemainingToApply = computed(() => this.sleeveApplyAmount() !== 0);

  /**
   * What the quick-apply control would actually move, signed, or 0 when it can do nothing.
   *
   * 🔴 AN EXCESS THE NEXT STEP CREATED CANNOT BE REMOVED FROM THIS ONE, which is the whole
   * reason this exists (AndyP, 2026-09-25). On a DISTRIBUTION `unappliedValue` is the
   * WHOLE-REQUEST balance -- it nets off cash taken and sale proceeds, both raised on the step
   * AFTER this one. Sales overshooting by $76.25 therefore surfaced here as "REMOVE EXCESS
   * $76.25" over two model boxes holding nothing, and pressing it did exactly nothing:
   * `applyRemainingToSleeves` spread -76.25 across the rows, clamped both to 0, and found no room
   * to redistribute the residue into.
   *
   * ⚠️ IT ONLY BECAME REACHABLE WITH ACMTECH-1000. Before over-raising was legal a distribution
   * had to land exactly, so a surplus could not survive to be seen here -- "remove excess" could
   * only ever mean "you typed too much into the model boxes", which this step genuinely can fix.
   * That case still works; it is now the only one offered.
   *
   * 🟢 CAPPED, NOT JUST GATED, so a mixed request behaves: models holding $10,000 with sales
   * overshooting by $76.25 offers to take back $76.25 and does. Only the part this step can reach
   * is ever named, so the label cannot promise more than the click delivers.
   *
   * 🟢 A reallocation and a contribution are unaffected either way -- their `unappliedValue` is
   * `netInvestable - placedInModels`, so an excess IS in the model boxes by construction and the
   * cap never binds.
   */
  readonly sleeveApplyAmount = computed(() => {
    const remaining = this.unappliedValue();
    if (Math.abs(remaining) < EPS) return 0;
    if (remaining > 0) return remaining;
    const removable = Math.min(-remaining, this.placedInModels());
    return removable < EPS ? 0 : -removable;
  });

  /**
   * Why the sleeve-amount step cannot be left, or null.
   *
   * ⚠️ Blocks only on being **over**, never on being under. The remainder is legitimately spent
   * on the later steps — buys and protected cash for a contribution, cash and sells for a
   * distribution — so demanding a full total here would make the step unleavable in exactly the
   * cases the wizard is designed for. Review holds the whole-request balance (see
   * {@link submitBlockers}).
   */
  readonly sleeveAmountsStepBlockedReason = computed<string | null>(() => {
    const state = this.sleeveAmounts();
    const overdrawn = state.rows.filter((r) => r.error !== null);
    if (overdrawn.length > 0) {
      return `${overdrawn.map((r) => r.modelName).join(', ')} `
        + `${overdrawn.length === 1 ? 'exceeds' : 'exceed'} the value held in the sleeve.`;
    }
    if (state.delta > EPS) {
      return this.isContribution()
        ? `${formatMoney(state.delta)} more has been allocated than this contribution carries.`
        : `${formatMoney(state.delta)} more has been taken from models than this distribution needs.`;
    }
    return null;
  });

  // ---- Funding step gate ---------------------------------------------------
  /**
   * Why the funding step cannot be left, or null.
   *
   * Ordered so the advisor is asked for one thing at a time, top of the form down — the amount
   * before the delivery details, because an amount of zero makes the rest moot.
   */
  /**
   * Required funding fields that are still empty, for the inputs to mark themselves with.
   *
   * ⚠️ WHY THESE EXIST SEPARATELY FROM {@link fundingStepBlockedReason}. That returns ONE
   * message, the first unmet condition in a fixed order, which is right for a single line beside
   * Next but wrong on the fields: an advisor told only "enter the amount" fills it in, presses
   * Next, and is then told about the bank account they could have chosen at the same time. These
   * are independent, so every outstanding field can say so at once (AndyP, 2026-09-22).
   *
   * ⚠️ Each condition is the SAME EXPRESSION the reason uses, not a paraphrase. If one drifts,
   * a field marks itself required while Next is happy, or worse the reverse.
   *
   * 🟢 They report EMPTY, not invalid. An amount that exceeds the account is a different problem
   * with its own message; marking it as "missing" would be untrue and would leave the advisor
   * clearing a field that is already filled in.
   */
  readonly fundingAmountMissing = computed(() => {
    this.formVersion();
    return !this.isCloseAccount() && this.fundingAmount() <= 0;
  });

  /**
   * ⚠️ Guarded on {@link usesBankAccount}, exactly as the reason is. Leave in Cash and Move to
   * Retail keep the money at the custodian, and a cheque is posted to an address, so marking a
   * bank account required on any of the three would demand something the request never files.
   */
  readonly bankAccountMissing = computed(
    () => this.usesBankAccount() && this.selectedStandingInstruction() === null,
  );

  /** The same, for the address a cheque is posted to. */
  readonly addressMissing = computed(
    () => this.usesAddressBook() && this.selectedStandingInstruction() === null,
  );

  /**
   * Schwab says this destination carries no investment-advisor authority.
   *
   * 🔴 GATED ON THE METHOD USING A BANK ACCOUNT. IA authority is a property of a standing BANK
   * authorization; a cheque is posted to an address and has none, so the warning described
   * something the request does not have. Leave in Cash and Move to Retail lose it for the same
   * reason — no destination, no authorization.
   *
   * ⚠️ Explicitly `=== false`, not falsy. Null means Schwab did not say, and rendering it as "no
   * authorization" asserts a fact nobody established — on most requests, which is how a warning
   * teaches people to ignore it.
   *
   * 🟢 One computed with two readers — the funding step's panel and Review & Submit's row. They
   * held the condition separately and one of them was missing this guard.
   */
  readonly noIaAuthority = computed(
    () => this.usesBankAccount() && this.selectedStandingInstruction()?.hasIaAuthority === false,
  );

  /**
   * The same again, for the method itself.
   *
   * Reachable only since the distribution stopped pre-selecting one (AndyP, 2026-11-03); the
   * reason it names has been in {@link fundingStepBlockedReason} all along. Never true for a
   * contribution, whose single method is seeded on load precisely because its select is locked.
   */
  readonly deliveryMethodMissing = computed(() => this.selectedDeliveryMethod() === null);

  readonly startDateMissing = computed(() => {
    this.formVersion();
    return this.startDateRequired() && this.fundingForm.controls.startDate.value == null;
  });

  readonly fundingStepBlockedReason = computed<string | null>(() => {
    // 🔴 Read DIRECTLY, as the first statement, and not left to the transitive reads below.
    //
    // This computed reads `startDate.value` off the form, and a form control is not a signal.
    // It used to depend on `formVersion` only through `isCloseAccount()`, `fundingAmount()` and
    // `startDateRequired()` — which looks sufficient and is not: an Angular `computed` does NOT
    // notify its consumers when it recomputes to an UNCHANGED value. Change only the start date
    // and every one of those three returns exactly what it returned before, the invalidation
    // stops there, and this never re-runs.
    //
    // The symptom was precise and misleading: the gate tracked the amount field perfectly (that
    // changes `fundingAmount`) while a chosen start date left "Choose when the transfer should
    // start" on screen with a date visible in the box. It reads as a broken datepicker.
    //
    // ⚠️ Any computed here that reads a form control directly needs this line. It is the idiom
    // the rest of the store already uses (`cashInput`, `federalTaxPct`, `isCloseAccount`).
    this.formVersion();

    // A close carries no typed amount: it is the whole account, and the account is never zero
    // by the time a distribution is being filed against it.
    if (!this.isCloseAccount()) {
      if (this.fundingAmount() <= 0) {
        return this.isContribution()
          ? 'Enter the amount being contributed.'
          : 'Enter the amount being distributed.';
      }
      // Only a distribution can ask for more than exists. A contribution's amount is money
      // arriving from outside, so the account's current value places no ceiling on it.
      if (this.isDistribution() && this.fundingAmount() - this.totalValue() > EPS) {
        return `${formatMoney(this.fundingAmount())} exceeds this account's value of `
          + `${formatMoney(this.totalValue())}.`;
      }
    }

    const method = this.selectedDeliveryMethod();
    if (method === null) return 'Choose how the money should be delivered.';
    // Guards a STALE selection rather than a fresh one. `selectedDeliveryMethod` resolves against
    // the offered list, so a request holding an id it is not allowed reads as no method at all
    // and is caught above -- but this states the rule explicitly, because the id is what reaches
    // the payload and a contribution filed against Check would be accepted by the database.
    //
    // ⚠️ Asks the ROW, not the request type. It used to be `locked && !method.isAch`, which was
    // only ever true for a contribution; the question is whether this method is allowed here, and
    // req.DeliveryMethod answers it per request type.
    if (!this.deliveryMethods().some((m) => m.id === method.id))
      return `${method.name} is not available for a `
        + `${this.isContribution() ? 'contribution' : 'distribution'}.`;

    // Not asked when the answer is fixed. A locked select cannot be used to satisfy a gate, so
    // demanding a value from it would block Next with no way forward -- the shape of gate this
    // file calls out for the bank account on Leave in Cash.
    if (!this.deliveryFrequencyLocked() && this.selectedDeliveryFrequency() === null)
      return 'Choose how often the transfer is made.';

    // WHERE A CHEQUE IS POSTED. Asked only of a method that uses the address book, which is
    // Check alone -- and never left unanswered, because the one case with nothing on file
    // auto-selects New (Other Address) and opens the payee panel instead.
    if (this.addressMissing()) {
      return this.funding()?.standingInstructionsFailed === true
        ? 'The account\'s addresses on file could not be loaded. Close and try again.'
        : 'Choose where the check should be sent.';
    }

    // Asked for only when the method actually uses one. Leave in Cash and Move to Retail keep
    // the money at the custodian and a cheque goes to an address, so demanding a bank account on
    // any of the three would make them unfileable.
    if (this.usesBankAccount() && this.selectedStandingInstruction() === null) {
      return this.funding()?.standingInstructionsFailed === true
        ? 'The account\'s bank authorizations could not be loaded. Close and try again.'
        : 'Choose the bank account for the transfer.';
    }

    // WHO THE MONEY IS GOING TO, for the methods that ask. After the destination checks above
    // because it is a question ABOUT that destination -- asking it first would name a second
    // problem while the first one is still unanswered.
    if (this.recipientTypeMissing()) {
      return 'Say whether this is going to a first party or a third party.';
    }

    if (this.startDateRequired()) {
      const start = this.fundingForm.controls.startDate.value;
      if (start == null) return 'Choose when the transfer should start.';
      if (start.getTime() < this.minStartDate().getTime()) {
        return this.isDistribution()
          ? 'A distribution cannot start before tomorrow.'
          : 'The start date cannot be in the past.';
      }
    }

    return null;
  });
  /**
   * Set when step 1 has nothing to list for a legitimate reason (no Custom Holdings
   * sleeve, or an empty one) rather than because the load failed. Rendered in place
   * of the positions table.
   */
  readonly positionsNotice = computed(() => this.data()?.positionsNotice ?? null);

  /**
   * Whether the empty step 1 is a Custom Holdings sleeve reporting a balance it returned no
   * holdings for -- as opposed to an account with no such sleeve, or one whose sleeve is
   * genuinely empty.
   *
   * 🔴 IT CHANGES WHAT THE SCREEN SAYS, not what it does. The request is still perfectly
   * usable -- a distribution can still be raised from the model sleeves -- so this must not gate
   * anything. It exists because "there is nothing here" and "we cannot currently see what is
   * here" are different messages, and the advisor can act on the second one (AndyP, 2026-09-25).
   */
  readonly customHoldingsUnavailable = computed(
    () => this.data()?.customHoldingsUnavailable === true,
  );

  /** Positions enriched with every derived field, for rendering. */
  readonly positions = computed<PositionView[]>(() => {
    const d = this.data();
    this.formVersion(); // establish dependency on form edits
    if (!d) return [];
    const total = d.account.totalValue;
    const dnt = this.dnt();
    // Hoisted out of the map: one read per recompute rather than one per position.
    const locksReviewable = REALLOCATION_V2_DISTRIBUTION_LOCKS_REVIEWABLE_SELLS && this.isDistribution();
    return d.positions.map((p, i) => {
      const group = this.positionsForm.at(i) as PositionForm | undefined;
      // Blank reads as 0. The input is allowed to sit empty while the advisor retypes it
      // (see the clamp in buildPositionsForm), so this is the one place that coercion happens
      // rather than every expression below having to repeat it.
      const rawSharesToSell = group ? group.controls.sharesToSell.value : p.shares ?? 0;
      const sharesToSell = Number.isFinite(rawSharesToSell) ? (rawSharesToSell as number) : 0;
      // 🔴 A ROW SELLING NOTHING IS NOT SELECTED, whatever the control says. This is the hole
      // `needsShares` used to BLOCK rather than close: a row flagged with an empty box derives
      // `inSleeve: false`, so its whole value leaves Custom Holdings while `soldValue` is 0 --
      // the money simply vanishes off the bar, and the payload carries a 0-share sell order.
      //
      // ⚠️ THE BLOCK WAS REMOVED BECAUSE THE UI CAN NO LONGER REACH THAT STATE (AndyP,
      // 2026-09-25) -- `selected` is derived from this very figure. But "unreachable through the
      // UI" is not the same as "cannot happen": `toggleSellAll` and the SMA commit write
      // `selected` directly, and a strip spec found exactly this by setting it by hand. Making
      // the projection defend itself means the invariant holds for every caller, not just the
      // ones that remember.
      //
      // 🟢 Guarded on the row HAVING a share count. Orion reports none for some holdings; those
      // are sold whole (`soldValue` falls back to market value), so 0 shares is not a signal
      // there and the flag stands on its own.
      const selected = group
        ? group.controls.selected.value && ((p.shares ?? 0) <= 0 || sharesToSell > 0)
        : false;
      const isPartialSell = selected && p.shares != null && sharesToSell > 0 && sharesToSell < p.shares;
      // ⚠️ Derived POSITIVELY, not as `selected && !isPartialSell`. That negation is true for
      // three different states, and only two of them are a liquidation: a full sell, a position
      // with no share count (sold whole), and -- the trap -- a SELECTED row with a blank share
      // box.
      //
      // 🔴 THE LAST ONE IS NOW PREVENTED ABOVE rather than blocked at the step. It used to say
      // "cannot be filed today because positionsStepBlockedReason holds step 1" -- that gate is
      // empty now (AndyP, 2026-09-25), and the `selected` derivation a few lines up is what stops
      // it instead. Which is the stronger place for it: a row selling nothing is not selected at
      // all, so this cannot label it Sell All however the control was written.
      //
      // `>=` rather than `===`: share counts can be fractional, and buildPositionsForm's clamp
      // already caps at the full lot, so equality is what should hold and >= is what survives a
      // float landing a hair over.
      const isSellAll =
        selected && (p.shares == null || (sharesToSell > 0 && sharesToSell >= p.shares));
      const inSleeve = !selected || isPartialSell;
      const keptShares =
        p.shares == null ? null : !inSleeve ? 0 : isPartialSell ? p.shares - sharesToSell : p.shares;
      // ⚠️ ROUNDED, and the kept half DERIVED from the sold half rather than computed
      // alongside it. Both matter.
      //
      // A partial sell prices its halves by proportion -- marketValue x n / shares -- which is
      // sub-cent for almost any n. That dust reached `investable` through `totalSoldValue` and
      // `nonCashCustomHoldingsValue`, so the amount left to place was a fraction of a cent off a
      // whole number of cents. Sweeping it into protected cash then rounded the destination but
      // not the source, leaving a half-cent residue that the strip displayed as $0.01 while the
      // EPS gates -- which compare against exactly that half cent -- refused to call it either
      // settled or a problem. Next stayed enabled beside a red OVER-ALLOCATED figure.
      //
      // Deriving kept from sold guarantees the two halves still sum to the position's market
      // value. Rounding them independently would let a position's parts drift from its whole,
      // which is the same class of bug one level down: both halves feed `investable`.
      const soldValue = !selected
        ? 0
        : p.shares ? round2((p.marketValue * sharesToSell) / p.shares) : p.marketValue;
      let keptValue: number;
      if (!inSleeve) keptValue = 0;
      else if (isPartialSell && p.shares) keptValue = round2(p.marketValue - soldValue);
      else keptValue = p.marketValue;
      return {
        ...p,
        // ⚠️ OVERRIDES the loaded value, and is the ONLY place the distribution lock is applied.
        // Everything downstream already respects `sellable` -- the disabled checkbox in
        // `buildPositionsForm`, the N/A cell and `toggleSellAll`'s skip
        // -- so putting it here makes the lock complete rather than needing four edits that could
        // disagree. See REALLOCATION_V2_DISTRIBUTION_LOCKS_REVIEWABLE_SELLS for why a
        // distribution locks what a reallocation deliberately does not.
        sellable: p.sellable && !(locksReviewable && p.requiresReview),
        selected,
        sharesToSell,
        // Prefer the source-reported values (Orion CurrentInPer / UnrealizedGainLoss);
        // fall back to derived when absent (e.g. the static demo payload).
        pctOfAccount: p.reportedPctOfAccount ?? (total > 0 ? (p.marketValue / total) * 100 : 0),
        gainLoss: p.reportedGainLoss ?? (p.totalCost == null ? null : p.marketValue - p.totalCost),
        isPartialSell,
        isSellAll,
        inSleeve,
        keptShares,
        keptValue,
        soldValue,
        isDoNotTrade: dnt.has(p.ticker),
      };
    });
  });

  /**
   * Positions that remain in the account (the Custom Holdings sleeve).
   *
   * EMPTY once an SMA is committed, and this is the one place that says so. An SMA is the
   * whole account — the external manager takes it and liquidates — so nothing is kept, and
   * everything that describes what the account holds reads through here:
   * {@link nonCashCustomHoldingsValue}, {@link resultingHoldings} (and through it the
   * payload's `customHoldings` and the Do Not Trade candidates), and the strip's Remaining
   * Positions. Expressing it once here is why none of them needs a case of its own.
   *
   * The positions are not itemised as SELLS. There is no instruction for the trade team to
   * execute — the manager does it — so a sell row would be an order nobody places.
   */
  readonly sleevePositions = computed(() =>
    this.smaCommitted() ? [] : this.positions().filter((p) => p.inSleeve),
  );
  /**
   * Positions the user has marked to sell (fully or partially), **liquidations first**.
   *
   * Sell All before Sell, matching the CRM request tracker, so the advisor and the trade team see
   * the same instructions in the same order rather than each screen following its own.
   *
   * ⚠️ The order reaches the PAYLOAD too, since `buildRequest` maps this collection — which is
   * intended: the filed request lists liquidations first for the same reason the screens do. CRM
   * orders on read as well, so neither side depends on the other having done it.
   *
   * `sort` is stable, so within each group the rows keep Orion's own order rather than being
   * re-shuffled into something arbitrary.
   */
  readonly soldPositions = computed(() => {
    // An explicit rank rather than sorting on the boolean: "false sorts after true" is a fact
    // about coercion, not a statement of what the order is meant to be.
    const rank = (p: PositionView): number => (p.isSellAll ? 0 : 1);
    return this.positions()
      .filter((p) => p.selected)
      .sort((a, b) => rank(a) - rank(b));
  });

  /**
   * Why Raise Cash cannot be left, or null when it can.
   *
   * 🟢 IT BLOCKS AGAIN, on one rule: an Amount to Use above what that model can release
   * (ACMTECH-1039). Both of the ORIGINAL branches are still gone for the reasons recorded in
   * the body, a day apart, and neither removal was about this gate.
   *
   * ⚠️ WHICH IS WHY IT WAS KEPT WHEN IT CAUGHT NOTHING (AndyP, 2026-09-25). The predicate is
   * the cheap half; the expensive half is the wiring -- {@link canProceed}, {@link canJumpTo}
   * and the footer line in the wizard shell, which together make Next, the step indicator and
   * the message agree. The draw rule inherited all of it and had to add nothing.
   *
   * ⚠️ The sell and cash rules are still prevented at the source rather than guarded here --
   * see the `selected` derivation in {@link positions}.
   *
   * Deliberately shaped like {@link buyStepBlockedReason} - same wording pattern, the same
   * naming of the offending tickers - so a rule added here reaches the screen the same way that
   * one does.
   */
  readonly positionsStepBlockedReason = computed<string | null>(() => {
    // 🔴 THE "NO SHARE COUNT" BLOCK IS GONE, because the state it caught cannot occur any more
    // (AndyP, 2026-09-25). It guarded a row that was TICKED with an empty box -- which derived
    // `inSleeve: false`, so the position's value counted as freed while `soldValue` was 0 and the
    // payload carried a 0-share sell. With no tick anywhere, `selected` is derived from the
    // figure: a row at 0 is simply a row that is not selling. Removed rather than left standing,
    // because a guard that can never fire reads as protection and is not.

    // 🔴 OVER-RAISING NO LONGER BLOCKS, and this is where it used to. The reasoning was that the
    // surplus "has nowhere to go -- the account is not being restructured, so there is no sleeve
    // to absorb it". The account itself absorbs it: the money stays there as cash, which is what
    // happens at settlement anyway.
    //
    // What that block actually cost was the advisor's own remedy. Shares are granular, so when
    // cash cannot cover the remainder the only way forward is to sell one more -- and that flips
    // the request from short to over. Both sides blocked, so an account holding no cash and one
    // $512 share could never raise $400 at all (AndyP, 2026-09-24).
    //
    // ⚠️ Under-sourcing still does not block HERE -- the advisor may be working down the list --
    // and `submitBlockers` still refuses a short request at Review. Only the over half went.

    // 🔴 AND THE CAP RULE HAS GONE WITH ITS CONTROL (AndyP, 2026-10-29). Amount to Raise moved
    // onto Model Allocations, so an over-cap figure now blocks THAT step -- see
    // `allocationStepBlockedReason`. A gate naming a box on another screen is a gate the
    // advisor cannot act on.
    //
    // ⚠️ THE HOOK STAYS, EMPTY, exactly as it did for the three weeks it caught nothing
    // (AndyP, 2026-09-25). The predicate is the cheap half; the expensive half is the wiring --
    // `canProceed`, `canJumpTo` and the footer line in the shell, which together make Next, the
    // step indicator and the message agree. The next rule added here inherits all of it.
    return null;
  });

  /** Total market value being sold (Σ soldValue) — the Positions Being Sold total. */
  readonly totalSoldValue = computed(() => this.soldPositions().reduce((sum, p) => sum + p.soldValue, 0));

  /**
   * Sold positions whose proceeds are held back rather than freed to spend.
   *
   * Everything outside Mutual Fund / CASH / Equity, plus anything that arrived without a usable
   * type — see `requiresSellReview` in the data service. The firm cannot commit these proceeds to
   * a model or a purchase inside one request, so they become cash set aside.
   */
  readonly reviewSoldPositions = computed(() => this.soldPositions().filter((p) => p.requiresReview));

  /** Σ proceeds held back for review. Reaches the payload as its own custom-holding line. */
  readonly reviewSoldValue = computed(
    () => this.reviewSoldPositions().reduce((sum, p) => sum + p.soldValue, 0),
  );

  /**
   * Sale proceeds the advisor may actually spend in THIS request.
   *
   * ⚠️ **Use this, not {@link totalSoldValue}, anywhere money is being made available.**
   * `totalSoldValue` is what the request sells; this is what that buys. They were the same figure
   * until reviewable sales existed, which is exactly why the difference is easy to miss.
   *
   * ⚠️ The same sum is written out in THREE places — `investable`, `investableBeforeBuys` and
   * `protectedCashStepBlockedReason`'s inline pool — whose docs promise they "agree by
   * construction". Miss one and they stop agreeing, which is the shape of ACMTECH-973.
   */
  readonly allocatableSoldValue = computed(() => this.totalSoldValue() - this.reviewSoldValue());

  // ---- Step 2: buys ---------------------------------------------------------
  /** Raw buy rows (step 2). Empty and unused when the step is flagged off. */
  readonly buyRows = signal<readonly BuyRow[]>([]);

  /**
   * Buy rows with their derived estimates.
   *
   * **Returns an empty list whenever the buys step is absent**, which is what lets every
   * downstream derivation — investable, the Do Not Trade merge, Review & Submit, change
   * detection, the payload — ignore both the flag and the request type entirely. Nothing
   * else checks {@link REALLOCATION_V2_BUY_STEP_ENABLED}, so nothing can drift out of
   * step with it.
   *
   * Keyed off the step list rather than the flag, so the Initial Account mode — which
   * drops the step — is covered by the same single guard rather than a second one.
   */
  readonly buys = computed<BuyRowView[]>(() => {
    if (!this.steps().includes('buys')) return [];
    const sold = this.soldSecurityKeys();
    // Tickers already claimed by an earlier row, so the second AAPL is the one flagged.
    const seenTickers = new Set<string>();
    return this.buyRows().map((r) => {
      const price = r.security?.price ?? 0;
      const hasSecurity = !!r.security;
      // A hand-typed ticker has no id and never gets a price, so its row is driven by the
      // amount the advisor typed rather than by shares. The two are exclusive: exactly one
      // of them is live on any row, decided by where the security came from.
      const isManual = hasSecurity && r.security?.securityId == null;
      const hasShares = r.shares > 0;
      const hasAmount = (r.manualAmount ?? 0) > 0;
      const hasFigure = isManual ? hasAmount : hasShares;
      const tickerKey = r.security?.ticker.trim().toUpperCase() ?? '';
      const isDuplicate = !!tickerKey && seenTickers.has(tickerKey);
      if (tickerKey) seenTickers.add(tickerKey);
      return {
        ...r,
        // ⚠️ ROUNDED. `shares x price` is sub-cent for most prices, and this figure reaches
        // `investable` through `totalBuyValue` -- so a purchase was enough on its own to make
        // the amount left to place a fraction of a cent off a whole number of cents, which is
        // half of what produced the $0.01 that could be neither placed nor complained about.
        // It is also sent to CRM as `estimatedValue`, and a dollar amount carrying four decimal
        // places is wrong on the wire regardless.
        //
        // The hand-typed branch is rounded at the point it is stored instead, in
        // `setBuyAmount`. This comment used to claim a counterpart to `normalizeCash` already
        // held that box to whole cents; there was no such thing, so a typed 2500.005 reached
        // CRM's `estimatedValue` with three decimal places.
        estimatedAmount: isManual
          ? r.manualAmount ?? 0
          : hasShares && price > 0 ? round2(r.shares * price) : 0,
        isActionable: hasSecurity && hasFigure,
        needsShares: hasSecurity && !isManual && !hasShares,
        needsAmount: isManual && !hasAmount,
        // Still keyed on shares: an amount typed against no security is not possible,
        // because the amount box only appears once a hand-typed ticker exists.
        // ⚠️ OR the pending term, and that second half is load-bearing. A row whose picker
        // closed on unresolved text has no shares -- the shares box is disabled without a
        // security -- so the `hasShares` half alone would read it as an empty row and let it
        // through Next, to be dropped without a word at submit.
        needsSecurity: !hasSecurity && (hasShares || r.pendingTerm.trim().length > 0),
        isOrphan: !hasSecurity && !hasShares,
        // Derived from the missing id rather than stored alongside it, so "no id" and
        // "needs review" cannot drift apart. Everything the security master resolves has
        // an id; nothing the advisor types does.
        needsTradeTeamReview: isManual,
        isDuplicate,
        // Checked on BOTH keys: a hand-typed buy has no id, so an id-keyed sell of the
        // same security would not match it, and the contradiction would ship.
        conflictsWithSell:
          !!r.security
          && (sold.has(holdingKey(r.security.securityId, r.security.ticker))
            || sold.has(holdingKey(null, r.security.ticker))),
      };
    });
  });

  /**
   * Securities marked to sell on step 1, keyed for conflict detection.
   *
   * Includes partial sells: selling 50 and buying 30 of the same security still nets to
   * selling 20, so it is an instruction the trade team would have to unpick rather than
   * act on.
   */
  private readonly soldSecurityKeys = computed(() => {
    const keys = new Set<string>();
    for (const p of this.soldPositions()) {
      keys.add(holdingKey(p.securityId, p.ticker));
      // The ticker form as well, so a buy with no id — a hand-typed ticker — still
      // matches a sell of the same security, which is keyed by id.
      keys.add(holdingKey(null, p.ticker));
    }
    return keys;
  });

  /**
   * Buys of a security the advisor is also selling.
   *
   * Contradictory as an instruction — and distinct from buying more of something being
   * **kept**, which is fine and is what {@link resultingHoldings} merges.
   */
  readonly conflictingBuys = computed(() => this.buys().filter((b) => b.conflictsWithSell));

  /** Rows buying a ticker an earlier row already buys. */
  readonly duplicateBuys = computed(() => this.buys().filter((b) => b.isDuplicate));

  /**
   * Why the buys step will not let the advisor continue, or null when it will. Drives
   * `canProceed` for this step.
   *
   * Not rendered in the footer: every reason is already visible where it happened — the
   * conflict and incomplete cases flag their own row, and over-buying shows a banner
   * above the table. A second copy in the footer would just be noise. Kept as a single
   * named reason so the gate has one source of truth, and so a test can say *why* it is
   * blocked rather than only that it is.
   */
  readonly buyStepBlockedReason = computed<string | null>(() => {
    if (this.conflictingBuys().length > 0) {
      const names = this.conflictingBuys().map((b) => b.security?.ticker).filter(Boolean);
      return `${names.join(', ')} ${names.length === 1 ? 'is' : 'are'} being sold on the previous step. `
        + 'Remove the sell or the buy.';
    }
    // Two rows for one ticker are two trade orders for the same security. The resulting
    // sleeve merges them, so the wizard would show one holding while sending two
    // instructions — the trade team reconciles the difference by hand or gets it wrong.
    if (this.duplicateBuys().length > 0) {
      const names = [...new Set(this.duplicateBuys().map((b) => b.security?.ticker).filter(Boolean))];
      return `${names.join(', ')} ${names.length === 1 ? 'is' : 'are'} on more than one buy row. `
        + 'Combine the shares into a single row.';
    }
    if (this.incompleteBuys().length > 0) return 'Finish or remove the incomplete buy rows.';
    if (this.buysExceedInvestable()) return 'Buys exceed the amount available to invest.';
    return null;
  });

  /** Rows that would actually trade — a security picked and a share count entered. */
  readonly actionableBuys = computed(() => this.buys().filter((b) => b.isActionable));

  /**
   * Buys of a hand-typed ticker. Surfaced on the row and again on Review & Submit.
   *
   * A **warning, not a block**: an advisor buying something Eclipse's security master
   * does not carry is a real case — a new issue, or a symbol we simply do not stock — and
   * refusing it would send them out of the wizard to file the request another way. What
   * it must never do is look identical to a resolved buy, because the trade team is the
   * one who has to notice.
   */
  readonly buysNeedingReview = computed(() => this.actionableBuys().filter((b) => b.needsTradeTeamReview));

  /**
   * Half-filled rows the advisor has to resolve: a security with no shares, or shares
   * against no security. Either would submit an instruction the trade team cannot act
   * on, so they block Next.
   */
  readonly incompleteBuys = computed(() =>
    this.buys().filter((b) => b.needsShares || b.needsAmount || b.needsSecurity),
  );

  /**
   * Σ estimated buy amounts, INCLUDING the ones pending review. An **estimate**: it multiplies
   * out Orion's point-in-time price, where every other total in this wizard reconciles with
   * Orion to the cent.
   *
   * ⚠️ This is the ARITHMETIC total and it must stay whole: every dollar here leaves the
   * investable balance, whoever ends up executing it, so `customHoldingsValue`,
   * `investable` and `buysExceedInvestable` all use this rather than the split below.
   *
   * What the advisor is SHOWN is split in two -- see {@link resolvedBuyValue}.
   */
  readonly totalBuyValue = computed(() =>
    this.actionableBuys().reduce((sum, b) => sum + b.estimatedAmount, 0),
  );

  /**
   * Buys the trade team can execute immediately -- everything except the pending-review rows.
   *
   * This is what "Purchases" means to the advisor. A hand-typed ticker is not a purchase yet:
   * nobody can act on it until the security master resolves it, and until then the money is
   * held as cash. It is reported under Protected Cash instead, with
   * {@link protectedCashFromReview} carrying that half.
   *
   * ⚠️ A DISPLAY split, not an arithmetic one. `resolvedBuyValue + protectedCashFromReview`
   * is exactly `totalBuyValue`, so the two together still partition the same dollars once --
   * which is what lets the strip show them as separate columns that still add up.
   */
  readonly resolvedBuyValue = computed(() =>
    this.actionableBuys()
      .filter((b) => !b.needsTradeTeamReview)
      .reduce((sum, b) => sum + b.estimatedAmount, 0),
  );

  /** Buys the trade team can execute immediately, for the tables that list them. */
  readonly resolvedBuys = computed(() =>
    this.actionableBuys().filter((b) => !b.needsTradeTeamReview),
  );

  /** Custom Holdings value from the (non-cash) sleeve positions only. */
  readonly nonCashCustomHoldingsValue = computed(() =>
    this.sleevePositions().reduce((sum, p) => sum + p.keptValue, 0),
  );

  /**
   * Cash the advisor keeps as a custodial holding (step-3 input). Cash is a
   * first-class Custom Holdings line item now, not a separate protected-cash
   * scalar — it folds into `customHoldingsValue` and becomes the CASH line in
   * `buildRequest()`.
   */
  readonly cashInput = computed(() => {
    this.formVersion();
    return this.cashControl.value || 0;
  });

  /**
   * Displayed Custom Holdings value = non-cash sleeve + custodial cash + buys.
   *
   * Buys belong here because that is where a bought security lands: value moves out of
   * cash (or sale proceeds) and into the hand-traded sleeve. Adding them here is the
   * single edit that makes `investable` shrink correctly, rather than each downstream
   * figure having to subtract buys for itself.
   */
  readonly customHoldingsValue = computed(
    () => this.nonCashCustomHoldingsValue()
      // ⚠️ Cash the advisor KEPT belongs here, and this is the only place it is added. The strip
      // term, the Review & Submit total and the payload all read this figure, so folding it in
      // once is what stops three surfaces disagreeing about what is in the sleeve.
      + this.cashKept()
      + this.cashInput()
      // ⚠️ REVIEWABLE SALE PROCEEDS STAY IN THE SLEEVE, and leaving this out was a real bug: the
      // position leaves `nonCashCustomHoldingsValue` the moment it is ticked, and the proceeds are
      // deliberately kept out of `investable` -- so without this line the money is in NEITHER and
      // simply disappears off the bar. Observed on a $551,424.68 account: four structured notes
      // ticked, and the strip's two runs fell $213,279.50 short of the account total, which was
      // exactly their value.
      //
      // Selling one of these moves value from a POSITION row to a CASH row inside the same
      // sleeve, so the sleeve's total is unchanged. That is why nothing visibly moves on the
      // strip today, and why splitting Cash into its own run is worth doing.
      + this.reviewSoldValue()
      + this.totalBuyValue(),
  );

  /**
   * Cash this request is holding **outside** the sleeve's ordinary cash — what the bar draws as
   * **Cash On Hold**.
   *
   *   reviewSoldValue          a SALE whose proceeds cannot be committed in this request
   *   protectedCashFromReview  a BUY whose ticker the Trade Team has yet to resolve
   *
   * ⚠️ THE ADVISOR'S OWN SET-ASIDE IS NOT HERE (AndyP, 2026-09-15), and the exclusion is the
   * whole point of this figure existing separately from {@link totalProtectedCash}. Step 4's
   * `cashInput` is just cash in the Custom Holdings sleeve, held by instruction — the same kind
   * of thing as the custodial cash sitting beside it, differing only in that somebody asked for
   * it. These two are different: nobody chose them, and both resolve when someone acts. Grouping
   * an instruction with a queue made the bar claim the advisor had done something they had not.
   *
   * 🟢 What is left is exactly "money this request cannot invest, because something is
   * unresolved". Both members clear on their own once the Trade Team acts, which is what makes
   * them one run rather than two.
   */
  readonly cashOnHold = computed(
    () => round2(this.protectedCashFromReview() + this.reviewSoldValue()),
  );

  /**
   * The sleeve minus the cash on hold — what the bar draws as Custom Holdings.
   *
   * Positions kept, buys the Trade Team can execute, the custodial cash the advisor left alone,
   * and the advisor's own set-aside.
   *
   * ⚠️ BY SUBTRACTION, deliberately, rather than re-summing the parts. `customHoldingsValue`
   * folds in `cashKept`, while the cash computeds sum `keptCashRows` — the same quantity arrived
   * at two ways, and they can land a cent apart. Subtracting guarantees these two runs add back
   * to `customHoldingsValue` whatever rounding does, which is what keeps the strip tying to the
   * account total.
   *
   * 🟢 Exists so the bar can draw Custom Holdings and cash-on-hold as SEPARATE runs. Apart, money
   * moving between them is visible — which it was not when one run covered both. Selling a
   * security whose proceeds are held moves value from this figure to {@link cashOnHold}, and the
   * bar shows it happen.
   *
   * ⚠️ Subtracts {@link cashOnHold}, NOT `totalProtectedCash`, so the step-4 set-aside stays in
   * this run. Both members of `cashOnHold` are inside `customHoldingsValue` already
   * (`reviewSoldValue` directly, `protectedCashFromReview` via `totalBuyValue`), so this only
   * moves money between the two runs, the pair still sums to the sleeve, and it cannot go
   * negative.
   */
  readonly customHoldingsExcludingCashOnHold = computed(
    () => round2(this.customHoldingsValue() - this.cashOnHold()),
  );

  /**
   * Investable money the request has not placed anywhere yet.
   *
   * `investable` is what is NOT committed to Custom Holdings; `allocation().totals.value`
   * is what the advisor has actually put into models. The gap is money the request would
   * leave homeless -- freed by a sell, or cash that was already sitting idle.
   *
   * It is a blocking condition, not a note: a request that leaves money unplaced is
   * incomplete, and {@link submitBlockers} refuses Submit while this is non-zero.
   *
   * ⚠️ SUBMIT, not the allocation step. This said {@link allocation} and had been wrong for a
   * while: "every investable dollar has to land somewhere" was deliberately moved OFF that
   * computed, because binding it there made the allocation grid the only place a shortfall
   * could be resolved -- see the note on `valid`. Corrected under ACMTECH-1039, which depends
   * on the real behaviour: freeing money from a model leaves it unplaced BY DESIGN, and the
   * advisor has to be able to carry it to the buys or protected-cash steps to spend it.
   *
   * The advisor closes it three ways, and all three are reachable from where they are:
   * put it into a model, buy something with it, or protect it as cash.
   *
   * ⚠️ **Signed, and deliberately NOT clamped at zero.** Negative means the models are
   * allocated more than the account has available -- protect cash after allocating and this
   * is where the excess shows up. Clamping hid it, and hid it in the worst possible way: the
   * strip renders these terms as an equation, so a suppressed -$5,000 made four correct
   * figures visibly fail to reach their own total with nothing on screen accounting for the
   * difference.
   */
  readonly unappliedValue = computed(() =>
    // ⚠️ Rounded to the cent, so what the advisor READS and what the gates DECIDE are the same
    // number. They were not: a half-cent residue rendered as "$0.01" through the currency pipe
    // while `> EPS` (EPS being exactly half a cent) declined to block on it, which is how the
    // strip came to show a red OVER-ALLOCATED beside an enabled Next.
    //
    // A backstop rather than the fix. The sub-cent values are eliminated where they are created
    // (see soldValue above), so this should now be rounding a number that is already whole
    // cents; it is here because money the advisor acts on must never disagree with itself, and
    // any future arithmetic that reintroduces dust should surface as a real block rather than
    // as this.
    // 🔴 A DISTRIBUTION is the one type this formula does not fit, and the difference is
    // directional rather than cosmetic. Everywhere else the pool is money looking for a home
    // and this is what has not found one. In a distribution the advisor states a target and then
    // *raises* it from three places — sleeves, custodial cash, and sold positions — so what is
    // outstanding is the target less everything sourced so far.
    //
    // Sells are a SOURCE here, where in a reallocation they enlarge the pool instead. Feeding
    // them through `sourcePool` would have double-counted them against the target.
    //
    // ⚠️ `netInvestable`, NOT `investable`, on the reallocation side (merge, ACMTECH-939). The
    // clamped figure cannot go below zero, so an overspend from BUYS was arithmetically
    // invisible: 0 minus 0 allocated is 0, which reads as a balanced request. Over-allocating to
    // MODELS still showed, because that travels through the subtrahend -- which is why it went
    // unnoticed. Only one of the two ways to over-commit was reported.
    //
    // ⚠️ `placedInModels()`, NOT `allocation().totals.value`. They are the same expression for a
    // reallocation; the indirection is what makes a CONTRIBUTION read its sleeve-amount rows
    // instead of an allocation grid that is deliberately empty.
    this.isDistribution()
      ? round2(this.fundingAmount() - (this.placedInModels() + this.cashTaken() + this.totalSoldValue()))
      : round2(this.netInvestable() - this.placedInModels()),
  );

  /** Investable = total − Custom Holdings (cash and buys included); clamped ≥0. */
  /** Σ what the model sleeves hold today. Same source the allocation grid opens from. */
  readonly modelSleeveValue = computed(() =>
    (this.data()?.sleeves ?? []).reduce((sum, sl) => sum + sl.currentValue, 0),
  );

  /**
   * The money this request has to work with, **before** anything is done with it.
   *
   * 🔴 The one seam where the request types genuinely differ, and the reason it is named for
   * the pool's *origin* rather than for a type:
   *
   * - Types **1 and 2** rearrange what the account already holds, so the pool is summed from
   *   the account — sleeves, custodial cash, and anything being sold. Note this is summed from
   *   parts and never taken as `totalValue`; see {@link investable} for the penny that cost.
   * - Types **3 and 4** are handed a figure by the advisor on step 1. The account's own value is
   *   context, not budget: a $6,000 contribution to a $2m account has $6,000 to place.
   *
   * Every downstream figure reads this instead of re-deriving the sum, so the reallocation
   * arithmetic is bit-for-bit what it was while the new types get the right budget.
   */
  readonly sourcePool = computed(() => {
    if (this.isMoveMoney()) return this.fundingAmount();
    // ⚠️ `cashReleased`, NOT `totalCash`, and `allocatableSoldValue`, NOT `totalSoldValue`
    // (merge with ACMTECH-939/970). Custodial cash is OPT-IN: cash the advisor has not released
    // is still theirs and is not available to invest. Proceeds from a reviewable sale are held
    // and never reach this pool -- that restriction is the whole point of the review.
    //
    // 🔴 THIS LINE MERGED CLEANLY AND WAS STILL WRONG. It is the abstraction three formulas now
    // read through, so it carried the pre-opt-in sum silently into all of them while git flagged
    // nothing. The arithmetic it replaced was rewritten on develop in three separate places, each
    // of which conflicted and was reviewed; this one did not, because only one side touched it.
    return this.modelSleeveValue() + this.cashReleased() + this.allocatableSoldValue();
  });

  /**
   * How much of the pool the advisor has committed to model sleeves.
   *
   * The other half of the type switch. Types 1 and 2 state a target allocation, so the figure
   * comes from the allocation grid's own total; types 3 and 4 state dollars per sleeve, so it is
   * the sum of those.
   *
   * ⚠️ Both are needed, not just {@link sourcePool}. Three separate figures subtract "money
   * already promised to models" — {@link investableBeforeBuys}, {@link unappliedValue} and the
   * protected-cash pool — and every one of them read the allocation grid directly. On a
   * contribution that grid is deliberately EMPTY, so each would have measured against zero and
   * let the advisor place the same money twice.
   */
  readonly placedInModels = computed(() =>
    this.isMoveMoney()
      ? round2(this.sleeveAmountRows().reduce((sum, r) => sum + r.amount, 0))
      : this.allocation().totals.value,
  );

  /**
   * What this request has available to invest.
   *
   * ⚠️ **Built from its parts, NOT as `totalValue − customHoldingsValue`.**
   *
   * Orion hands us two figures for the same account that do not agree: the account total is
   * summed from each sleeve's `accountValue.totalValue`, while the Custom Holdings figures are
   * summed from that sleeve's individual holdings on the PENDING basis. On a live account the
   * two were a cent apart -- `60,014.34 + 92,119.61 + 607.48` came to 152,741.43 against a
   * reported 152,741.42.
   *
   * Subtracting made this figure the residual, so it absorbed that disagreement: an account
   * holding $607.48 of cash and doing nothing else reported $607.47 available to invest. The
   * penny landed on the one number the advisor has to act on, and the one they check against
   * their cash. Summing the parts puts it on the account total instead, which is context they
   * read rather than a figure they reconcile against — and the strip no longer presents these
   * as an equation to verify, so a cent between the total and the parts is not on show.
   *
   * ⚠️ One real behavioural difference, beyond rounding: `data.sleeves` drops sleeves whose
   * `targetBalanceType` is not percent (the service warns to console). Their value used to
   * inflate this figure, forcing the advisor to allocate money held in a sleeve the grid
   * cannot show; now it is simply out of scope. Better, but it means the strip's figures can
   * fall short of Total Account Value by more than a cent on such an account.
   */
  readonly investable = computed(() => Math.max(0, this.netInvestable()));

  /**
   * The same arithmetic as {@link investable}, **without the floor at zero**.
   *
   * ⚠️ THE CLAMP WAS HIDING AN OVERSPEND, and splitting it off is what stops that. `investable`
   * floors at 0 because it is a BUDGET -- the model pot is clamped to it, and a negative ceiling
   * is meaningless. But anything REPORTING the state of the request needs the real number: a buy
   * larger than the money available drove the sum negative, the floor returned 0, and
   * `unappliedValue` came out at exactly 0 -- so the strip announced FULLY ALLOCATED over a
   * request that was short, while the buy step's own banner said the opposite.
   *
   * Observed on an account with no models: one $3,479.90 buy against $0.00 available, a green
   * tick on the strip, and a Custom Holdings run drawn LARGER than the whole account by that
   * exact figure. The bar had stopped reconciling and still called itself balanced.
   *
   * 🟢 Use this for anything that DESCRIBES the request; use {@link investable} for anything that
   * SPENDS against it. They differ only when the request is over-committed, which is precisely
   * the case worth telling the advisor about.
   */
  readonly netInvestable = computed(() => {
    // An SMA is 100% of the account, so the account total IS what there is to invest —
    // model sleeves, custodial cash and the Custom Holdings positions the manager will
    // liquidate, all of it. Not summed from the parts here, deliberately: the parts and
    // the total disagree by a cent (see above), and `commitToSma` funds the row from this
    // same figure, so taking both from one number leaves nothing unplaced by construction.
    if (this.smaCommitted()) return this.totalValue();

    return this.sourcePool() - this.cashInput() - this.totalBuyValue();
  });

  /**
   * What was investable before buys — the ceiling on how much can be bought.  /**
   * What was investable before buys — the ceiling on how much can be bought.
   *
   * Kept separate from {@link investable} because that figure already has buys taken
   * out of it, so comparing buys against it would always look satisfied.
   */
  readonly investableBeforeBuys = computed(() =>
    Math.max(
      0,
      this.sourcePool()
      - this.cashInput()
      // ⚠️ ACMTECH-945 -- money already committed to models is NOT available to buy with.
      //
      // Allocation is now the step BEFORE this one, so by the time the advisor is buying they
      // have already said what the models should hold. Without this subtraction the Buy step
      // would happily spend that same money, and nothing would object until Review.
      //
      // This is what makes "use as you go" a real budget rather than a suggestion. It holds for
      // a contribution too, where the same order applies against the contributed amount.
      - this.placedInModels(),
    ),
  );

  /**
   * True when buys would spend more than the account has to spend.
   *
   * Blocks Next rather than clamping the share count. Silently rewriting a number the
   * advisor typed is worse than refusing to advance and saying why — and a share count
   * cannot be clamped to a dollar ceiling without inventing a fractional share.
   */
  readonly buysExceedInvestable = computed(
    () => this.totalBuyValue() - this.investableBeforeBuys() > 0.005,
  );

  /**
   * Whether there is at least a cent left to allocate to models. Guards the
   * step 3 → step 4 hand-off: when protected cash has consumed the whole
   * investable amount there is nothing to allocate. Uses a one-cent floor so
   * floating-point dust left after clamping (e.g. 0.0000001) still reads as
   * "nothing investable", matching the $0.00 the currency pipe displays.
   */
  readonly hasInvestableForModels = computed(() => this.investable() >= 0.01);

  /**
   * The request commits more than the account can cover.
   *
   * ⚠️ Reads {@link unappliedValue}, so it catches BOTH ways to over-commit -- money put into
   * models, and money spent on buys. `buysExceedInvestable` sees only the second and is scoped
   * to the buy step's own banner; this is the whole-request condition.
   *
   * Epsilon-guarded for the same reason `unappliedValue` rounds: a half-cent residue must not
   * put a red banner on a request that is, to the cent, exactly balanced.
   */
  /**
   * 🔴 `rowsUseTicks` LIVED HERE AND IS GONE (AndyP, 2026-09-25). It was the single seam deciding
   * whether a row carried a tick or the figure beside it was the decision, written as a seam
   * precisely because reallocations were expected to follow the distribution. They have, and so
   * has Initial Account -- and those are the only other two types that reach this step, so the
   * flag had no value left to return but `false`.
   *
   * ⚠️ WHAT IT MEANT IS NOW UNCONDITIONAL, so it is worth stating once: every share box and every
   * cash box is LIVE from the start, and `selected` FOLLOWS the figure rather than gating it. A
   * row cannot be half-decided -- in the sale with nothing in the box -- which is what made the
   * tick worth removing, and what `needsShares` used to have to catch.
   *
   * ⚠️ IT WAS NEVER A SYNONYM FOR `!isDistribution()`, and the rules that still test the type
   * directly are the proof: the 1% exemption, the missing Available column and the bar's red run
   * are trade rules about a DISTRIBUTION, not statements about a row's shape. They stayed behind
   * when this went, which is the outcome keeping them separate was for.
   */

  /**
   * Whether the cash still releasable could cover what is outstanding.
   *
   * 🔴 THIS DRIVES A TOOLTIP, AND ONLY A TOOLTIP. It was `distributionNeedsSales` and drove a
   * fourth strip caption -- "Short by", in orange -- meant to tell counting down apart from being
   * stuck. That distinction has since been built away and the caption reverted (AndyP,
   * 2026-09-24): the 1% exemption freed the cash, over-raising removed the dead end, and SELL 1
   * SHARE means some button always closes the gap. The row buttons say what to do, which is the
   * job the caption was invented for.
   *
   * ⚠️ "Short by" was also the wrong word. An outstanding balance mid-flow is the NORMAL state of
   * a request being filled in -- the same reason the strip refuses amber for the countdown -- and
   * naming it a shortfall put a fault on screen for most of the advisor's time in the wizard.
   *
   * 🟢 What survives is worth keeping: when there is no cash left to take, the tooltip should stop
   * offering "take custodial cash" as one of three remedies.
   */
  readonly distributionCashCannotCover = computed(() => {
    if (!this.isDistribution()) return false;
    const outstanding = this.unappliedValue();
    if (outstanding <= EPS) return false;
    return outstanding > round2(this.cashAvailableToRelease() - this.cashReleased()) + EPS;
  });

  readonly isOverCommitted = computed(
    // 🔴 NEVER ON A DISTRIBUTION, where raising more than the target is legitimate: the surplus
    // stays in the account as cash, which is what happens at settlement anyway. Selling one more
    // share is often the ONLY way to cover a remainder that cash cannot, so the state this
    // computed names has to stop being a fault before that remedy is usable.
    //
    // ⚠️ Over-committing a CONTRIBUTION is still a fault and still flagged -- you cannot place
    // more than the client sent. The two are not symmetric and must not be collapsed.
    () => !this.isDistribution() && this.unappliedValue() < -EPS,
  );

  /**
   * Everything this request has made available, BEFORE any of it is spent.
   *
   * ⚠️ Gross, not net: no set-aside, no buys, no allocation subtracted. That is the whole point.
   * `investable` reads $0.00 in two completely different situations -- nothing has been freed
   * yet, or all of it has been committed -- and telling an advisor to go free up funds when they
   * have in fact allocated every dollar correctly is worse than saying nothing.
   */
  readonly grossAvailable = computed(
    () => this.modelSleeveValue() + this.cashReleased() + this.allocatableSoldValue(),
  );

  /**
   * Nothing has been freed, so there is nothing for the models step to divide.
   *
   * ⚠️ INFORMATION, not an error. An advisor who does not want to allocate to models is making a
   * perfectly ordinary request, so this must not block Next and must not be red. What it fixes is
   * a silent dead end: `setModelPot` clamps to `investable`, so with nothing available the total
   * box accepted keystrokes and stored 0 -- the advisor typed a figure, watched nothing happen,
   * and was told nothing.
   *
   * 🟢 False once the account holds model sleeves, even with nothing sold: `modelSleeveValue` is
   * in the gross figure, so an advisor re-weighting what they already have is never told to go
   * and free up funds they do not need.
   */
  readonly hasNothingToAllocate = computed(() => this.grossAvailable() < EPS);

  /**
   * Protected cash the request creates on its own: the estimated amounts of the buys the
   * trade team has to resolve.
   *
   * A hand-typed ticker cannot be executed by the portal, so its money has to sit as cash
   * until the trade team identifies the security. The advisor does not choose this and
   * cannot edit it -- it follows from step 2.
   *
   * ⚠️ Do NOT add this into the arithmetic. These amounts are already inside `totalBuyValue`
   * (see `estimatedAmount`, which returns `manualAmount` for a hand-typed row) and therefore
   * already inside `customHoldingsValue`; adding them again would count the same dollars twice.
   *
   * What changed is only where they are REPORTED. They used to be shown under Purchases and
   * restated here, so the strip and the Protected Cash step disagreed about the same money --
   * the strip said $5,000 protected while the step said $15,000, and both were right. Now
   * Purchases shows {@link resolvedBuyValue} and this is the other half, so each dollar
   * appears in exactly one column.
   *
   * 🟢 The identity that keeps that honest: `resolvedBuyValue + protectedCashFromReview ===
   * totalBuyValue`, so `customHoldingsValue` below is unchanged whichever way it is written.
   */
  readonly protectedCashFromReview = computed(() =>
    this.buysNeedingReview().reduce((sum, b) => sum + b.estimatedAmount, 0),
  );

  /**
   * Everything this request is holding back, however it got there. Drawn on the bar as **Cash On
   * Hold** and totalled at the foot of the Set-Aside Cash step.
   *
   * Three sources, and the advisor only controls the last:
   *
   *   protectedCashFromReview  a BUY whose ticker the Trade Team has yet to resolve
   *   reviewSoldValue          a SALE whose proceeds cannot be committed in this request
   *   cashInput                the advisor's own set-aside
   *
   * ⚠️ THE ONLY FIGURE FOR THIS NOW. The bar used to draw a narrower one — `cashInput +
   * reviewSoldValue`, with the pending buy left inside Custom Holdings — so the run and the step
   * total were two different numbers under one name, which is precisely the confusion the name
   * change was meant to end. AndyP's point on 2026-09-15: a pending ticker is the purest case of
   * money the request cannot invest, so it belongs with the other two rather than beside them.
   *
   * ⚠️ "Cash On Hold" is the DISPLAY name; the internal name and the payload stay `protected
   * cash`, because that is what CRM's tracker section and the `PROTECTED CASH` custom-holding
   * line are called. Renaming those spans two repos and the requests already filed.
   *
   * ⚠️ Nothing double-counts. `reviewSoldValue` never entered `investable` in the first place —
   * see {@link allocatableSoldValue} — so there is nothing to subtract for it, unlike `cashInput`.
   */
  readonly totalProtectedCash = computed(
    () => this.protectedCashFromReview() + this.reviewSoldValue() + this.cashInput(),
  );

  /**
   * The sleeve's SETTLED cash — money that is simply sitting there:
   *
   *   kept custodial cash  the account had it and the advisor left it alone
   *   protected cash       deliberately held back on step 4
   *
   * ⚠️ **Pending-review buys are NOT in this**, and that is the change rather than an oversight
   * (feedback, 2026-09-11). They used to be, itemised under this figure alongside the other two.
   * Arithmetically they belong — a buy whose ticker nobody has resolved does end up as cash in
   * the sleeve — but presenting them as cash buries the one thing on the page that is a JOB. A
   * cash rollup that swallows them is the single number here that costs someone an afternoon.
   * They now carry their own line and their own total, {@link protectedCashFromReview}.
   *
   * ⚠️ THE ARITHMETIC IS UNCHANGED. {@link totalProtectedCash} still counts them, `investable`
   * still holds them back, and the payload still files them as cash. Only the grouping on Review
   * & Submit moved. Do not "fix" this by folding them back in.
   *
   * ⚠️ NOT the same as `totalProtectedCash` for a second reason: protected cash is value this
   * request holds OUT of the models, where kept cash was never going anywhere. Labelling the
   * rollup "protected" while it included kept cash would claim an intent the advisor never
   * stated.
   *
   * 🟢 Already inside {@link customHoldingsValue}, every part of it — so this is a rollup for
   * reading, never an addend. Adding it to anything would double-count.
   */
  readonly totalSleeveCash = computed(
    () => this.cashInput()
      // ⚠️ REVIEWED SALE PROCEEDS ARE OUT as of 2026-09-15, having briefly been in. The argument
      // for including them was that the sleeve really does end up holding the money -- true, and
      // it is still true. What changed is that they now have somewhere better to be: they are
      // drawn as Cash On Hold on the bar and grouped under it on Review & Submit, so counting
      // them here as well would show the same dollars in two rollups on one page.
      //
      // Pending-review BUYS were removed for a related reason on 2026-09-11 and stay out.
      + this.keptCashRows().reduce((sum, c) => sum + c.kept, 0),
  );

  /**
   * Why Protected Cash cannot be left, or null when it can.
   *
   * Two messages, one block. The second condition subsumes the first -- over-committing the
   * pool clamps `investable` to 0, so "nothing left to allocate" is true either way -- so
   * the order matters: the specific diagnosis has to be tested first or it is never seen.
   * Same shape as {@link buyStepBlockedReason}.
   *
   * ⚠️ The review-driven portion can never be the sole cause. `buysExceedInvestable` already
   * blocks step 2 when buys exceed the pool, so by the time this runs the buys fit and only
   * the advisor's own set-aside can tip it over. Every block here is actionable.
   */
  readonly protectedCashStepBlockedReason = computed<string | null>(() => {
    // What the pool has been committed to. Buys are included because the pool has to cover
    // them too -- and because the review-driven protected cash IS a subset of them.
    const committed = this.cashInput() + this.totalBuyValue();
    // ⚠️ SUMMED FROM THE PARTS, not derived as `totalValue - nonCashCustomHoldingsValue`.
    //
    // That derivation looks equivalent and is not: it omits sale proceeds entirely. A request
    // that sold $412.80, allocated the lot to models and set the rest aside as cash balanced
    // to $0.00 unapplied in the strip while this blocked by exactly $412.80 — the step and the
    // figure on screen disagreeing about the same account. It was survivable only while the
    // pool carried enough slack to hide it; netting off the models (ACMTECH-945) made it tight
    // enough to bite.
    //
    // Built from the same terms as {@link investable} and {@link investableBeforeBuys}, so all
    // three agree by construction rather than by coincidence. Deliberately NOT clamped at zero
    // either: `investable` floors, and borrowing that clamp here would hide an over-commitment
    // whenever nothing was allocated to models.
    //
    // Net of the models because allocation is now two steps earlier — what is left to protect
    // is what the models did not take.
    const pool = this.sourcePool() - this.placedInModels();
    // Same epsilon as buysExceedInvestable: currency differences land a hair off.
    if (committed - pool > 0.005) {
      // ⚠️ "Set-aside cash", not "protected cash". The step, the strip's run, the payload line
      // and both request trackers were all renamed on 2026-09-15.
      //
      // ⚠️ SHORT on purpose. A first draft spelled out all four routes -- reduce the amount,
      // sell, release cash, pull back models or buys -- which is exhaustive and unreadable in a
      // red bar the advisor scans rather than studies.
      //
      // ⚠️ A CONTRIBUTION HAS NOTHING TO SELL, so the general wording would point at positions
      // that are not in its wizard. Same sentence, different second half.
      return this.isContribution()
        ? 'Not enough funds are available to set-aside this amount of cash. '
          + 'Correct the allocations to models or the securities being bought.'
        : 'Not enough funds are available to set-aside this amount of cash. '
          + 'Correct the allocations from positions or models.';
    }
    // The "nothing left to allocate to any models" branch was REMOVED with ACMTECH-945.
    // It guarded a step 3 -> step 4 hand-off that no longer exists: the models are chosen two
    // steps before this one, so a message predicting the advisor will not be able to allocate
    // describes something already done. What remains -- protected cash exceeding what is
    // left -- is the only condition this step can still create.
    return null;
  });

  /**
   * Set one sleeve's amount.
   *
   * An **upsert**, not a map over existing rows. `sleeveAmountRows` holds what the advisor has
   * entered, and it is emptied wholesale when Close Account is ticked — so a plain `map` would
   * silently discard the first edit made after unticking it, with the box showing the typed
   * figure and the total ignoring it.
   */
  editSleeveAmount(sleeveKey: string, amount: number): void {
    const normalized = Number.isFinite(amount) ? round2(Math.max(0, amount)) : 0;
    this.sleeveAmountRows.update((rows) => {
      const existing = rows.findIndex((r) => r.sleeveKey === sleeveKey);
      if (existing < 0) return [...rows, { sleeveKey, amount: normalized }];
      const next = [...rows];
      next[existing] = { sleeveKey, amount: normalized };
      return next;
    });
  }

  /**
   * Spread whatever is still outstanding across the sleeves by Orion's own percentages.
   *
   * 🔴 Applies the **remaining** balance, not the whole funding amount — the same contract as
   * {@link applyRemainingToModels} on the reallocation grid, and the reason matters. The other
   * steps also consume the amount: a distribution may already have taken custodial cash and sold
   * positions, and a contribution may have bought securities or set cash aside. Re-apportioning
   * the whole figure here would silently overwrite those decisions and push the request over.
   *
   * It also means the control works from any starting point — part-filled rows, a second press
   * after editing one sleeve, or a fresh grid (where the remaining balance IS the whole amount,
   * so it behaves exactly as apportioning from scratch).
   *
   * Handles both directions. A negative remainder means more has been committed than the request
   * carries, and the same weighting takes it back out.
   *
   * Guarded rather than scaled — see {@link sleeveAmountsCanApplyOrionPercent} for why a column
   * that does not total 100 is refused.
   */
  applyRemainingToSleeves(): void {
    if (!this.sleeveAmountsCanApplyOrionPercent() || !this.hasRemainingToApply()) return;

    // ⚠️ THE CAPPED FIGURE, not `unappliedValue`. On a distribution the raw one includes cash
    // and sales raised on the NEXT step, which this one cannot take back -- see
    // {@link sleeveApplyAmount}.
    const remaining = this.sleeveApplyAmount();
    const rows = this.sleeveAmounts().rows;
    // Only a DISTRIBUTION has a ceiling per sleeve: it cannot take more than the sleeve holds.
    // A contribution is new money, so there is nothing to cap.
    const capOf = (row: SleeveAmountRowView): number =>
      this.isDistribution() ? row.currentValue : Number.POSITIVE_INFINITY;

    const next = rows.map((r) => ({
      sleeveKey: r.sleeveKey,
      amount: r.amount + (remaining * r.orionPercent) / 100,
      cap: capOf(r),
    }));

    // Clamp to [0, cap] and collect what the clamps refused.
    let residue = 0;
    for (const n of next) {
      const clamped = Math.min(Math.max(0, n.amount), n.cap);
      residue += n.amount - clamped;
      n.amount = clamped;
    }

    // One redistribution pass over the rows that still have room, weighted by how much room each
    // has. Without it the button under-delivers on exactly the accounts where it is most useful:
    // an account whose distribution percentages point mostly at a small sleeve would clamp there
    // and leave the balance outstanding, with the label promising otherwise.
    if (Math.abs(residue) >= EPS) {
      const room = next.map((n) => (residue > 0 ? n.cap - n.amount : n.amount));
      const total = room.reduce((sum, r) => sum + r, 0);
      if (total > EPS && Number.isFinite(total)) {
        next.forEach((n, i) => {
          n.amount += residue * (room[i] / total);
        });
      } else if (residue > 0 && next.length > 0 && !Number.isFinite(next[0].cap)) {
        // Uncapped (contribution): everything has room, so the first row can absorb it.
        next[0].amount += residue;
      }
    }

    // Whole cents, with any rounding drift landing on the last row that can take it — so the
    // column totals the target exactly rather than leaving a cent that Review would report as an
    // outstanding balance the advisor cannot see or clear.
    const target = round2(rows.reduce((sum, r) => sum + r.amount, 0) + remaining);
    const rounded = next.map((n) => ({ ...n, amount: round2(n.amount) }));
    const drift = round2(target - rounded.reduce((sum, n) => sum + n.amount, 0));
    if (Math.abs(drift) >= EPS) {
      for (let i = rounded.length - 1; i >= 0; i--) {
        const candidate = round2(rounded[i].amount + drift);
        if (candidate >= 0 && candidate <= rounded[i].cap) {
          rounded[i].amount = candidate;
          break;
        }
      }
    }

    this.sleeveAmountRows.set(rounded.map((n) => ({ sleeveKey: n.sleeveKey, amount: n.amount })));
  }

  /** Zero every sleeve amount, for the "start again" control on the step. */
  clearSleeveAmounts(): void {
    this.sleeveAmountRows.set([]);
  }

  // ---- Step 1 / 2 select-all helpers ---------------------------------------
  /**
   * Whether to offer SELL ALL POSITIONS above the securities table.
   *
   * ⚠️ IT IS WHAT THE HEADER CHECKBOX USED TO BE. That checkbox carried a third state -- the
   * indeterminate dash, for some rows selected -- which a button cannot; `allPositionsSellingAll`
   * renders the pressed state instead, and answers a narrower question on purpose. Half the rows
   * being sold is not a half-pressed "sell everything", it is simply not that.
   */
  showSellAllPositionsButton(): boolean {
    if (!this.positions().length) return false;
    return !this.isDistribution() || REALLOCATION_V2_SELL_ALL_POSITIONS_ENABLED;
  }

  /**
   * The account's Custom Holdings as they will stand after the request: what is kept,
   * merged with what is bought.
   *
   * One derivation serving two consumers — the Do Not Trade tab (so an advisor who keeps
   * AAPL and buys more marks **one** line) and Review & Submit / the payload (so CRM
   * writes **one** Custom Holdings row). Deriving it twice would let the screen and the
   * database disagree about the same request.
   *
   * Merged on `securityId`, falling back to ticker when Orion omits it. When buys are
   * flagged off this is just the kept positions, since `buys()` is empty.
   *
   * ⚠️ `value` is part reconciled, part estimated whenever a buy contributes —
   * {@link ResultingHolding.isEstimated} says which rows.
   *
   * The templates no longer mark those rows individually. They had used a leading tilde,
   * which pilot users read as a minus sign, so the columns are captioned `Est.` as a whole
   * instead — honest enough, since every price here is point-in-time.
   */
  readonly resultingHoldings = computed<ResultingHolding[]>(() => {
    const total = this.totalValue();
    const dnt = this.dnt();
    const byKey = new Map<string, ResultingHolding>();

    for (const p of this.sleevePositions()) {
      byKey.set(holdingKey(p.securityId, p.ticker), {
        securityId: p.securityId,
        securityTypeId: p.securityTypeId,
        ticker: p.ticker,
        description: p.description,
        shares: p.keptShares,
        value: p.keptValue,
        // Placeholders; both are computed once every row's value is final, below.
        pctOfAccount: 0,
        price: null,
        boughtShares: 0,
        isEstimated: false,
        isNewPosition: false,
        isDoNotTrade: dnt.has(p.ticker),
        dntEligible: p.dntEligible,
        needsReview: false,
      });
    }

    // Ticker → the key its row is filed under, so a buy that has no id can still find a
    // held position that does. Without it a hand-typed ticker opens a second row for a
    // security the sleeve already holds, and the resulting holdings list it twice.
    const keyByTicker = new Map<string, string>();
    for (const [key, holding] of byKey) keyByTicker.set(holding.ticker.trim().toUpperCase(), key);

    for (const b of this.actionableBuys()) {
      const security = b.security as SecurityOption;
      const ownKey = holdingKey(security.securityId, security.ticker);
      const key = byKey.has(ownKey)
        ? ownKey
        : keyByTicker.get(security.ticker.trim().toUpperCase()) ?? ownKey;
      const existing = byKey.get(key);
      // A hand-typed buy contributes an AMOUNT and no share count: nothing has resolved
      // the ticker, so there is no price to turn dollars into shares. Its value lands on
      // the row while `shares` stays as whatever is actually held, and the row is flagged
      // for the trade team to finish.
      //
      // ⚠️ On a merged row that inflates the derived price below, since value grew and
      // shares did not. Internally consistent - shares x price still equals value - but
      // the price is not a real one, which is exactly what the review flag is saying.
      if (b.needsTradeTeamReview) {
        if (existing) {
          existing.value += b.estimatedAmount;
          existing.isEstimated = true;
          existing.needsReview = true;
          // Nothing to contribute: a hand-typed buy has no type, and `??=` would leave the
          // kept side's alone anyway. Stated so the omission reads as decided, not missed.
        } else {
          byKey.set(key, {
            securityId: security.securityId,
            // Null on this branch by construction -- it is the hand-typed one, and a ticker
            // the master does not carry has no type to carry either.
            securityTypeId: security.securityTypeId,
            ticker: security.ticker,
            description: security.description,
            // Null, not 0: nobody knows the share count yet, and a zero would read as a
            // position being opened with no shares in it.
            shares: null,
            value: b.estimatedAmount,
            pctOfAccount: 0,
            price: null,
            boughtShares: 0,
            isEstimated: true,
            isNewPosition: true,
            isDoNotTrade: dnt.has(security.ticker),
            dntEligible: true,
            needsReview: true,
          });
          keyByTicker.set(security.ticker.trim().toUpperCase(), key);
        }
        continue;
      }
      if (existing) {
        existing.shares = (existing.shares ?? 0) + b.shares;
        existing.value += b.estimatedAmount;
        existing.boughtShares += b.shares;
        existing.isEstimated = true;
        // ⚠️ Fills a GAP, never overwrites. Both sides are the same security so their types
        // agree when both have one -- but either side can be missing one, and which row was
        // written first is an implementation detail that must not decide what CRM stores.
        existing.securityTypeId ??= security.securityTypeId;
      } else {
        byKey.set(key, {
          securityId: security.securityId,
          securityTypeId: security.securityTypeId,
          ticker: security.ticker,
          description: security.description,
          shares: b.shares,
          value: b.estimatedAmount,
          pctOfAccount: 0,
          price: null,
          boughtShares: b.shares,
          isEstimated: true,
          isNewPosition: true,
          isDoNotTrade: dnt.has(security.ticker),
          dntEligible: true,
          needsReview: false,
        });
        // Registered so a second buy of the same ticker merges into this row rather than
        // opening a third.
        keyByTicker.set(security.ticker.trim().toUpperCase(), key);
      }
    }

    // Percentages and price last, once every row's value is final. Same denominator step 1
    // uses: buys move value *within* the account, so the total does not change.
    //
    // Price is DERIVED from the row's own value and shares rather than carried through,
    // for the same reason step 1 derives it: a price from one basis printed beside a value
    // from another produces a row whose arithmetic does not work. On a merged row it is a
    // blend of the held and bought prices, which is what makes shares x price = value hold.
    return [...byKey.values()].map((h) => ({
      ...h,
      pctOfAccount: total > 0 ? (h.value / total) * 100 : 0,
      price: h.shares ? h.value / h.shares : null,
    }));
  });

  /**
   * The resulting sleeve split by whether the trade team still has to resolve the row.
   *
   * A hand-typed buy lands in `resultingHoldings` with `needsReview` set, no share count and
   * its value. Economically that money is cash held back, not a holding, so Review & Submit
   * and the trackers group those rows under CASH instead of listing them as securities the
   * account will own.
   *
   * ⚠️ Split, never duplicated. A row that appeared both under CASH and standalone would be
   * read as two positions and double the value on screen.
   */
  readonly pendingReviewHoldings = computed(() => this.resultingHoldings().filter((h) => h.needsReview));

  /** Everything the account will actually hold as a security. */
  readonly resolvedHoldings = computed(() => this.resultingHoldings().filter((h) => !h.needsReview));

  /** Merged holdings that may be marked Do Not Trade (excludes cash-like rows). */
  readonly dntCandidates = computed(() => this.resultingHoldings().filter((h) => h.dntEligible));

  // ---- Do Not Trade attestation --------------------------------------------
  /** The positions actually marked Do Not Trade, as the advisor is confirming them. */
  readonly markedDoNotTrade = computed(() => this.resultingHoldings().filter((h) => h.isDoNotTrade));

  /**
   * The marked set, reduced to a comparable string.
   *
   * Sorted, so re-marking the same positions in a different order is the same list and
   * does not throw away a confirmation the advisor has already given.
   */
  private readonly dntFingerprint = computed(() =>
    this.markedDoNotTrade().map((h) => h.ticker).sort().join('|'),
  );

  /**
   * The marked set at the moment the advisor confirmed it, or null if they never have.
   *
   * Stored as the **fingerprint rather than a boolean** so the confirmation expires by
   * construction: any change to what is marked changes the fingerprint, and the
   * attestation stops matching. A boolean would have to be reset by hand from every
   * mutation that can touch the list — `toggleDnt`, `toggleDntAll`, and also the ones
   * that are not obviously about DNT at all, since selling a position or buying a new one
   * changes the merged holdings this list is built from. The one that got forgotten would
   * leave an advisor's tick standing against a list they never saw.
   */
  private readonly dntConfirmedFor = signal<string | null>(null);

  /** Whether the advisor has attested to exactly the set currently marked. */
  readonly dntConfirmed = computed(() => this.dntConfirmedFor() === this.dntFingerprint());

  /**
   * Whether the step is holding the advisor for an attestation.
   *
   * Nothing marked, nothing to confirm — the step is then a screen they can pass straight
   * through, which is the common case.
   */
  readonly dntConfirmationRequired = computed(
    () => this.markedDoNotTrade().length > 0 && !this.dntConfirmed(),
  );

  /** Record (or withdraw) the advisor's attestation for the set as it stands now. */
  setDntConfirmed(confirmed: boolean): void {
    this.dntConfirmedFor.set(confirmed ? this.dntFingerprint() : null);
  }

  readonly dntAllState = computed(() => {
    // Reads the merged list, not the kept positions, so select-all covers bought
    // securities too rather than reporting indeterminate forever.
    const eligible = this.dntCandidates();
    const marked = eligible.filter((p) => p.isDoNotTrade).length;
    return {
      checked: eligible.length > 0 && marked === eligible.length,
      indeterminate: marked > 0 && marked < eligible.length,
    };
  });

  // ---- Step 4: allocation grid (derived view + validity) -------------------
  /**
   * How much of the account is going to models. **The pot every Target % is a share of.**
   *
   * ⚠️ It is STATED, not inferred, and that is the whole of ACMTECH-969. There used to be no
   * such figure: the total was whatever the rows happened to add up to, so `editTarget` held it
   * and moved every row's dollars while `editValue` moved it and re-derived every row's target.
   * One keystroke changed two rows against a total nobody had named, and no advisor could say
   * what the percentages were percentages OF.
   *
   * Seeded at what the models hold TODAY -- see {@link seedOpeningRows} -- so the step opens on
   * the account as it stands and the advisor moves money out of models on Raise Cash, with
   * {@link setModelFreeAmount}, or back into them with {@link useAllInvestable}.
   *
   * ⚠️ Written ONLY through {@link setModelPot}, {@link seedOpeningRows}, `commitToSma` and
   * {@link applyModelFreeAmount}. Exposed read-only so the grid can never acquire a second,
   * implicit pot by some other route -- which is the exact failure this replaces.
   *
   * 🔴 THE ADVISOR NO LONGER TYPES THIS (ACMTECH-1039). It was a figure they stated and the grid
   * split; the two boxes that set it are read-only displays now, because stating a budget and
   * then reconciling it against what was actually freed up is what they found confusing. It is
   * an AGGREGATE now -- holdings less what Raise Cash is freeing -- and the only controls
   * that move it are Amount to Use, Allocate Remaining and the two Resets.
   */
  private readonly modelPotSignal = signal(0);
  readonly modelPot = this.modelPotSignal.asReadonly();

  readonly allocation = computed(() => {
    const rows = this.allocationRows();
    const inv = this.investable();
    const pot = this.modelPot();

    const targets = rows.map((r) => r.targetExact ?? r.target);
    const sumTarget = targets.reduce((s, t) => s + t, 0);

    // ⚠️ EVERY row's dollars are DERIVED: `value = pot × target%`. Nothing is stored per row,
    // and there is no per-row override -- typing in the Value box sets that row's TARGET (see
    // {@link editValue}), so the identity holds however the advisor got here.
    //
    // A `valueCap` field used to hold each row's dollars, pinned so that "nothing happening on
    // another step should quietly rewrite it". It is gone, and with it the whole family of bugs
    // the comments here recorded: a null cap falling through to `investable × target%` and
    // swallowing the account, a cap that shrank to fit a falling balance and made an
    // over-commitment look settled, and two editors disagreeing about which of the value and
    // the percentage was the source of truth. One source now, one direction.
    const values = targets.map((t) => round2((pot * t) / 100));
    let sumValue = round2(values.reduce((total, v) => total + v, 0));

    // ⚠️ Only when the targets total 100, and then only a cent or two. Each share is rounded to
    // the cent, so Σ can miss the pot by a hundredth -- and `unappliedValue` compares against a
    // half-cent tolerance, so that dust would surface as money still to place with nothing on
    // screen to explain it. The last row absorbs it, which is the one place a row's dollars are
    // not exactly its share.
    if (rows.length > 0 && Math.abs(sumTarget - 100) < EPS) {
      const drift = round2(pot - sumValue);
      if (Math.abs(drift) > EPS) {
        values[values.length - 1] = round2(values[values.length - 1] + drift);
        sumValue = round2(sumValue + drift);
      }
    }

    const viewRows: AllocationRowView[] = rows.map((r, i) => {
      const effectiveTarget = targets[i];
      const allocatedValue = values[i];
      const minimum = this.minimumFor(r.modelId);
      return {
        ...r,
        effectiveTarget,
        // manual (typed) or seeded (from the sleeve) -> the stored figure; otherwise the
        // target itself, with nothing stored that could go stale against it.
        effectiveDistribution: r.distributionManual || r.distributionSeeded ? r.distribution : effectiveTarget,
        effectiveContribution: r.contributionManual || r.contributionSeeded ? r.contribution : effectiveTarget,
        allocatedValue,
        minimum,
        // `>=` — exactly $15,000 clears a $15,000 minimum. A model with no minimum on
        // record reads as 0 here, so nothing is ever below it (see `minimumFor`).
        belowMinimum: allocatedValue + EPS < minimum,
        errors: [],
      };
    });

    // Messages are collected per row AFTER the view rows exist, so a row can carry more
    // than one and the template renders a list rather than a single hard-coded line. The
    // text lives here rather than in the template because the conditions do; a template
    // deciding which message applies would be a second place to keep the rules in step.
    for (const row of viewRows) {
      if (row.belowMinimum)
        row.errors.push(`Below the ${formatMoney(row.minimum)} minimum for this model`);
      if (row.modelId === '')
        row.errors.push(
          row.sleeveModelName
            ? `This sleeve is currently on ${row.sleeveModelName}, which isn't in your model list — pick the matching model`
            : 'Select a model for this row',
        );
    }

    const sumDistribution = viewRows.reduce((s, r) => s + r.effectiveDistribution, 0);
    const sumContribution = viewRows.reduce((s, r) => s + r.effectiveContribution, 0);
    const totals: AllocationTotals = {
      target: sumTarget,
      distribution: sumDistribution,
      contribution: sumContribution,
      value: sumValue,
      // Signed: positive is over, negative is short. The percentages answer to 100 and
      // the value answers to the investable amount, which is the only figure the dollars
      // can legitimately add up to.
      targetDelta: sumTarget - 100,
      distributionDelta: sumDistribution - 100,
      contributionDelta: sumContribution - 100,
      valueDelta: sumValue - inv,
    };

    // ⚠️ THIS STEP's rules only -- the columns, the models, the minimums.
    //
    // "Every investable dollar has to land somewhere" is deliberately NOT here. It is a
    // whole-request invariant, not a property of the allocation grid, and binding it to this
    // step made the step the only place it could be resolved. See {@link submitBlockers}.
    // No rows at all is VALID: it means "this account holds no models", which is a legitimate
    // request (everything to custom holdings or protected cash) now that the last row can be
    // removed. Submit still refuses to let the money go nowhere -- see submitBlockers.
    //
    // The length check has to come first rather than joining the conjunction: an empty grid
    // has totals of 0, not 100, so every percentage check below would fail it.
    const valid =
      viewRows.length === 0 ||
      (Math.abs(totals.target - 100) < EPS &&
        Math.abs(totals.distribution - 100) < EPS &&
        Math.abs(totals.contribution - 100) < EPS &&
        viewRows.every(
          (r) =>
            r.modelId !== '' &&
          r.effectiveTarget > 0 &&
          r.effectiveDistribution > 0 &&
          r.effectiveContribution > 0 &&
          r.allocatedValue > 0 &&
          // Orion would reject an under-funded model, so Next blocks here rather than
          // letting the advisor build a request the trade team cannot action. A model with
          // no minimum on record cannot be under-funded -- see `minimumFor`.
          !r.belowMinimum,
        ));

    return { rows: viewRows, totals, valid };
  });

  /**
   * Why Allocate to Models cannot be left, or null when it can.
   *
   * Step-local only. Per-row problems (no model chosen, below minimum) stay on their rows,
   * where the grid already lists them; this is the summary the disabled Next needs.
   *
   * ⚠️ Unplaced money is NOT here. It no longer blocks this step -- see {@link submitBlockers}.
   */
  // ---- Managed Models: freeing money out of a sleeve (step 1) ----------------
  /**
   * The Managed Models table on Raise Cash.
   *
   * ⚠️ Built from {@link allocationRows}, NOT from `data().sleeves`, even though the three
   * read-only figures originate there. {@link cap} depends on the model's investment minimum
   * and the MODEL is editable on step 2, so a view keyed off the loaded sleeve would go stale
   * the moment the advisor swapped one and would cap against a minimum belonging to a model
   * the row no longer carries.
   *
   * Rows the advisor ADDED are absent: no sleeve behind them, so nothing to free.
   *
   * Empty for a contribution/distribution (they raise from sleeves on a step of their own)
   * and once an SMA is committed (the SMA takes the account; step 1 is locked out anyway).
   */
  readonly managedModelRows = computed<readonly ManagedModelRowView[]>(() => {
    if (this.isMoveMoney() || this.smaCommitted()) return [];

    const out: ManagedModelRowView[] = [];

    this.allocationRows().forEach((r, i) => {
      const totalValue = r.sleeveCurrentValue;
      if (totalValue == null) return;

      out.push({
        sleeveKey: r.orionConnectAccountId != null ? String(r.orionConnectAccountId) : `idx:${i}`,
        modelName: this.modelName(r.modelId) || r.sleeveModelName || '',
        currentTarget: r.sleeveCurrentTarget,
        actualPct: r.sleeveActualPct,
        totalValue,
      });
    });

    return out;
  });

  /**
   * The most that can be freed before a model falls under its investment minimum.
   *
   * 🔴 IT BINDS ON THE FIRST MODEL TO REACH ITS MINIMUM, not on the total (AndyP,
   * 2026-10-29). The money comes out BY TARGET, so after freeing `F` model i holds
   * `(Σheld − F) × target_i`, which rearranges to `F ≤ Σheld − max(minimum_i ÷ target_i)`.
   *
   * A $15,000 minimum on a 20% sleeve binds FOUR TIMES EARLIER than the same minimum on an
   * 80% one. `Σheld − Σminimums` -- the obvious rule -- misses that entirely: the total
   * still looks fine while a small-target sleeve sits under water, and the advisor finds
   * out on the next step with a red row.
   *
   * ⚠️ Where no minimum is on record {@link minimumFor} reports 0, which step 2 reads as
   * "cannot be under-funded". {@link MODEL_DRAW_FLOOR_RATE} of what that sleeve holds stands
   * in, so the raise can never empty a model to nothing -- a row at $0 fails
   * `effectiveTarget > 0` and blocks the step the advisor was trying to get past.
   *
   * ⚠️ Rows at a ZERO target are skipped. They hold nothing after the raise whatever F is,
   * so dividing by that target would cap everything at zero. A 0% sleeve still holding money
   * is a pre-existing condition for step 2 to report, not something this should block on.
   */
  readonly modelFreeCap = computed(() => {
    const rows = this.allocationRows().filter((r) => r.sleeveCurrentValue != null);
    if (rows.length === 0) return 0;

    let potFloor = 0;
    for (const r of rows) {
      const target = r.targetExact ?? r.target;
      if (target <= EPS) continue;
      const minimum = this.minimumFor(r.modelId);
      const keep = minimum > 0
        ? minimum
        : round2((r.sleeveCurrentValue ?? 0) * MODEL_DRAW_FLOOR_RATE);
      potFloor = Math.max(potFloor, (keep / target) * 100);
    }

    return Math.max(0, round2(this.modelSleeveValue() - potFloor));
  });

  /**
   * What Raise Cash is actually freeing out of the models.
   *
   * ⚠️ THE CAPPED FIGURE, not what was typed. An over-cap entry stands in its box and blocks
   * the step, but it must never reach this: the pot is set from the same capped figure, so a
   * raw reading here would claim money the models never gave up and
   * {@link allocateRemainingCheck} would offer to hand back more than exists.
   *
   * Name kept from when this summed per-sleeve draws -- it is read by the review screens and
   * by the payload, and it still answers the same question.
   */
  readonly totalDrawnFromModels = computed(() =>
    round2(Math.min(this.modelFreeAmount(), this.modelFreeCap())),
  );

  /**
   * What each sleeve gave up on Raise Cash, keyed by Orion Connect account id.
   *
   * One source for two readers that must not disagree: the payload (`cashRaised` on the
   * current allocations) and Review & Submit's Cash Raised figure. They answer the same question,
   * and computing it twice is how a request comes to state one figure and display another.
   *
   * 🔴 MEASURED, NOT APPORTIONED (AndyP, 2026-10-29). It used to be `F × target_i` -- the
   * pro-rata box divided by the target column -- which could not see the OTHER way of raising
   * cash at all: a value edit released money and this map stayed empty, so the trade team was
   * told nothing had left the models. Now each sleeve is compared against what it held.
   *
   * ⚠️ THE OBVIOUS FORMULA, `held − now`, OVERSTATES A RAISE WHENEVER MONEY MOVES BETWEEN
   * MODELS. Drop A by $10,000 and lift B by $4,000 and only $6,000 left the models, but
   * `held − now` reports $10,000 against A -- so Left to Allocate would read $6,000 and the
   * tracker "$10,000 raised from models". Two figures, one request, and the disagreement 1040
   * exists to prevent. So the RELEASED TOTAL is apportioned across the sleeves that gave money
   * up, and the $4,000 that merely moved surfaces as re-weighting, which is what it is.
   *
   * 🟢 In pro-rata mode this reduces to exactly the old formula: a box of $10,000 on a 20/80
   * account drops the sleeves by $2,000 and $8,000, which apportions back to $2,000 and
   * $8,000. One expression covers both mechanisms, so neither has to be stored or branched on.
   *
   * ⚠️ OVER THE ACCOUNT'S SLEEVES, NOT THE GRID ROWS. A REMOVED model has no row, and a
   * row-based sum misses it -- which the old one did. A sleeve whose money went to cash now
   * reports its whole value raised; one whose money another model absorbed reports nothing and
   * a negative re-weight. The second was previously unrepresentable.
   *
   * ⚠️ The released total is measured against the WHOLE grid, new rows included, so a model
   * added here and funded from a sleeve releases nothing. Summing only the sleeves would call
   * that a raise.
   *
   * ⚠️ Keyed on the CONNECT ACCOUNT ID, not the sleeve key, because that is the only key the
   * receiving end can join on -- CRM matches requested rows to current ones by
   * `OrionConnectAccountID` and nothing else. A sleeve without one is therefore absent here:
   * a figure nobody can match back to a model is worse than no figure. Raise Cash itself
   * is unaffected either way; it reads the draws directly.
   *
   * Rows at zero are left out, so `get` returning undefined means "nothing freed".
   */
  readonly cashRaisedByConnectAccountId = computed<ReadonlyMap<number, number>>(() => {
    const out = new Map<number, number>();
    const sleeves = this.data()?.sleeves ?? [];
    if (sleeves.length === 0) return out;

    // What the models will hold, keyed the only way the far end can join on. Summed rather
    // than assigned: two rows can carry the same sleeve while the advisor is mid-edit.
    const holding = new Map<number, number>();
    for (const r of this.allocation().rows) {
      if (r.orionConnectAccountId != null) {
        holding.set(
          r.orionConnectAccountId,
          round2((holding.get(r.orionConnectAccountId) ?? 0) + r.allocatedValue),
        );
      }
    }

    // Against the WHOLE grid, so a newly added model funded out of a sleeve nets to nothing.
    const released = round2(this.modelSleeveValue() - this.allocation().totals.value);
    if (released <= EPS) return out;

    // 🔴 A PRO-RATA RAISE REPORTS BY TARGET, WHATEVER IS RE-WEIGHTED AFTERWARDS (AndyP,
    // 2026-10-29). Measuring each sleeve's drop is the honest answer to "what did this model give
    // up", and the WRONG answer to "where did the raise come from" the moment the advisor also
    // moves the split -- because the two acts net together in the figure.
    //
    // Raise $50,000 across 20/80 and then re-weight to 30/70: the small sleeve ends up holding
    // MORE than it started with, so its measured drop is negative and the whole $50,000 lands on
    // the other model. Arithmetically exact, and it reads as though one model funded a raise the
    // advisor had explicitly spread across both.
    //
    // 🟢 THE LOCK IS WHAT MAKES THE BRANCH SAFE. The two mechanisms can never both be live
    // (`modelValuesEdited`), so each reports the way that is true for it: the box knows its own
    // split, and a value edit has no split to know. Re-weighting then surfaces as re-weighting,
    // where it belongs, and nets to zero across the models.
    const drawn = this.totalDrawnFromModels();
    if (drawn > EPS) {
      // ⚠️ NORMALISED ACROSS THE SLEEVE ROWS ONLY. A model added on this step holds no sleeve and
      // gave nothing up, but it carries a target -- so dividing by 100 would quietly drop its
      // share of the raise, which came out of the existing sleeves like all the rest.
      const seated = this.allocation().rows
        .filter((r) => r.sleeveCurrentValue != null && r.orionConnectAccountId != null);
      const targetSum = seated.reduce((sum, r) => sum + r.effectiveTarget, 0);
      if (seated.length === 0 || targetSum <= EPS) return out;

      const byTarget = seated.map((r) => round2((drawn * r.effectiveTarget) / targetSum));
      const slip = round2(drawn - byTarget.reduce((sum, v) => sum + v, 0));
      byTarget[byTarget.length - 1] = round2(byTarget[byTarget.length - 1] + slip);

      seated.forEach((r, i) => {
        if (r.orionConnectAccountId != null && byTarget[i] > EPS) {
          out.set(r.orionConnectAccountId, byTarget[i]);
        }
      });
      return out;
    }

    // Every sleeve that gave money up, with or without a Connect id: one without still gave
    // it up, and leaving it out of the division would hand its share to the others.
    const drops: Array<{ accountId: number | null; amount: number }> = [];
    let dropped = 0;
    for (const s of sleeves) {
      const now = s.accountId != null ? holding.get(s.accountId) ?? 0 : 0;
      const drop = round2((s.currentValue ?? 0) - now);
      if (drop > EPS) {
        drops.push({ accountId: s.accountId ?? null, amount: drop });
        dropped = round2(dropped + drop);
      }
    }
    if (dropped <= EPS) return out;

    const shares = drops.map((d) => round2((released * d.amount) / dropped));
    // The last absorbs the rounding, as the allocation drift does, so the parts sum to exactly
    // what was released -- the portal aggregate and CRM's have to agree to the cent.
    const drift = round2(released - shares.reduce((sum, v) => sum + v, 0));
    shares[shares.length - 1] = round2(shares[shares.length - 1] + drift);

    drops.forEach((d, i) => {
      if (d.accountId != null && shares[i] > EPS) out.set(d.accountId, shares[i]);
    });

    return out;
  });

  // 🔴 `allocationUntouched` WAS HERE AND IS GONE (2026-10-29). It answered "has the advisor
  // stated anything on step 2 themselves", for the confirmation that warned before a draw
  // re-derived the target column. Freeing cash no longer touches that column, so there is
  // nothing to overwrite and nothing to ask about -- see `applyModelFreeAmount`.

  /**
   * Why Amount to Raise cannot stand, or null when it can.
   *
   * Its own computed rather than a branch inside {@link allocationStepBlockedReason}, because
   * the Raise Cash panel prints it BESIDE THE BOX: the step-level reason also carries "all %
   * columns must total 100%" and the rest, and a message about the grid displayed inside the
   * raise panel points the advisor at the wrong half of the screen (ACMTECH-1039).
   *
   * ⚠️ ONE message about ONE figure. The amount is not per model, and the cap binds on
   * whichever model reaches its minimum first -- naming it would point at a sleeve the advisor
   * never typed anything against.
   */
  readonly modelRaiseBlockedReason = computed<string | null>(() => {
    const cap = this.modelFreeCap();
    if (this.modelFreeAmount() - cap > EPS) {
      return `Only ${formatMoney(cap)} can be raised from the models -- the rest is needed to `
        + 'keep each one above its investment minimum. Lower the amount, or remove a model '
        + 'below.';
    }
    return null;
  });

  readonly allocationStepBlockedReason = computed<string | null>(() => {
    const a = this.allocation();

    // ⚠️ BEFORE `a.valid`, and that is load-bearing. An over-cap figure stands in its box but
    // only the cap is ever applied, so the pot is sound and the allocation is VALID -- the
    // early return below would hand back null and let the advisor leave with a figure on
    // screen that the request will not honour.
    const raise = this.modelRaiseBlockedReason();
    if (raise) return raise;

    if (a.valid) return null;

    // 🔴 A BRANCH WAS HERE AND IS GONE (2026-10-29). It caught an advisor who had freed every
    // model's balance, because the pot-is-empty message below would have told them to "set how
    // much should go to models" -- an instruction to undo what they had just deliberately done.
    //
    // It is unreachable now. The amount is capped at `modelFreeCap`, which holds the pot at or
    // above the largest `minimum ÷ target` across the sleeves -- a positive figure whenever any
    // sleeve has a target -- so the models cannot be emptied from that step at all. A guard
    // that can never fire reads as protection and is not.

    // ⚠️ THE POT FIRST, because everything else is downstream of it. With nothing in it every
    // row derives to $0, so every row fails `allocatedValue > 0` and the generic message would
    // send the advisor to fix eight boxes when the one thing missing is the total.
    if (a.rows.length > 0 && this.modelPot() <= EPS) {
      return 'Set how much of this account should go to models.';
    }

    // ⚠️ Not always the percentages. Since the columns were decoupled, a row can carry a
    // perfectly good target and no money — so this used to name the one failure it knew about
    // and be wrong about it. The rows carry their own messages; this points at them.
    if (a.totals.value < EPS && a.rows.every((r) => !r.touched)) {
      return 'Enter each model\'s Target %, or remove the models you are not using.';
    }

    const off = (sum: number): boolean => Math.abs(sum - 100) >= EPS;
    if (off(a.totals.target) || off(a.totals.distribution) || off(a.totals.contribution)) {
      return 'All % columns must total 100%';
    }
    return 'Give every model row a model and an amount, and clear any warnings shown above.';
  });

  /**
   * Everything stopping this request from being submitted, in the advisor's words.
   *
   * A list, and a REQUEST-level one, because that is what these conditions actually are. The
   * placement rule used to live on `allocation().valid`, which had two costs: the advisor
   * could not leave the allocation step to go and fix the problem somewhere else, and the
   * only place the problem was named was beside a disabled Next on that one step.
   *
   * Now it surfaces on Review & Submit, where the advisor is deciding whether the request is
   * finished, and Submit is what it holds shut. Getting there is free.
   *
   * Both directions name EVERY exit, not just the one on the allocation step: the advisor has
   * not necessarily decided the models are where this money belongs — that is the point of the
   * Investable figure — and an imbalance is equally well fixed by selling more or protecting
   * less.
   */
  readonly submitBlockers = computed<string[]>(() => {
    // ⚠️ An SMA the account already holds is the ONLY thing this panel says, and it replaces
    // every other reason rather than joining them.
    //
    // Not just tidier -- the other blockers would be actively wrong. Their remedies are
    // "allocate the funds to a model, buy securities or allocate to protected cash", and the
    // lock forbids all three. Telling the advisor to do something they cannot do is worse than
    // saying nothing, and the one thing that IS available -- removing the model -- is what this
    // message asks for. Same words as the dimmed Add Model button and the dimmed steps, so the
    // wall reads the same wherever they meet it.
    if (this.smaLockActive()) return [this.smaLockMessage()];

    const blockers: string[] = [];

    // 🔴 BEFORE THE CLOSE-ACCOUNT RETURN BELOW, and that placement is the whole point
    // (ACMTECH-1036). A close is still a distribution, and a closing account paid out to a third
    // party is exactly the case where getting the recipient wrong costs the most -- the money
    // leaves for good and there is no second request to correct it on. Sitting after that early
    // return, these two checks would never run on the requests that need them most.
    //
    // They are also NOT in `fundingStepBlockedReason`. That gate holds Next on step 1, and the
    // document it asks for is attached on a step the advisor has not reached yet -- a gate
    // naming a box on a later screen is a gate with no way forward. Submit is the right lock:
    // by then every step exists and the advisor is deciding whether the request is finished.
    if (this.newRecipientDocumentMissing()) {
      // ⚠️ THE SAME SENTENCE REVIEW'S OWN NOTICE USED TO CARRY, and now the only one on that
      // page. The two were deliberately worded differently -- a statement beside an imperative
      // -- but they still said one thing twice on one screen, which reads as two problems and
      // sends the advisor looking for a second thing to fix. The blocker panel is the right of
      // the two homes: it is where every other reason Submit is shut already appears.
      blockers.push(
        instructionsToDocumentsStep(this.selectedDeliveryMethod()?.name ?? 'Delivery'),
      );
    }

    const missingPayee = this.payeeMissingFields();
    if (missingPayee.length > 0) {
      // Counted rather than named. The fields are marked individually on the funding step, which
      // is where they are fixed; repeating six labels here would be a second, worse copy of that
      // marking -- and the advisor has to go to the step either way.
      blockers.push(
        `The payee needs ${missingPayee.length} more `
        + `${missingPayee.length === 1 ? 'detail' : 'details'} before this check can be sent. `
        + 'Complete the payee address on the first step.',
      );
    }

    // 🔴 Closing the account balances by construction: the amount IS the account and every
    // position is liquidated, so there is nothing to apportion and nothing that could fail to
    // add up. Running the checks below would compare the account's value against a set of
    // instructions the wizard deliberately never collected, and report the whole balance as
    // unaccounted for on a request that is complete.
    if (this.isCloseAccount()) return blockers;

    if (this.isMoveMoney()) {
      // The funding gate is repeated here rather than assumed. Free step navigation means
      // Review is reachable, and a request with no amount must not present a clean Submit.
      const fundingProblem = this.fundingStepBlockedReason();
      if (fundingProblem !== null) blockers.push(fundingProblem);

      const outstanding = this.unappliedValue();
      if (outstanding > EPS) {
        blockers.push(
          this.isContribution()
            ? `${formatMoney(outstanding)} of this contribution has not been placed yet. `
              + 'Allocate it to a model, buy securities, or set it aside as cash.'
            : `${formatMoney(outstanding)} of this distribution has not been sourced yet. `
              + 'Take more from the models, take custodial cash, or sell positions.',
        );
      } else if (outstanding < -EPS && this.isContribution()) {
        // 🔴 CONTRIBUTIONS ONLY. A contribution cannot place more than the client sent -- the
        // money does not exist -- so over is a genuine fault. A DISTRIBUTION may raise more than
        // it pays out, and the surplus simply stays in the account as cash; the review page says
        // so rather than this refusing the request.
        blockers.push(
          `${formatMoney(-outstanding)} more has been placed than this contribution carries. `
          + 'Reduce the model amounts, the securities being bought, or the cash set aside.',
        );
      }
      return blockers;
    }

    // The two remedies lists are mirror images on purpose -- same three places money can go,
    // read forwards or backwards -- so an advisor who has seen one recognises the other.
    // Buying is omitted outright when the request cannot buy, rather than offered and refused.
    const spendIt = this.hasBuyStep()
      ? 'Allocate the funds to a model, buy securities or allocate to protected cash.'
      : 'Allocate the funds to a model or allocate to protected cash.';
    const freeItUp = this.hasBuyStep()
      ? 'Reduce the model allocation, sell more positions, or reduce purchases or protected cash.'
      : 'Reduce the model allocation, sell more positions, or reduce protected cash.';

    const unapplied = this.unappliedValue();
    if (unapplied > EPS) {
      blockers.push(`${formatMoney(unapplied)} of this account is not allocated yet. ${spendIt}`);
    } else if (unapplied < -EPS) {
      blockers.push(
        `${formatMoney(-unapplied)} more has been allocated than this account has available. `
        + freeItUp,
      );
    }

    // ⚠️ A FALLBACK, only when nothing more specific is outstanding -- hence the length check.
    //
    // It is the vaguest thing this panel can say, and an advisor who has $1,417.73 unplaced does
    // not need to be told they have made no changes as well: the second line adds nothing to the
    // first and dilutes it. So a real outstanding item suppresses this one, and it surfaces only
    // when the request is genuinely just empty.
    //
    // It also lives here rather than beside the Submit button, which is where it used to sit.
    // Every reason Submit is shut now reads from one place, instead of the specific ones showing
    // in this panel and this one showing in the footer -- which made a disabled Submit look like
    // two different kinds of problem depending on which had tripped.
    if (blockers.length === 0 && !this.hasChanges()) {
      blockers.push(
        'No changes to submit yet. Mark a position to sell, add a buy, set protected cash aside, '
        + 'or change the model allocation.',
      );
    }

    return blockers;
  });

  /**
   * Whether Submit may fire: nothing is outstanding.
   *
   * `hasChanges()` is no longer tested here because {@link submitBlockers} tests it -- an empty
   * blocker list now implies something was asked for. One condition, one place, so the button and
   * the panel can never disagree about whether the request is submittable.
   */
  readonly canSubmit = computed(() => this.submitBlockers().length === 0);

  /**
   * Whether the current step's inputs allow advancing. Step 3 (Protected Cash)
   * blocks when protected cash has consumed the entire investable amount — there
   * is nothing left to allocate to models. Step 4 gates on the allocation totals.
   */
  readonly canProceed = computed(() => {
    switch (this.currentStepId()) {
      case 'funding':
        return this.fundingStepBlockedReason() === null;
      case 'sleeveAmounts':
        return this.sleeveAmountsStepBlockedReason() === null;
      case 'positions':
        return this.positionsStepBlockedReason() === null;
      case 'buys':
        return this.buyStepBlockedReason() === null;
      case 'doNotTrade':
        return !this.dntConfirmationRequired();
      case 'protectedCash':
        return this.protectedCashStepBlockedReason() === null;
      case 'allocation':
        // ⚠️ THE REASON, NOT `allocation().valid` (ACMTECH-1039). This was the one case reading
        // the predicate instead of its own message, which was harmless while the two said the
        // same thing. Amount to Raise moved onto this step and broke that: an over-cap figure
        // leaves the allocation VALID -- only the cap is ever applied, so the pot is sound --
        // and Next stayed open with a figure on screen the request would not honour. Every
        // other step here gates on its reason; this one now does too.
        return this.allocationStepBlockedReason() === null;
      default:
        return true;
    }
  });

  // ---- Change detection ----------------------------------------------------
  /**
   * The allocation as it stood when the wizard opened, for change detection.
   *
   * Only meaningful because step 4 is **pre-loaded** from the account's existing sleeves
   * (ACMTECH-906): without a baseline there would be nothing to compare against, since
   * a pre-loaded grid looks "filled in" from the moment it renders.
   */
  private readonly allocationBaseline = signal<readonly AllocationFingerprint[]>([]);

  /** True when the allocation grid differs from what the account already had. */
  readonly allocationChanged = computed(() => {
    const before = this.allocationBaseline();
    const rows = this.allocation().rows;

    // ⚠️ The grid opens BLANK (ACMTECH-945), so at load it differs from the baseline by
    // construction — the baseline is what the account HOLDS, and the grid is what the advisor
    // has SAID, which is nothing yet. Comparing the two directly would report a change before
    // anyone touched anything, and the "no changes to submit" hint would never appear.
    //
    // An empty, untouched grid is an unanswered question, not a proposal. This cannot let a
    // no-op through: with nothing allocated the whole balance is unplaced, and submitBlockers
    // holds Submit shut until it lands somewhere.
    // "Opening state" means the advisor has entered nothing ANYWHERE -- no value typed, no
    // percentage set. It is no longer "nothing allocated": the value column is PREFILLED from
    // the sleeves now, so a grid holding the account's own figures is still an unanswered
    // question rather than a proposal. Once anything is touched the full comparison runs.
    const untouched = rows.every(
      (r) => !r.touched && !r.distributionManual && !r.contributionManual,
    );
    if (untouched) {
      // ...but choosing a model IS a proposal, even before money is put against it, and so is
      // removing one. Compare the model SET while ignoring the absent figures: a blank row
      // with nothing selected is the opening state whether it came from a sleeve or from an
      // unsleeved account, and anything else is the advisor having said something.
      const chosen = rows.map((r) => r.modelId).filter((m) => m !== '');
      const held = before.map((b) => b.modelId).filter((m) => m !== '');
      if (chosen.length !== held.length || chosen.some((m, i) => m !== held[i])) return true;

      // ⚠️ Same models and nothing typed, but that is NOT the same as nothing being asked for.
      //
      // The comment above used to say a grid holding "the account's own figures" is still an
      // unanswered question. That was true when the value column opened blank, and it stopped
      // being true when the grid started seeding from the CURRENT TARGETS: it opens at
      // target% x the pot, which is what the account SHOULD hold, not what it does. On a
      // drifted account those are different numbers -- 60/40 targets against sleeves holding
      // 63/37 seeds a grid that moves $28,150.03 between two models.
      //
      // That is a real instruction and the trade team has real work to do, so it has to count
      // as a change even though the advisor typed nothing. What they are submitting is
      // "put this account back on its stated targets".
      //
      // 🟢 A genuinely settled account is unaffected: with no drift the seeded values equal what
      // the sleeves hold, this comparison finds nothing, and "no changes to submit yet" still
      // appears -- which is the case that hint was written for.
      //
      // Compared by INDEX rather than by model, because an untouched grid is built from the
      // sleeves in order. `before[i]` is missing only for a row the advisor added, and a row
      // they added with no model has no value against it either, so it compares equal to 0.
      return rows.some(
        (r, i) => Math.abs(r.allocatedValue - (before[i]?.allocatedValue ?? 0)) > EPS,
      );
    }

    const now = rows.map(fingerprintAllocation);
    if (before.length !== now.length) return true;
    return now.some((row, i) => !sameAllocation(row, before[i]));
  });

  /**
   * Whether the advisor has actually asked for anything.
   *
   * Guards Submit, because a request that instructs nothing is indistinguishable from a
   * mistake and still costs the trade team a review.
   *
   * Notes and attachments deliberately do **not** count: a note with no trade
   * instruction is not a reallocation, and the General Account Message workflow exists
   * for that. ⚠️ So an advisor who writes "please rebalance to target" and changes
   * nothing else is blocked — intentionally.
   */
  readonly hasChanges = computed(() => {
    // 🔴 A move-money request asks for something the moment it carries an amount, and the test
    // has to be this rather than the reallocation one — which would answer YES on an untouched
    // contribution and NO on a perfectly valid one.
    //
    // Yes, because `allocationChanged()` compares the (deliberately empty) grid against a
    // baseline built from the account's real sleeves, so it reads "changed" before the advisor
    // does anything. And no, because a contribution that leaves everything as cash sells
    // nothing, buys nothing and allocates nothing — every term below is zero, yet $6,000 is
    // arriving and the trade team has to place it.
    if (this.isMoveMoney()) return this.fundingAmount() > 0;

    return (
      this.soldPositions().length > 0
      || this.actionableBuys().length > 0
      || this.dnt().size > 0
      || this.cashInput() > 0
      // ⚠️ Releasing sleeve cash is deliberately NOT counted here, though it does change the
      // filed Custom Holdings line. It instructs nothing on its own: the released money is
      // unallocated, which Submit refuses anyway, so this would add no gate. And counting it
      // made "starts with nothing to submit" false for any account merely holding cash, which
      // is the opposite of what that assertion is protecting.
      || this.allocationChanged()
    );
  });

  constructor() {
    // The funding form is created with the store and never rebuilt, so its subscriptions live
    // here rather than in `initialize()` where `positionsForm`'s do — that form is rebuilt from
    // each load, this one is not.
    this.fundingForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.formVersion.update((v) => v + 1);
    });

    this.fundingForm.controls.isCloseAccount.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((closing) => this.onCloseAccountChanged(closing));

    // `cashTakenControl`'s clamp subscription lived here. The control went with the panel that
    // wrote it; `cashForm` does its own clamping, per row, against that row's balance.
  }


  /**
   * Custodial cash the advisor is taking toward a distribution (their step 1).
   *
   * ⚠️ SAME NUMBER, DIFFERENT CONTROL as of 2026-09-17. This used to read its own
   * `cashTakenControl` -- a single total typed into a panel above the positions table. That panel
   * is gone and a distribution now uses the per-position release rows the reallocation wizard
   * already had, so this is `cashReleased` under the name that means something here.
   *
   * 🟢 The rename is the point of keeping it. "Released" describes freeing cash INTO the
   * investable pool; a distribution draws it OUT toward a target. The arithmetic is identical and
   * the intent is opposite, so `unappliedValue`, the strip's term and the review page all keep
   * reading a name that matches what the advisor is doing.
   *
   * ⚠️ Zero for every other type, which is what stops a reallocation counting its released cash
   * twice -- once through `sourcePool` and again as a distribution's source.
   */
  readonly cashTaken = computed(() => (this.isDistribution() ? this.cashReleased() : 0));

  /**
   * Keep the wizard coherent when the Close Account checkbox adds or removes steps.
   *
   * 🔴 Two things go wrong on their own, and both are silent.
   *
   * `currentStep` is a plain 1-based index into {@link steps}, and closing the account cuts
   * that list to two. An advisor who walked forward, came back to step 1 and ticked the box
   * would leave the index past the end, where `currentStepId()` is `undefined` — the body
   * `@switch` matches no case, so the modal renders blank between a header and a live Next
   * button, and {@link canProceed} falls through to its `default: true` and lets them press it.
   *
   * And the steps that disappear take their state with them only if it is cleared. Left
   * behind, a sell marked before the box was ticked still reaches the payload, so a request to
   * empty and close an account also carries "sell 12 shares of CL" — a partial instruction on
   * a total liquidation, which the trade team would have to reconcile by hand.
   */
  private onCloseAccountChanged(closing: boolean): void {
    // Bumped FIRST rather than left to the group-level subscription above. Everything below
    // reads `steps()`, which reads `isCloseAccount()`, which reads `formVersion()`; Angular
    // emits a control's stream before its parent group's, so relying on that bump would clamp
    // against the step list as it stood a moment ago.
    this.formVersion.update((v) => v + 1);

    if (closing) {
      this.toggleSellAll(false);
      this.sleeveAmountRows.set([]);
      // ⚠️ AND THE PREFILL LATCH WITH THEM. Closing removes step 2 altogether, so an advisor who
      // ticks the box and unticks it comes back to a grid this cleared -- and with the latch left
      // set they would land on an empty one, the prefill having been "used up" on a step they
      // never got to see. Reset here rather than in `clearSleeveAmounts`: this is the request
      // changing shape underneath the advisor, not the advisor clearing the grid on purpose.
      this.sleeveAmountsPrefilled.set(false);
      // The locked select reads its value from this control, so the lock alone would leave a
      // previously chosen Monthly on screen, greyed and wrong. Rewriting it is what makes the
      // displayed answer and the filed answer the same fact rather than two that happen to match.
      //
      // ⚠️ NOT RESTORED on untick, like the cash and sleeve rows below: One Time is the load
      // default, so an advisor who unticks lands exactly where they started rather than on a
      // schedule they may have set several steps ago and forgotten.
      const oneTime = this.oneTimeFrequency();
      if (oneTime) this.fundingForm.controls.deliveryFrequencyId.setValue(oneTime.id);
      // EMITTING, for the reason given in `commitToSma`: `cashInput` advances off this
      // control's value stream, so a silent write would leave the derived figures behind.
      this.cashControl.setValue(0);
    }

    const last = this.totalSteps;
    if (this.currentStep() > last) this.currentStep.set(last);

    // 🔴 ARRIVING WITHOUT MOVING. This checkbox adds and removes steps, so the advisor can end up
    // standing on a different step than they were a moment ago without any navigation running --
    // and on untick that is exactly what happens: the step they are on is Raise Cash from Models
    // again, with the grid this method emptied. `goToStep` would not help them, because it
    // returns early when the target is already the current step.
    //
    // Treated as an arrival, which is what it is from where the advisor is sitting.
    this.enterCurrentStep();
  }

  // --------------------------------------------------------------------------
  // Loading
  // --------------------------------------------------------------------------
  initialize(account: AccountModel | undefined): void {
    this.investmentAccountId.set(account?.investmentAccountID ?? null);
    this.isLoading.set(true);
    this.loadError.set(false);
    this.dataService
      // The funding wave runs only for the types that have a funding step, and the direction is
      // what filters the account's bank authorizations: a contribution can only use an INCOMING
      // instruction, a distribution or a close only an OUTGOING one. Offering the wrong
      // direction would let an advisor point money at an account that cannot receive it.
      .loadFormData(
        account,
        this.isMoveMoney()
          ? { direction: this.isContribution() ? 'INCOMING' : 'OUTGOING' }
          : undefined,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.buildPositionsForm(data);
          this.data.set(data);
          // Stamped here, at the moment the data actually arrived, rather than at submit.
          this.loadedAt.set(new Date());
          this.buyRows.set([]);
          this.modelFreeAmountSignal.set(0);
          this.smaCommitted.set(false);
          // 🔴 A move-money request states DOLLARS against existing sleeves, never a target
          // allocation, so its grid stays empty and `sleeveAmountRows` carries step 2 instead.
          //
          // Not merely unused — actively harmful if seeded. `allocation().totals.value` feeds
          // the money math (`unappliedValue`, `investableBeforeBuys`, the protected-cash pool),
          // so a seeded grid would present the account's own value as already committed and
          // every gate on a contribution would measure against the wrong pool.
          //
          // An empty grid is a valid one by design (see `allocation().valid`), so leaving it
          // empty blocks nothing.
          if (this.isMoveMoney()) {
            this.allocationRows.set([]);
            this.sleeveAmountRows.set(this.buildSleeveAmountRows(data));
          } else {
            this.allocationRows.set(this.buildAllocationRows(data));
            this.modelValuesEditedSignal.set(false);
            this.seedOpeningRows();
          }
          // ⚠️ Built from the SLEEVES, not from the grid we just set.
          //
          // It used to read `allocation().rows`, which worked only because the grid opened at
          // the account's real state. Since ACMTECH-945 the grid opens BLANK, so that would
          // snapshot a baseline of zeros — and an advisor who typed the account's existing
          // numbers back in would register as having changed it, making a no-op request
          // submittable.
          //
          // `allocationChanged()` has to mean "differs from what the account HOLDS", so the
          // baseline is the account, independent of how the grid chooses to open.
          this.allocationBaseline.set(this.buildAllocationBaseline(data));
          // Protected cash defaults to 0 (not pre-filled): the account's cash
          // starts as investable, and the advisor sets aside cash explicitly.
          this.formVersion.update((v) => v + 1);

          // SEEDED ONLY WHEN THERE IS NOTHING TO CHOOSE, which is the contribution: one method
          // is allowed, the select is locked onto it, and a locked control the advisor cannot
          // use must already hold its answer or the step's gate blocks Next with no way forward.
          //
          // 🔴 A DISTRIBUTION OPENS UNCHOSEN (AndyP, 2026-11-03), reversing the day-old default.
          // Several methods are allowed and they are not interchangeable -- a cheque is posted,
          // an ACH lands in a bank account, Leave in Cash never leaves the custodian -- so the
          // opening selection was an answer the form had supplied on the advisor's behalf and
          // that they could file without ever reading. The gate for it already existed
          // (`fundingStepBlockedReason`, 'Choose how the money should be delivered'); it was
          // simply unreachable while this line ran for both request types.
          //
          // ⚠️ KEYED ON `deliveryMethodLocked`, not on `isContribution()`. The day contributions
          // are given a second method, this stops seeding by itself rather than quietly picking
          // one of the two. Same reason the lock is counted rather than typed.
          if (this.isMoveMoney()
            && this.deliveryMethodLocked()
            && this.fundingForm.controls.deliveryMethodId.value == null) {
            const only = this.deliveryMethods()[0];
            if (only) this.fundingForm.controls.deliveryMethodId.setValue(only.id);
          }

          // One Time likewise, matching V1's default. The overwhelmingly common case is a single
          // transfer, and defaulting to it means the step needs no interaction at all on the
          // delivery side — a recurring schedule is the deliberate choice, not the baseline.
          //
          // ⚠️ Found by its own `oneTime` flag, never by name or id: it is also the frequency for
          // which a start date is meaningless, so defaulting here is what keeps the date control
          // out of the way until someone asks for a schedule.
          if (this.isMoveMoney() && this.fundingForm.controls.deliveryFrequencyId.value == null) {
            const oneTime = this.oneTimeFrequency();
            if (oneTime) this.fundingForm.controls.deliveryFrequencyId.setValue(oneTime.id);
          }

          // ⚠️ Skip step 1 outright when there is nothing on it.
          //
          // An account fully invested through its model has no positions of its own to sell, and
          // neither does an account whose Custom Holdings sleeve is empty. Opening on a blank
          // table and an explanatory line asks the advisor to read, understand and dismiss a
          // step that had nothing for them -- so the wizard lands on the first step that does.
          //
          // 🟢 The step is SKIPPED, not removed. It keeps its place in the indicator and Back
          // still reaches it, so the notice is there for an advisor who wonders where the
          // holdings went rather than being deleted along with the step.
          //
          // ⚠️ Keyed on there being no positions AT ALL, not on none being sellable. A locked
          // row -- a Structured Product, say -- is still information about the account, and an
          // advisor who cannot sell it is precisely the one who needs to see it is there.
          //
          // ⚠️ `smaLockActive()` is NOT redundant with the position count. `findPositionSource`
          // prefers a Custom Holdings sleeve over everything else, so an SMA account that also
          // carries one -- which should not exist, and is the commingling the lock is there to
          // stop -- yields positions and would otherwise open on a step the lock has closed.
          // Valid here because `allocationRows` was seeded a few lines up.
          //
          // 🔴 Move-money types are excluded, and not just because their first step is
          // different. `stepIndex` returns 0 for a step that is not in the current list, and
          // neither a contribution nor a distribution has an 'allocation' step — so this would
          // set `currentStep` to 0 and leave `currentStepId()` reading `steps()[-1]`, i.e.
          // `undefined`. The wizard would open on a blank body. Their step 1 is Funding, which
          // is never empty, so there is nothing to skip past in the first place.
          //
          // ⚠️ Cash counts as something on the step. It is opt-in now, so a sleeve holding only
          // cash has a real decision waiting here -- skipping past would leave that cash
          // unreachable and unable to enter any request.
          //
          // 🔴 AND SO DO THE MODELS, as of ACMTECH-1039. Amount to Use frees money OUT of a
          // model sleeve, so an account that is fully invested through its models -- the first
          // case this skip was written for -- now has the most to do here, not the least. It
          // was being sent past the only screen that offers it.
          //
          // ⚠️ `managedModelRows`, not `data.sleeves`: it is the list the table actually
          // renders, so the skip and the screen cannot disagree about whether there is
          // anything on it. It reads `allocationRows`, seeded a few lines up, and returns
          // nothing for a move-money request or a committed SMA.
          //
          // ⚠️ Resolved from two branches, and the nesting is the whole point: the first two
          // conditions widen WHEN to jump, and the move-money guard says the jump has nowhere
          // to land at all -- so it has to be the OUTER one. Parenthesised rather than left to
          // precedence, because getting it wrong opens the wizard on an undefined step.
          const nothingToDoOnStepOne = data.positions.length === 0
            && (data.cashPositions ?? []).length === 0
            && this.managedModelRows().length === 0;

          if ((nothingToDoOnStepOne || this.smaLockActive()) && !this.isMoveMoney()) {
            this.currentStep.set(this.stepIndex('allocation'));
          }
        },
        complete: () => this.isLoading.set(false),
        error: (err) => {
          this.isLoading.set(false);
          this.loadError.set(true);
          // Any ReallocationV2LoadError carries advisor-facing text of its own; anything
          // else is unexpected and gets the generic line.
          //
          // ⚠️ The PANEL is the only place this is reported. It used to also fire a toast with
          // the same string, so an SMA account said "changes are handled by the external
          // manager" twice at once -- in the middle of the modal and again in the corner.
          //
          // A toast earns its place when the thing it reports might otherwise be missed. This
          // cannot be: `loadError` swaps the whole wizard body for a full-height "Unable to
          // Load" panel carrying this exact message and a Close button, on a modal that is
          // already open and in front of the advisor. Saying it twice does not make it twice as
          // clear -- it reads as two separate problems.
          //
          // Applies to every load failure, not only the SMA block: they all render through that
          // same panel, so singling one out would leave the others double-reported for no
          // reason anyone could infer later.
          const message =
            err instanceof ReallocationV2LoadError
              ? err.message
              : 'There was an error loading the account setup form. Please try again.';
          this.loadErrorMessage.set(message);
        },
      });
  }

  /**
   * One row per model sleeve the account already has, all starting at zero.
   *
   * Zero, not pre-apportioned by Orion's contribution/distribution percentages, even though
   * those percentages are exactly what the quick-apply button uses. The step opens as an
   * unanswered question: pre-filling it would present Orion's standing instruction as this
   * request's decision, and an advisor who accepted it would have their agreement recorded
   * without ever having been asked. The button makes taking those percentages one click.
   *
   * ⚠️ Sleeves with no Connect account id are dropped. The key has to identify the sleeve for
   * the life of the request and a null cannot; such a row also has nothing to write back to.
   * `data.sleeves` is already filtered to real model sleeves (Custom Holdings excluded), so a
   * null here is a data problem rather than a shape to accommodate.
   */
  private buildSleeveAmountRows(data: ReallocationV2Data): readonly SleeveAmountRow[] {
    // `sleeves` is optional and legitimately absent — an unsleeved account has none. That is not
    // an error here: the step renders its own notice and the money flows to the later steps
    // instead, so an empty list is a valid answer rather than a missing one.
    return (data.sleeves ?? [])
      .filter((s) => s.accountId != null)
      .map((s) => ({ sleeveKey: String(s.accountId), amount: 0 }));
  }

  private buildPositionsForm(data: ReallocationV2Data): void {
    this.positionsForm.clear();
    // ⚠️ THE SAME RULE `positions()` APPLIES, and it has to be stated twice because the two read
    // different things: that computed derives a view row from `data.positions`, while this builds
    // the control whose `disabled` actually stops the click. Deriving the control from the view
    // would invert the dependency -- `positions()` reads this form for `selected`.
    //
    // 🟢 Safe to decide once at build time: `requestType` is set before `initialize`, so the type
    // cannot change under a built form. It reloads with the data if it ever does.
    const locksReviewable = REALLOCATION_V2_DISTRIBUTION_LOCKS_REVIEWABLE_SELLS && this.isDistribution();
    for (const p of data.positions) {
      const sellable = p.sellable && !(locksReviewable && p.requiresReview);
      const group: PositionForm = new FormGroup({
        selected: new FormControl<boolean>({ value: false, disabled: !sellable }, { nonNullable: true }),
        fill: new FormControl<PositionFillMode>('none', { nonNullable: true }),
        // nonNullable is kept for reset(), which should restore the full lot rather than
        // clear the box; it does not stop the DOM reporting null when the box is emptied.
        // `updateOn: 'blur'` for the same reason as cashControl: shares drive totalSoldValue
        // and therefore the strip, and a part-typed share count is not a share count. Typing
        // 150 over 1500 should not report 1, then 15, then 150 as freed value along the way.
        // 🔴 RESTS AT ZERO AND IS LIVE FROM THE START, on every type that reaches this step.
        // There is no Sell checkbox anywhere any more (AndyP, 2026-09-25), so nothing would ever
        // enable this box -- the advisor would face a screen of greyed inputs and no way in.
        sharesToSell: new FormControl<number | null>(
          { value: this.restingFill(), disabled: !sellable },
          { nonNullable: true, updateOn: 'blur' },
        ),
      });

      // 🔴 THE FIGURE IS THE DECISION: `selected` follows the share count instead of gating it.
      // Rows rest at 0, so ticking a box only to type a number into the field beside it was a
      // second click that said nothing the number did not -- and it made a half-state reachable
      // (ticked, zero shares) that needed its own error, its own red border and its own entry in
      // the step's blocked reason.
      //
      // ⚠️ KEPT AS A CONTROL, not removed. `positions()`, `soldPositions`, `inSleeve` and the
      // payload all read `selected`; deriving it here leaves every one of them untouched and
      // confines the change to what SETS it.
      //
      // ⚠️ ON BLUR, like the box itself, so the row highlights when the figure is committed
      // rather than part-way through typing it.
      group.controls.sharesToSell.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((value) => {
          const wanted = Number.isFinite(value) && (value as number) > 0;
          if (group.controls.selected.value !== wanted) {
            group.controls.selected.setValue(wanted, { emitEvent: false });
          }
        });

      // 🔴 NOT DEAD CODE, THOUGH NOTHING TICKS A ROW ANY MORE. `resetStepOne`, the SMA commit and
      // `toggleSellAll` all clear rows THROUGH this control, so it is the one path that returns a
      // row to nothing. An earlier cut of the derivation above short-circuited it and broke Reset:
      // a reset row stayed flagged, kept the whole lot and stayed disabled. A spec caught it.
      //
      // 🟢 There is no feedback loop to guard against: the derivation writes `selected` with
      // `emitEvent: false`, so it never re-enters here. Only a real untick does -- Reset, SMA, or
      // another type's checkbox.
      group.controls.selected.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((selected) => {
        if (selected) {
          group.controls.sharesToSell.enable({ emitEvent: false });
        } else {
          // ⚠️ RELEASES SELL ALL TOO. Unticking is how a row is undone -- including by Reset,
          // which unticks everything -- so leaving the flag set would hand back a row that
          // looked ordinary and re-locked its box the moment it was ticked again.
          group.controls.fill.setValue('none', { emitEvent: false });
          // ⚠️ THE BOX STAYS LIVE -- it is the only control the row has left, so disabling it here
          // would leave the advisor no way back in after a Reset.
          group.controls.sharesToSell.setValue(this.restingFill(), { emitEvent: false });
        }
        this.formVersion.update((v) => v + 1);
      });

      // Clamp shares-to-sell to [0, total shares].
      group.controls.sharesToSell.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
        // An empty box stays empty. Angular reports a cleared (or unparseable) number input
        // as null, and the old `value || 0` clamped that to 0 and wrote it back -- which both
        // refilled a field the advisor had just cleared and left a leading zero on whatever
        // they typed next, so backspacing 100 and typing 20 produced "020".
        //
        // Only a real number is clamped. Blank is a valid intermediate state, and every
        // reader already treats it as 0, so nothing downstream sees a null.
        if (Number.isFinite(value)) {
          const max = p.shares ?? 0;
          let clamped = Math.min(Math.max(0, value as number), max);

          // 🔴 WHOLE SHARES ON EVERY TYPE -- UNLESS THE WHOLE LOT IS GOING (AndyP, 2026-09-25;
          // distribution-only for a day before that). The trade team executes on the share count,
          // and a partial-share sell order is not something any advisor asked for; the share
          // calculator has floored its answer since it was built, and its own comment admitted
          // the gap this closes -- "the share input still accepts a fraction if the advisor types
          // one; this just does not manufacture one".
          //
          // ⚠️ NO DEAD END ON A REALLOCATION, which is why extending it is safe there. A
          // distribution has to LAND on a figure, so flooring could in principle strand it -- and
          // SELL 1 SHARE exists for exactly that. A reallocation has no target to land on: selling
          // ENLARGES the pool rather than closing a gap, so a floored share count simply frees
          // slightly less and the allocation grid absorbs it.
          //
          // ⚠️ SELL ALL IS THE EXCEPTION, and it has to be. A position can genuinely hold 12.734
          // shares, and selling all of it means all of it -- flooring there would strand a
          // fraction in an account the advisor believes they have emptied.
          //
          // 🔴 IT NEEDS NO TEST HERE. That path writes the lot with `emitEvent: false`, so it does
          // not reach this subscription at all -- EVERY value that does is one the advisor typed.
          // There used to be a `fill !== 'all'` guard as well, and once SELL ALL stopped locking
          // its box that guard became a hole: a row sitting in 'all' accepted a hand-typed
          // 548.9673 and kept every decimal (AndyP, 2026-09-24).
          //
          // 🟢 DOWN, not nearest, matching the calculator: rounding up sells more of the client's
          // holding than was typed. With over-raising allowed a sale that overshoots is no longer
          // blocked, which makes rounding up worse rather than better -- it would go through.
          clamped = Math.floor(clamped);

          if (clamped !== value) group.controls.sharesToSell.setValue(clamped, { emitEvent: false });
        }
        // 🔴 A TYPED FIGURE UN-PRESSES THE BUTTON, same rule as a cash row: a pressed button
        // asserts something about the number beside it, and typing over that number makes the
        // assertion false. SELL ALL writes with `emitEvent: false` and so never lands here.
        if (group.controls.fill.value !== 'none') {
          group.controls.fill.setValue('none', { emitEvent: false });
        }
        this.formVersion.update((v) => v + 1);
      });

      this.positionsForm.push(group);
    }

    // The cash rows, in their own array. Same two-control shape and the same two subscriptions
    // as a position row -- toggle enables and resets, the value clamps -- because it is the same
    // interaction: tick to act on the row, then adjust how much.
    this.cashForm.clear();
    // Indexed, because a row's ceiling is looked up by position -- the same index pairing
    // `cashGroup`, `cashRowHolds` and `cashRowCeilings` all rely on.
    (data.cashPositions ?? []).forEach((c, i) => {
      const group: CashUseForm = new FormGroup({
        use: new FormControl<boolean>(false, { nonNullable: true }),
        fill: new FormControl<CashFillMode>('none', { nonNullable: true }),
        // Pre-filled, disabled until ticked, matching `sharesToSell` -- so releasing what the
        // row can give is one tick and releasing part of it is one edit.
        //
        // ⚠️ Seeded from the BALANCE here and corrected to the row's available figure by
        // `syncCashRowsToCeilings` on the first run. It cannot be seeded from the ceiling
        // directly: this form is built BEFORE `data` is set, so `cashRowCeilings()` is still
        // empty at this point and would seed every row at zero.
        //
        // `updateOn: 'blur'` for the reason given on `cashControl`: this figure feeds
        // `investable` and therefore the strip, and a part-typed amount is not an amount --
        // typing 5000 over 15000 should not report 5, then 50, then 500 as released along
        // the way. That is also why the input needs `appCommitOnEnter`.
        // 🔴 LIVE FROM THE START -- same reasoning as `sharesToSell` above: the Use checkbox is
        // gone on every type now, so nothing would ever enable this.
        amount: new FormControl<number | null>(
          { value: this.restingFill(), disabled: false },
          { nonNullable: true, updateOn: 'blur' },
        ),
      });

      // The cash counterpart of the derivation on `sharesToSell`: an amount above zero IS the
      // decision to use the row, so `use` follows it rather than gating it.
      group.controls.amount.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((value) => {
          const wanted = Number.isFinite(value) && (value as number) > 0;
          if (group.controls.use.value !== wanted) {
            group.controls.use.setValue(wanted, { emitEvent: false });
          }
        });

      group.controls.use.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((use) => {
        // ⚠️ SET HERE, SYNCHRONOUSLY, on the way in as well as the way out -- not left to
        // `cashRowSync`. Effects are SCHEDULED, so between selling a position and ticking a row
        // in the same turn the effect has not run yet, and the tick would release a figure
        // computed against the sleeve as it was. That is the arithmetic, so it cannot depend on
        // when a flush happens to land; the effect is left to keep the DISPLAY honest.
        const resting = this.restingFill();
        if (use) {
          // 🔴 ONLY WHEN `use` DID NOT COME FROM THE BOX. The amount is what sets `use` now, so
          // writing a resting 0 back here would erase the figure that had just turned it on. The
          // derivation writes with `emitEvent: false` and never re-enters, but Reset and SMA do
          // -- and those want the clear.
          if ((group.controls.amount.value ?? 0) <= 0) {
            group.controls.amount.setValue(resting, { emitEvent: false });
          }
          group.controls.amount.enable({ emitEvent: false });
        } else {
          // ⚠️ THE BOX STAYS LIVE -- it is the row's only control, so disabling it here would
          // leave no way back in after a Reset.
          //
          // A row taken out of the request is not filled by a button any more.
          group.controls.fill.setValue('none', { emitEvent: false });
          group.controls.amount.setValue(resting, { emitEvent: false });
        }
        this.formVersion.update((v) => v + 1);
      });

      group.controls.amount.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
        // Only a real number is clamped; blank stays blank while it is being retyped, and every
        // reader treats it as 0. Same rule as `sharesToSell`, and for the same reason -- writing
        // 0 back would refill a box the advisor had just cleared.
        if (Number.isFinite(value)) {
          const clamped = round2(Math.min(Math.max(0, value as number), c.marketValue));
          if (clamped !== value) group.controls.amount.setValue(clamped, { emitEvent: false });
        }
        // 🔴 ANY CHANGE THROUGH HERE UN-PRESSES THE BUTTONS. This subscription fires for a typed
        // figure AND for the buttons' own write -- which is why they set `fill` immediately
        // afterwards rather than before. Anything else that reaches the box (Reset, SMA, the
        // ceiling sync) genuinely should clear it.
        if (group.controls.fill.value !== 'none') {
          group.controls.fill.setValue('none', { emitEvent: false });
        }
        this.formVersion.update((v) => v + 1);
      });

      this.cashForm.push(group);
    });

    // Normalise custodial cash whenever it changes (see normalizeCash). No cap: exceeding
    // the pool is reported by protectedCashStepBlockedReason, not silently corrected.
    this.cashControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.normalizeCash();
      this.formVersion.update((v) => v + 1);
    });
  }

  /**
   * Keep custodial cash a non-negative, whole-cent number. Nothing more.
   *
   * ⚠️ This used to CLAMP to the pre-cash investable, and that cap was removed on purpose
   * (ACMTECH-943). A cap meant the advisor's number was silently rewritten to fit, so no
   * warning could ever fire -- the value was already legal by the time anything could object.
   * `protectedCashStepBlockedReason` now refuses to advance and says why instead, which is
   * the rule the buy step already follows: "silently rewriting a number the advisor typed is
   * worse than refusing to advance and saying why".
   *
   * Rounding stays, and matters for a reason worth keeping: the pool is a difference of
   * currency amounts, so it lands a hair under its true value (36555.31 − 32777.78 is
   * 3777.5299999999997). Flooring turned that into 3777.52 -- a cent the advisor could never
   * set aside, which also left investable() stuck at 0.01 and blocked the step 3 → 4 hand-off.
   *
   * Uses emitEvent:false to avoid re-entering the cash subscription; callers bump formVersion
   * so cashInput() and everything derived from it refresh.
   */
  private normalizeCash(): void {
    const current = this.cashControl.value || 0;
    const normalized = round2(Math.max(0, current));
    if (normalized !== current) this.cashControl.setValue(normalized, { emitEvent: false });
  }

  positionGroup(index: number): PositionForm {
    return this.positionsForm.at(index) as PositionForm;
  }

  /**
   * Keep each cash row's controls in step with what the target leaves it.
   *
   * Two things move as the advisor works, and both are consequences of the target being live:
   * selling out of the sleeve lowers it and frees cash; putting a sold position back raises it
   * and can take a row's whole balance away again.
   *
   * ⚠️ THE TICK IS DISABLED THROUGH THE CONTROL, not a template `[disabled]`. Angular warns about
   * the attribute form on a reactive control, and the two states can disagree -- the same reason
   * `buildPositionsForm` disables an unsellable position's checkbox on the control itself.
   *
   * ⚠️ An UNTOUCHED row's amount tracks the ceiling, so the box always offers what the row can
   * actually give. A row the advisor has ticked keeps THEIR figure: silently rewriting a number
   * someone typed is the thing this wizard refuses to do, and `cashReleased` clamps it on read
   * anyway.
   *
   * ⚠️ `allowSignalWrites`, because unticking a row that has just lost its balance has to bump
   * `formVersion` -- otherwise `cashReleased` would go on counting a release the row can no
   * longer make. The write is guarded by an actual state change, so it cannot loop.
   *
   * ⚠️ A FIELD INITIALIZER, not a call from `buildPositionsForm`. `effect()` needs an injection
   * context and a form build runs from a subscribe callback, which is not one -- registering it
   * there throws NG0203 and takes the whole step down. Being a field also means it is registered
   * exactly once, where the per-load call site needed a flag to avoid stacking effects.
   *
   * 🟢 Registering before any row exists is harmless: the body iterates an empty `cashForm` until
   * the first load populates it, and the effect re-runs when the ceilings appear.
   */
  private readonly cashRowSync = effect(() => {
    const ceilings = this.cashRowCeilings();
    let changed = false;

    this.cashForm.controls.forEach((group, i) => {
      const ceiling = ceilings[i] ?? 0;
      const usable = ceiling > 0;

      if (usable && group.controls.use.disabled) {
        group.controls.use.enable({ emitEvent: false });
      } else if (!usable && group.controls.use.enabled) {
        if (group.controls.use.value) changed = true;
        group.controls.use.setValue(false, { emitEvent: false });
        group.controls.use.disable({ emitEvent: false });
        group.controls.amount.disable({ emitEvent: false });
      }

      // Only while the row is untouched -- see the note above about not rewriting typed figures.
      //
      // ⚠️ Tracks the RESTING value, which is 0. Left tracking the ceiling, this effect would
      // undo the zeroing above every time the ceilings recomputed -- so a box the advisor had
      // just been given empty would refill itself with the whole balance.
      const resting = this.restingFill();
      if (!group.controls.use.value && group.controls.amount.value !== resting) {
        group.controls.amount.setValue(resting, { emitEvent: false });
      }
    });

    if (changed) this.formVersion.update((v) => v + 1);
  }, { allowSignalWrites: true });

  /**
   * What a freshly-built or just-unticked row rests at.
   *
   * 🔴 A DISTRIBUTION RESTS AT ZERO; every other type rests at the whole lot.
   *
   * 🔴 ZERO IS NOT A PREFERENCE, it is what makes a tickless row mean anything. `selected` is
   * DERIVED from this figure, so a row resting at the whole lot would arrive already selected and
   * the step would open having decided to liquidate the entire account.
   *
   * ⚠️ IT USED TO RETURN THE WHOLE LOT on a reallocation and Initial Account, where a tick was
   * what selected a row and the box merely followed. The argument for that was real at the time:
   * ticking a position there means "free this up to invest", and freeing the WHOLE position is
   * the common case, so pre-filling the lot made the common case one click and a partial one
   * edit. SELL ALL is now that one click, and it says what it does rather than the wizard
   * inventing a figure that reads exactly like one the advisor chose.
   *
   * 🟢 Kept as a named call rather than inlined as `0`: it is the ONE place "what an undecided row
   * holds" is stated, and Reset, the untick handler and the un-press paths all have to agree
   * with it.
   */
  private restingFill(): number {
    return 0;
  }

  /**
   * What the cash target holds back on one row, paired by index -- see {@link cashRowHolds}.
   *
   * A method rather than another array signal because the template asks per row; the underlying
   * computed is shared, so this costs one array read.
   */
  cashRowHeld(index: number): number {
    return this.cashRowHolds()[index] ?? 0;
  }

  /** The cash row's controls, paired to `data().cashPositions` by index -- see {@link cashForm}. */
  cashGroup(index: number): CashUseForm {
    return this.cashForm.at(index) as CashUseForm;
  }

  /**
   * Release every dollar of sleeve cash, or none of it.
   *
   * The cash counterpart of {@link toggleSellAll}, and index-coupled the same way.
   */
  /**
   * How much more this cash row could still give -- its balance less whatever is already in the
   * box. Zero once the row is fully committed.
   */
  cashRowHeadroom(index: number): number {
    this.formVersion();
    const ceiling = this.cashRowCeilings()[index] ?? 0;
    const current = this.cashGroup(index)?.controls.amount.value ?? 0;
    return round2(Math.max(0, ceiling - current));
  }

  /**
   * Whether what is still outstanding could be covered by this row ALONE, with room to spare.
   *
   * 🔴 Decides WHICH of the two buttons a cash row offers, so only one is ever on screen: "use
   * the lot" and "use just enough" are different answers to the same question, and offering both
   * makes the advisor work out which applies.
   *
   * ⚠️ MEASURED AGAINST HEADROOM, not the row's balance. Once the box holds a figure, that figure
   * is already counted in `unappliedValue` -- comparing against the full balance would offer
   * "use for remaining" on a row that cannot actually reach it.
   *
   * ⚠️ STRICTLY LESS THAN. At exactly the headroom the two buttons do the same thing, and "use
   * all" is the one that says what is happening.
   */
  cashRowCoversRemaining(index: number): boolean {
    if (!this.isDistribution()) return false;
    const outstanding = this.unappliedValue();
    if (outstanding <= EPS) return false;
    return outstanding < this.cashRowHeadroom(index) - EPS;
  }

  /** Which fill button, if any, is currently pressed on a row. */
  cashRowFill(index: number): CashFillMode {
    this.formVersion();
    return this.cashGroup(index)?.controls.fill.value ?? 'none';
  }

  /**
   * Whether to draw USE FOR REMAINING on a row -- the only button a cash row offers.
   *
   * 🔴 THERE WAS A USE ALL BESIDE IT, and it is gone on purpose (AndyP, 2026-09-24). CASH IS A
   * LAST RESORT: the trade team would rather an advisor sold what the request needs than swept
   * the account's cash into it, and a one-click "take the lot" invited exactly that. What is left
   * offers cash only in the case where it actually settles the request -- when this row alone can
   * cover what is outstanding.
   *
   * ⚠️ So a row that CANNOT cover the remainder now offers nothing at all. That is deliberate,
   * not an oversight: the advisor can still type any figure into the box, which is a considered
   * act rather than a button press.
   *
   * 🟢 Pressed keeps it on screen, whatever the arithmetic then says -- filling the row changes
   * the outstanding figure this very test reads, so it would otherwise vanish on its own click
   * and leave no way to undo it.
   */
  showUseRemainingOnCashRow(index: number): boolean {
    if (!this.isDistribution() || (this.cashRowCeilings()[index] ?? 0) <= 0) return false;
    if (this.distributionSatisfied()) return false;
    if (this.cashRowFill(index) === 'remaining') return true;
    return this.cashRowCoversRemaining(index);
  }

  /**
   * Whether to draw USE AVAILABLE on a cash row -- the button every type BUT a distribution gets.
   *
   * 🔴 IT IS NOT THE OLD "USE ALL" COMING BACK, and the name is the difference. `cashRowCeilings`
   * is what the row may release AFTER the 1% cash target, so on a reallocation this button fills
   * the box with a figure that is genuinely smaller than the row's balance. Calling it USE ALL
   * would promise the balance and hand over less.
   *
   * ⚠️ A DISTRIBUTION DELIBERATELY HAS NO SUCH BUTTON. Its cash button was removed because cash
   * is a last resort when money is leaving the account (AndyP, 2026-09-24); a reallocation is
   * only moving cash into the pool to be placed, so the same objection does not apply.
   *
   * 🟢 Always on screen, pressed or not. Nothing here has a finish line to hide behind -- that is
   * `distributionSatisfied`, which is false on every type this button appears on.
   */
  showUseAvailableOnCashRow(index: number): boolean {
    if (this.isDistribution()) return false;
    return (this.cashRowCeilings()[index] ?? 0) > 0;
  }

  /**
   * USE AVAILABLE -- the row's releasable figure, or back to nothing if it is already pressed.
   *
   * ⚠️ REPLACES rather than adds, unlike {@link useRemainingOnCashRow}. There is no target to
   * count toward here, so "available" means the same figure however many times it is pressed;
   * adding would let two presses ask for twice what the row has.
   *
   * ⚠️ UN-PRESSING CLEARS THE BOX rather than restoring whatever was typed before -- the same
   * contract as SELL ALL on a position. Remembering a prior figure would mean holding a second,
   * invisible value per row.
   */
  useAvailableOnCashRow(index: number): void {
    const group = this.cashGroup(index);
    const ceiling = this.cashRowCeilings()[index] ?? 0;
    if (!group || ceiling <= 0) return;
    const pressed = group.controls.fill.value === 'available';
    group.controls.amount.setValue(pressed ? this.restingFill() : ceiling);
    group.controls.fill.setValue(pressed ? 'none' : 'available', { emitEvent: false });
    this.formVersion.update((v) => v + 1);
  }

  /**
   * Put just enough in the box to cover what is outstanding.
   *
   * ⚠️ ADDS TO WHAT IS THERE, it does not replace it -- the same contract as
   * `applyRemainingToCash` and `applyRemainingToSleeves`. `unappliedValue` is already net of this
   * row's current figure, so overwriting with the outstanding amount would take back whatever the
   * advisor had already committed here and land short by exactly that much.
   *
   * 🟢 Clamped at the ceiling anyway, so a stale outstanding figure cannot overfill the row.
   */
  useRemainingOnCashRow(index: number): void {
    const group = this.cashGroup(index);
    const ceiling = this.cashRowCeilings()[index] ?? 0;
    if (!group || ceiling <= 0) return;
    const pressed = group.controls.fill.value === 'remaining';
    if (pressed) {
      group.controls.amount.setValue(this.restingFill());
      group.controls.fill.setValue('none', { emitEvent: false });
    } else {
      const current = group.controls.amount.value ?? 0;
      const outstanding = Math.max(0, this.unappliedValue());
      group.controls.amount.setValue(round2(Math.min(ceiling, current + outstanding)));
      group.controls.fill.setValue('remaining', { emitEvent: false });
    }
    this.formVersion.update((v) => v + 1);
  }

  toggleUseAllCash(checked: boolean): void {
    this.cashForm.controls.forEach((group, i) => {
      const ceiling = this.cashRowCeilings()[i] ?? 0;
      // ⚠️ A row the cash target holds in FULL is skipped rather than marked. Marking it would
      // put a figure in a box whose ceiling is $0, so the advisor would see an editable field
      // that can only ever hold zero while the request reported it as released.
      //
      // 🟢 Partly-held rows ARE marked: they have a real balance left to release, and the amount
      // control clamps to it on read.
      if (checked && ceiling <= 0) return;

      // 🔴 THE FIGURE IS WRITTEN HERE, and it has to be. `use` used to be the decision, and the
      // box followed it to the row's resting value -- which on a reallocation WAS the ceiling.
      // Rows rest at 0 on every type now (AndyP, 2026-09-25), so setting `use` alone marked every
      // row and released nothing: the SMA commit, whose whole point is that the cash goes to the
      // model, quietly sent $0. The positions side already had this exact fix in `toggleSellAll`
      // -- this control is an INSTRUCTION, not an assumption about what the advisor wants.
      if (checked) {
        if ((group.controls.amount.value ?? 0) !== ceiling) {
          group.controls.amount.setValue(ceiling, { emitEvent: false });
        }
        if (!group.controls.use.value) group.controls.use.setValue(true, { emitEvent: false });
        group.controls.amount.enable({ emitEvent: false });
      } else if (group.controls.use.value) {
        // The untick path still goes through `use`, which clears the box and the fill mode.
        group.controls.use.setValue(false);
      }
    });
    this.formVersion.update((v) => v + 1);
  }

  // 🔴 `useAllCashState` LIVED HERE AND IS GONE (AndyP, 2026-09-25) -- the checked/indeterminate
  // state of the cash table's header checkbox, which went with every other tick on this step.
  // `toggleUseAllCash` stays: Reset and the SMA commit still clear rows through it.
  //
  // ⚠️ NOTHING REPLACED IT ON THE CASH SIDE. The positions table got SELL ALL POSITIONS, but cash
  // has no bulk button on purpose -- USE AVAILABLE is per row, so the advisor says which sleeve's
  // cash to release rather than sweeping all of them.

  // --------------------------------------------------------------------------
  // Step 1 / 2 mutations
  // --------------------------------------------------------------------------
  /**
   * Sell this row's whole position, or release it back to being edited.
   *
   * The counterpart to the calculator: one is "I know the dollars", this is "I want the lot".
   * Between them a distribution's rows need no arithmetic on the advisor's side, which is what
   * resting them at 0 cost and this gives back.
   *
   * ⚠️ ORDER IS LOAD-BEARING. `selected` is set FIRST because its own subscription resets the
   * share box to the row's resting value -- setting the shares before it would be overwritten
   * with 0 immediately. Same trap the calculator has.
   *
   * ⚠️ The box is DISABLED rather than left editable at the full lot. A locked box says the
   * figure is not the advisor's to change while the toggle is on, and the toggle is the way to
   * change it -- an editable box holding a number the button put there invites an edit that
   * would then disagree with a button still reading as active.
   *
   * 🟢 A disabled control is excluded from `group.value` but NOT from `controls.x.value`, which
   * is what `positions` reads. The share count still reaches the payload.
   */
  setRowSellAll(index: number, on: boolean): void {
    const group = this.positionsForm.at(index) as PositionForm | undefined;
    const position = this.positions()[index];
    if (!group || !position?.sellable) return;

    if (on) {
      if (!group.controls.selected.value) group.controls.selected.setValue(true);
      group.controls.fill.setValue('all', { emitEvent: false });
      group.controls.sharesToSell.setValue(position.shares ?? 0, { emitEvent: false });
      // 🔴 NO LONGER DISABLES THE BOX (AndyP, 2026-09-24). It used to, on the reasoning that the
      // whole lot has no partial version to adjust toward -- true, but the lock was not carrying
      // that information, the pressed button and the full share count already do. What it did
      // carry was a DEAD END: once the buttons hide on a covered request, a locked row had no
      // control left at all and could only be undone by resetting the whole step.
      //
      // 🟢 It also contradicted this screen's own rule. Taking the checkboxes off settled that the
      // FIGURE is the decision; a figure nobody can edit is not that. Typing over it clears the
      // mode through the usual subscription, so the button and the box cannot disagree.
    } else {
      group.controls.fill.setValue('none', { emitEvent: false });
      // Back to the row's resting value rather than leaving the full lot behind: the advisor
      // just said they do NOT want the whole position, so offering it as the starting point
      // again is answering a question they have already answered.
      group.controls.sharesToSell.setValue(this.restingFill(), { emitEvent: false });
      // ⚠️ `emitEvent: false` above means the derivation did not run, so clear `selected` here.
      // Releasing SELL ALL leaves the row at 0 shares, which is not a row being sold.
      group.controls.selected.setValue(false, { emitEvent: false });
      group.controls.sharesToSell.enable({ emitEvent: false });
    }

    this.formVersion.update((v) => v + 1);
  }

  /**
   * SELL ALL POSITIONS -- every sellable row at once, as though each SELL ALL had been pressed.
   *
   * 🔴 A TOGGLE, NOT A ONE-WAY SWITCH (AndyP, 2026-09-25). It only ever pressed, so once the
   * whole account was marked the button sat there reading as pressed with nothing to do, and the
   * only way back was clicking SELL ALL down every row. It reads as pressed, so pressing it again
   * has to mean what that says.
   *
   * ⚠️ THE DIRECTION IS DECIDED ONCE, UP FRONT, not per row. Asking `isRowSellAll` inside the
   * loop would flip rows against each other -- the first press clears row 1, which makes
   * `allPositionsSellingAll` false, so row 2 gets pressed instead of cleared.
   *
   * ⚠️ ROUTED THROUGH {@link setRowSellAll} rather than writing the controls here, so a row ends
   * in exactly the state one click would have left it in: the box filled AND `fill` set to 'all'.
   *
   * ⚠️ `fill` IS THE HALF THAT MATTERS, and the reason changed under this comment. It used to
   * say `toggleSellAll` would "leave every button looking unpressed" -- true when pressed came off
   * the `fill` flag, and no longer true now that it comes off the share count, which
   * `toggleSellAll` does write. What `fill` still decides is WHICH BUTTON a distribution draws:
   * `showSellAllOnRow` and `showSellRemainingOnRow` both read it, so a row filled the other way
   * would hold its whole lot with `fill` at 'none' and could resolve to SELL FOR REMAINING over a
   * row with no headroom left (AndyP, 2026-09-25).
   *
   * 🟢 THE ROW SET COMES FROM {@link bulkSellRows}, shared with `allPositionsSellingAll`. Two
   * places deciding which rows count is what let the two drift far enough for the toggle to stick
   * one-way; sharing the filter makes "every row this acts on" and "every row this checks" the
   * same set by construction rather than by agreement.
   */
  sellAllPositions(): void {
    const on = !this.allPositionsSellingAll();
    this.bulkSellRows().forEach(({ index }) => this.setRowSellAll(index, on));
  }

  /**
   * The rows SELL ALL POSITIONS acts on, and the same rows it reads its pressed state from.
   *
   * 🔴 INDICES FROM THE ORIGINAL ARRAY, carried alongside each row. `setRowSellAll` and
   * `isRowSellAll` look a control up by its position in `positionsForm`, which is index-paired
   * with the UNFILTERED `positions()` -- so filtering first and using the callback's index asks
   * about the wrong row the moment any position is unsellable.
   *
   * ⚠️ "HAS SOMETHING TO SELL" IS NOT "HAS SHARES". A null share count means Orion did not report
   * one and the wizard sells the position WHOLE, so those rows belong here; a genuine zero lot
   * has nothing to do and would sit permanently un-pressable, holding the toggle one-way.
   */
  private bulkSellRows(): { p: PositionView; index: number }[] {
    return this.positions()
      .map((p, index) => ({ p, index }))
      .filter(({ p }) => p.sellable && (p.shares == null || p.shares > 0));
  }

  /**
   * Whether every row SELL ALL POSITIONS acts on is already selling its whole position.
   *
   * 🟢 SAME SET AS {@link sellAllPositions}, by construction -- see `bulkSellRows`.
   */
  allPositionsSellingAll(): boolean {
    this.formVersion();
    const rows = this.bulkSellRows();
    return rows.length > 0 && rows.every(({ index }) => this.isRowSellAll(index));
  }

  /**
   * Nothing further to raise -- the distribution is covered, exactly or over.
   *
   * 🔴 HIDES THE BUTTON ON AN UNTOUCHED ROW. This was built as a DISABLED state first, on the
   * reasoning that a stood-down button says "nothing more is needed here" where a missing one says
   * nothing at all. On screen that was wrong: a column of greyed SELL ALLs down every row reads as
   * a fault rather than as an answer, and the rows still contributing are what the eye should find
   * (AndyP, 2026-09-24).
   *
   * ⚠️ EVERY BUTTON, INCLUDING ON THE ROWS THAT DID THE COVERING. Keeping those was the first
   * cut, so a press could be undone -- but the undo is the BOX, which is live on every row now
   * that SELL ALL has stopped locking one. Leaving four pressed buttons on a finished screen just
   * asked the advisor to re-read a decision they had already made.
   *
   * 🟢 They all come back the moment the request goes short again, because this reads
   * `unappliedValue` live rather than latching when it balanced.
   */
  readonly distributionSatisfied = computed(
    () => this.isDistribution() && this.unappliedValue() <= EPS,
  );

  /**
   * Whether to draw the share calculator on a row.
   *
   * 🔴 ALWAYS, ON EVERY TYPE BUT A DISTRIBUTION. "Sell $5,000 of this" is a real instruction on a
   * reallocation -- partial sells are priced by proportion there -- and pressing SELL ALL must not
   * take the way back. The distribution hides it on any row a button has decided, and everywhere
   * once the request is covered, because that screen has a finish line and offering a third way to
   * add to a finished request is noise.
   *
   * ⚠️ The component still asks `canCalculateShares` as well -- this is the request-level rule,
   * that is the row-level one (a price is needed to divide an amount into shares).
   */
  showCalculatorOnRow(index: number): boolean {
    if (!this.isDistribution()) return true;
    if (this.distributionSatisfied()) return false;
    return this.rowFill(index) === 'none';
  }

  /**
   * Whether a ticked cash row is still missing its amount.
   *
   * 🔴 `cashRowNeedsAmount` LIVED HERE AND IS GONE (AndyP, 2026-09-25), for the same reason as
   * `needsShares` on a position: `use` is now DERIVED from the amount, so a row cannot be in use
   * and empty at the same time. The red border it drove went with it.
   */

  /** Which fill button, if any, is pressed on a position row. */
  rowFill(index: number): PositionFillMode {
    this.formVersion();
    return this.positionsForm.at(index)?.controls.fill.value ?? 'none';
  }

  /**
   * What this row could still raise -- its unsold shares at their price.
   *
   * ⚠️ UNSOLD, not the whole market value. Once shares are marked, that value is already counted
   * in `unappliedValue`; measuring against the full position would offer "sell for remaining" on
   * a row that cannot actually reach it.
   *
   * 🟢 Zero without a price, which is what routes an unpriced row to SELL ALL -- see
   * {@link rowCoversRemaining}.
   */
  rowHeadroomValue(index: number): number {
    this.formVersion();
    const p = this.positions()[index];
    if (!p || (p.price ?? 0) <= 0) return 0;
    const marked = this.positionGroup(index)?.controls.sharesToSell.value ?? 0;
    return round2(Math.max(0, (p.shares ?? 0) - marked) * (p.price as number));
  }

  /**
   * Whether this row alone could cover what is outstanding, with room to spare.
   *
   * 🔴 FALSE WITHOUT A PRICE, and that is the branch cash does not need. SELL FOR REMAINING has
   * to divide the outstanding amount by something; Orion prices some holdings at null, and those
   * are sold whole or not at all -- which is why `canSellWholeRow` needs no price while
   * `canCalculateShares` does. An unpriced row therefore falls through to SELL ALL regardless of
   * what it is worth.
   */
  rowCoversRemaining(index: number): boolean {
    if (!this.isDistribution()) return false;
    const p = this.positions()[index];
    if (!p?.sellable || (p.price ?? 0) <= 0) return false;
    const outstanding = this.unappliedValue();
    if (outstanding <= EPS) return false;
    return outstanding < this.rowHeadroomValue(index) - EPS;
  }

  /**
   * Whether this row offers a sell button at all.
   *
   * ⚠️ NO LONGER DISTRIBUTION-ONLY (AndyP, 2026-09-25). `distributionSatisfied` is false on every
   * other type by construction, so the quiet-once-covered rule below stays a distribution rule
   * without needing to say so: nothing else has a finish line to reach.
   *
   * 🟢 A row with no share count still gets nothing. Those are sold whole or not at all, so SELL
   * ALL would write the `?? 0` fallback and light up over a row it had not changed.
   */
  private rowHasSellButton(index: number): boolean {
    const p = this.positions()[index];
    if (!p?.sellable || (p.shares ?? 0) <= 0) return false;
    return !this.distributionSatisfied();
  }

  /**
   * Whether to draw SELL ALL on a row.
   *
   * 🔴 ALWAYS, ON EVERY TYPE BUT A DISTRIBUTION. It is the only button those rows have, and it is
   * what replaced the tick -- so hiding it under any condition would take the row's one control
   * away. The distribution keeps its pair, where SELL ALL and SELL FOR REMAINING are complements
   * of one predicate and exactly one is on screen.
   *
   * 🟢 Pressed keeps it on screen -- see the cash equivalent.
   */
  showSellAllOnRow(index: number): boolean {
    if (!this.rowHasSellButton(index)) return false;
    if (!this.isDistribution()) return true;
    if (this.isRowSellAll(index)) return true;
    if (this.rowFill(index) !== 'none') return false;
    // 🟢 Drawn even once the request is covered. `rowCoversRemaining` is false with nothing
    // outstanding, so this is the branch a satisfied row lands in.
    return !this.rowCoversRemaining(index);
  }

  /**
   * Whether to draw the cover-the-remainder button. The complement of {@link showSellAllOnRow},
   * and DISTRIBUTION-ONLY.
   *
   * 🔴 IT HAS NOTHING TO AIM AT ANYWHERE ELSE, which is why it is not simply "not built yet" for
   * a reallocation. `unappliedValue` there is what is left to PLACE, and selling ENLARGES the
   * pool -- so a button that sold enough to cover it would drive the figure away from zero. The
   * direction is reversed, not the layout.
   */
  showSellRemainingOnRow(index: number): boolean {
    if (!this.isDistribution() || !this.rowHasSellButton(index)) return false;
    const fill = this.rowFill(index);
    if (fill === 'remaining' || fill === 'one') return true;
    if (fill === 'all') return false;
    return this.rowCoversRemaining(index);
  }

  /**
   * What the cover-the-remainder button should SAY.
   *
   * 🔴 "SELL 1 SHARE" when a single share costs more than is outstanding, because that is what
   * pressing it will do. Labelling that "SELL FOR REMAINING" promises a figure the row cannot
   * land on -- and before this the button simply did nothing at all, since `floor` returned 0
   * ($14.00 left against a $15.53 share, on RYLD).
   *
   * ⚠️ Reads the STORED mode once pressed, not the arithmetic, so the label does not change under
   * the cursor the moment the press consumes the remainder that chose it.
   */
  rowSellsOneShare(index: number): boolean {
    const fill = this.rowFill(index);
    if (fill === 'one') return true;
    if (fill !== 'none') return false;
    const p = this.positions()[index];
    if (!p || (p.price ?? 0) <= 0) return false;
    return Math.floor(Math.max(0, this.unappliedValue()) / (p.price as number)) === 0;
  }

  /**
   * SELL FOR REMAINING — enough shares to cover what is outstanding, or back to nothing if it is
   * already pressed.
   *
   * 🔴 UP TO BUT NEVER OVER -- EXCEPT THAT ONE SHARE IS THE FLOOR. `Math.floor` normally lands
   * the proceeds at or under the outstanding amount, which is why this is not a "done" button:
   * the last few dollars come from cash, or from nudging this box up by one. NO box is locked on
   * this screen any more -- SELL ALL stopped locking its own, for the reasons in `setRowSellAll`.
   *
   * ⚠️ BUT `floor` RETURNS 0 WHEN ONE SHARE COSTS MORE THAN IS OUTSTANDING, and that made the
   * button do nothing at all while still lighting up as pressed -- $14.00 left against a $15.53
   * share, seen on RYLD (AndyP, 2026-09-24). Selling one share overshoots by $1.53 and finishes
   * the request; selling none leaves the advisor pressing a button that visibly does nothing.
   *
   * 🟢 Overshooting is only acceptable because a distribution may now raise more than it pays
   * out -- the surplus stays in the account as cash. Under the old exact-match rule this floor
   * would have produced a request that could not be submitted.
   *
   * ⚠️ ADDS TO WHAT IS MARKED. `unappliedValue` is already net of this row's current shares, so
   * replacing rather than adding would give back whatever was already marked here.
   */
  setRowSellRemaining(index: number): void {
    const group = this.positionsForm.at(index) as PositionForm | undefined;
    const p = this.positions()[index];
    if (!group || !p?.sellable || (p.price ?? 0) <= 0) return;

    if (group.controls.fill.value === 'remaining' || group.controls.fill.value === 'one') {
      group.controls.sharesToSell.setValue(this.restingFill(), { emitEvent: false });
      group.controls.selected.setValue(false, { emitEvent: false });
      group.controls.fill.setValue('none', { emitEvent: false });
      this.formVersion.update((v) => v + 1);
      return;
    }

    const marked = group.controls.sharesToSell.value ?? 0;
    const outstanding = Math.max(0, this.unappliedValue());
    const whole = Math.floor(outstanding / (p.price as number));
    // One share is the floor -- see the note above. Which of the two it was is recorded, so the
    // label stays honest after the press.
    const extra = Math.max(1, whole);
    const mode: PositionFillMode = whole === 0 ? 'one' : 'remaining';
    const next = Math.min(p.shares ?? 0, marked + extra);

    // Nothing left in this row to add -- leave the button unpressed rather than lighting it up
    // over a figure it did not change.
    if (next <= marked) return;

    group.controls.sharesToSell.setValue(next, { emitEvent: false });
    group.controls.selected.setValue(next > 0, { emitEvent: false });
    group.controls.fill.setValue(mode, { emitEvent: false });
    this.formVersion.update((v) => v + 1);
  }

  /**
   * Whether a row is selling its whole position -- what SELL ALL renders as pressed.
   *
   * 🔴 READ OFF THE FIGURE, NOT THE `fill` FLAG (AndyP, 2026-09-25). The flag is written only by
   * the buttons and CLEARED by typing, so a box holding the whole lot for any other reason showed
   * an unpressed SELL ALL above it: the share calculator landing on the full lot, or the advisor
   * simply typing it. On a distribution that took a hand-typed fraction to hit and almost never
   * happened; on a reallocation, typing the whole lot IS the common act, so it would have been
   * visible on the first screen.
   *
   * 🔴 AND IT DELEGATES RATHER THAN RE-DERIVING. This first shipped as its own share comparison,
   * which is the same question {@link PositionView.isSellAll} already answers for the PAYLOAD --
   * `TradeAction` 3 rather than 2 -- and for the Sell All label on Review. The copy left out the
   * `shares == null` branch, so a holding Orion reports no share count for went to the trade team
   * as Sell All while the button above it read as unpressed, and `allPositionsSellingAll` could
   * never come true on an account holding one (AndyP, 2026-09-25).
   *
   * ⚠️ So the button, the bulk toggle, the payload and Review now cannot disagree about what
   * "selling everything" means. Any change to that rule belongs in `positions()`.
   */
  isRowSellAll(index: number): boolean {
    this.formVersion();
    return this.positions()[index]?.isSellAll ?? false;
  }

  toggleSellAll(checked: boolean): void {
    const positions = this.positions();
    this.positionsForm.controls.forEach((group, i) => {
      if (positions[i]?.sellable && group.controls.selected.value !== checked) {
        group.controls.selected.setValue(checked);

        // 🔴 SELL ALL MEANS THE WHOLE LOT, on every type, and on a distribution that has to be
        // said explicitly. Rows there rest at 0 so the wizard never assumes the advisor wants a
        // whole position -- but this control is not an assumption, it is an instruction, and
        // without this it ticked every row and sold nothing, leaving a screen of red.
        //
        // 🟢 Only rows whose state actually CHANGED are touched, per the guard above, so a row
        // already ticked with a partial figure the advisor typed is left alone.
        if (checked) {
          group.controls.sharesToSell.setValue(positions[i]?.shares ?? 0, { emitEvent: false });
        }
      }
    });
    this.formVersion.update((v) => v + 1);
  }

  /**
   * Whether step 1 has anything on it to undo — any position marked to sell, or any cash
   * released. Drives the Reset button's disabled state.
   *
   * ⚠️ Reads the CONTROLS rather than the derived rows, because that is what Reset writes to
   * and the two must agree about whether there is anything to write. Through `formVersion`,
   * since a FormArray is not a signal.
   */
  readonly stepOneHasEntries = computed(() => {
    this.formVersion();
    return this.positionsForm.controls.some((g) => g.controls.selected.value)
      || this.cashForm.controls.some((g) => g.controls.use.value)
      // The freed amount is a step-1 entry like any other, and it is NOT in a form -- see
      // `modelFreeAmountSignal`. Reset sitting disabled over a filled-in box is the whole bug.
      || this.modelFreeAmount() > 0;
  });

  /**
   * Put step 1 back to how it loaded: nothing marked to sell, no cash released.
   *
   * 🟢 Unticking is all it takes, and that is by design rather than luck — each row's `selected`
   * / `use` subscription already disables its amount and restores it to the full lot or the
   * whole balance. So this cannot drift from what unticking a row by hand does, because it IS
   * unticking every row.
   *
   * ⚠️ It deliberately does NOT touch anything outside step 1 — protected cash, buys, what the
   * advisor has stated on Allocate to Models. "Reset" sitting on one step has to mean that step,
   * or an advisor clearing a couple of sell ticks loses work on three others. Starting over
   * entirely is what closing the request without submitting is for.
   *
   * ⚠️ IT DOES MOVE THE MODEL POT, through `clearModelFreeAmount`, and that is not an
   * exception to the paragraph above (ACMTECH-1039). Freeing cash is a step-1 control whose
   * entire effect is expressed over there: putting the balance back into the models IS the
   * undo, and leaving the pot short would keep the money freed with nothing on screen still
   * saying so. The target column is not touched, here or anywhere on that path.
   */
  resetStepOne(): void {
    this.toggleSellAll(false);
    this.toggleUseAllCash(false);
    this.clearModelFreeAmount();
  }

  toggleDnt(ticker: string, marked: boolean): void {
    const next = new Set(this.dnt());
    if (marked) next.add(ticker);
    else next.delete(ticker);
    this.dnt.set(next);
  }

  toggleDntAll(checked: boolean): void {
    // The merged list, matching what the tab renders — otherwise select-all skips
    // bought securities and the header checkbox never reaches "all".
    const eligible = this.dntCandidates();
    const next = new Set(this.dnt());
    for (const p of eligible) {
      if (checked) next.add(p.ticker);
      else next.delete(p.ticker);
    }
    this.dnt.set(next);
  }

  // --------------------------------------------------------------------------
  // Step 2: buy mutations
  // --------------------------------------------------------------------------
  addBuyRow(): void {
    this.buyRows.update((rows) => [
      ...rows,
      { id: `buy-${++this.buyRowSeq}`, security: null, shares: 0, manualAmount: null, pendingTerm: '' },
    ]);
  }

  removeBuyRow(id: string): void {
    this.buyRows.update((rows) => rows.filter((r) => r.id !== id));
  }

  /**
   * Attach a picked security to a row. Carries the whole option — including `price` and
   * `priceAsOf` — rather than just an id, because the estimate and the future
   * write-back both need the price that was current when the advisor chose.
   *
   * ⚠️ Clears whichever figure the row is no longer entered by. Picking a listed security
   * after hand-typing one drops the typed amount — the row is priced now, so the amount
   * is derived from shares and keeping the old number would leave two figures on one row
   * that disagree. Going the other way drops the shares for the same reason.
   */
  selectBuySecurity(id: string, security: SecurityOption | null): void {
    const becomesManual = !!security && security.securityId == null;
    this.buyRows.update((rows) =>
      rows.map((r) => {
        if (r.id !== id) return r;
        return {
          ...r,
          security,
          shares: becomesManual ? 0 : r.shares,
          manualAmount: becomesManual ? r.manualAmount : null,
          // Resolved one way or the other, so there is no unfinished text left to carry.
          pendingTerm: security ? '' : r.pendingTerm,
        };
      }),
    );
  }

  /**
   * Remember text the advisor typed into the picker and left without choosing anything.
   *
   * Called only as the picker CLOSES, never per keystroke — see {@link BuyRow.pendingTerm}.
   * Keeping it is what lets the row say "this is not finished" instead of the picker
   * deciding on the advisor's behalf: the text is still in the box when they come back,
   * and {@link incompleteBuys} holds Next until they pick from the list or add it manually.
   */
  setBuyPendingTerm(id: string, term: string): void {
    const trimmed = term.trim();
    this.buyRows.update((rows) => rows.map((r) => (r.id === id ? { ...r, pendingTerm: trimmed } : r)));
  }

  /**
   * Accept a ticker the advisor typed that no search result matched.
   *
   * Deliberately carries no id, price or description — inventing any of them would make
   * an unverified row indistinguishable from a resolved one downstream, which is the
   * whole risk here. The null id is what flags it for the trade team.
   *
   * Uppercased and trimmed so `AAPL`, `aapl ` and ` Aapl` are one ticker: the sell
   * conflict check and the resulting-holdings merge both key off it when there is no id.
   */
  enterManualBuySecurity(id: string, ticker: string): void {
    const normalized = ticker.trim().toUpperCase();
    if (!normalized) return;
    this.selectBuySecurity(id, {
      securityId: null,
      // ⚠️ Null, and NOT a stand-in for "custom". CRM decides that, from this row reaching it
      // with no security id -- see ACMTECH-976. Inventing a type here would make a row nobody
      // has verified indistinguishable from one Orion typed, which is the whole risk the null
      // `securityId` exists to carry.
      securityTypeId: null,
      ticker: normalized,
      description: '',
      price: null,
      priceAsOf: null,
    });
  }

  setBuyShares(id: string, shares: number): void {
    const safe = Number.isFinite(shares) && shares > 0 ? shares : 0;
    this.buyRows.update((rows) => rows.map((r) => (r.id === id ? { ...r, shares: safe } : r)));
  }

  /**
   * The advisor's own dollar figure for a hand-typed ticker.
   *
   * Null rather than 0 when cleared, so an emptied box stays empty while it is being
   * retyped — the same reason step 1's shares-to-sell control is nullable.
   *
   * ⚠️ ROUNDED here, which is the counterpart to `normalizeCash` on the protected-cash box.
   * This figure is `estimatedAmount` verbatim for a hand-typed row, so it reaches `investable`
   * through `totalBuyValue` and goes to CRM as `estimatedValue` — and a dollar amount carrying
   * three decimal places is wrong on the wire whatever the arithmetic does with it. The
   * priced branch of `estimatedAmount` has always rounded; this one only looked as though it
   * did, because a comment there said so.
   *
   * ⚠️ Not the same job as `appTwoDecimals` on the input, which only formats what is on
   * screen. A figure can reach this method without passing through a blur, so the guarantee
   * has to live here.
   */
  setBuyAmount(id: string, amount: number | null): void {
    const rounded = amount != null && Number.isFinite(amount) ? round2(amount) : null;
    const safe = rounded != null && rounded > 0 ? rounded : null;
    this.buyRows.update((rows) => rows.map((r) => (r.id === id ? { ...r, manualAmount: safe } : r)));
  }

  // --------------------------------------------------------------------------
  // Step 4: allocation mutations (pure rebalancing, ported from the mockup)
  // --------------------------------------------------------------------------
  selectModel(id: string, modelId: string): void {
    this.updateRow(id, (r) => ({ ...r, modelId }));
  }

  /**
   * What picking `modelId` demands before it can be committed to a row.
   *
   * Lives in the STORE, with the copy, because every input to the decision is store state —
   * the model's `isSma`, its minimum, the account total, and what work would be lost. The
   * component only owns the dialog.
   *
   * ⚠️ A model with no minimum on record clears this unconditionally, because
   * {@link minimumFor} reports it as 0. There used to be a third outcome here -- blocked,
   * "no minimum is on record, check with the trade team" -- and it is gone by the same
   * decision that removed the row-level error. An SMA whose minimum Connect does not carry
   * is now treated as having none.
   *
   * ⚠️ The comparison is against `totalValue()` — the WHOLE account — not the row's
   * allocated value as `belowMinimum` uses. An SMA takes the account, so the account total
   * is what has to clear its minimum; the row's figure is a fraction of it, and comparing
   * that would refuse accounts that qualify.
   */
  smaSelectionCheck(modelId: string): SmaSelectionCheck {
    const model = this.modelOption(modelId);
    if (!model?.isSma) return { kind: 'none' };

    const minimum = this.minimumFor(modelId);
    const total = this.totalValue();
    // `>=` — an account worth exactly the minimum clears it.
    if (total + EPS < minimum) {
      return {
        kind: 'blocked',
        message: `${model.name} requires a minimum investment of ${formatMoney(minimum)}. This `
          + `account's total value is ${formatMoney(total)}, so it does not meet the minimum `
          + 'for this model.',
      };
    }

    return { kind: 'confirm', ...this.smaConfirmationCopy(model.name) };
  }

  /**
   * The confirmation copy, naming the cost of confirming only when there is one.
   *
   * The advisor may be several steps into a request they are about to lose; a request with
   * nothing in it yet should not be warned about losing nothing.
   */
  private smaConfirmationCopy(modelName: string): { message: string; question: string } {
    const sells = this.soldPositions().length;
    const buys = this.buys().filter((b) => !b.isOrphan).length;
    const cash = this.cashInput();

    const losses: string[] = [];
    if (sells > 0) losses.push(`${sells} ${sells === 1 ? 'position' : 'positions'} marked to sell`);
    if (buys > 0) losses.push(`${buys} ${buys === 1 ? 'purchase' : 'purchases'}`);
    if (cash > EPS) losses.push(`${formatMoney(cash)} of protected cash`);

    const lost = losses.length ? ` This will clear ${joinPhrases(losses)}, and cannot be undone.` : '';

    return {
      // ⚠️ "cannot", "or assets", "are executed by" (2026-09-16 feedback). Three small edits,
      // and the second is the substantive one: the old wording said an SMA cannot be commingled
      // with other MODELS, which is narrower than the rule -- it cannot be commingled with other
      // assets either, and this dialog is the one place the advisor is told so before committing.
      message: 'Separately Managed Accounts (SMAs) are 100% of the account and cannot be '
        + 'commingled with other models or assets. All buys and sells are executed by an '
        + 'external manager. This strategy requires a custodial Limited Power of Attorney (LPOA) '
        + 'on file for the client.'
        + lost,
      question: `Do you confirm that you want to position all assets in this account to ${modelName}?`,
    };
  }

  /**
   * The copy for REMOVING an SMA row -- the explanation and the question it leads to -- or null
   * when the row needs no question at all.
   *
   * ⚠️ The reachable case is an account that ALREADY holds an SMA -- a pre-loaded sleeve, which
   * only became loadable in ACMTECH-956. A just-selected SMA cannot get here: `commitToSma`
   * sets the latch, and `canJumpTo` then refuses every step but Review, so Allocate to Models
   * is unreachable with an uncommitted SMA row on it.
   *
   * 🟢 Which is why the copy talks about the LPOA rather than about what confirming will clear.
   * Entering an SMA destroys the request built so far, and {@link smaConfirmationCopy} counts
   * that cost; leaving one destroys nothing -- the survivors keep their dollars (see
   * {@link removeRow}) -- but it does need the custodial paperwork to exist. Different
   * consequence, so different sentence, under the same heading.
   *
   * `string | null` rather than the three-way {@link SmaSelectionCheck}: a removal can be
   * questioned or not, and there is no third answer. Nothing blocks it -- an account cannot be
   * trapped in a strategy by this wizard, and the minimum that gates ENTERING an SMA has no
   * bearing on leaving it.
   *
   * ⚠️ Silent when the row's model does not resolve to a CRM model, because `isSma` lives only
   * on CRM's record and there is no other authority for it (see `ModelOption.isSma`). A sleeve
   * that failed to resolve renders "Not in your model list -- remove this row", and removing it
   * asks nothing. Unavoidable rather than intended: with no model there is no flag to read.
   */
  smaRemovalPrompt(rowId: string): { message: string; question: string } | null {
    const row = this.allocationRows().find((r) => r.id === rowId);
    const model = row ? this.modelOption(row.modelId) : undefined;
    if (!model?.isSma) return null;

    return {
      // ⚠️ "require", not "are", on the removal side -- the sentence has to work for a strategy
      // being taken OFF the account, where "are 100%" states a present fact that is about to stop
      // being true. Otherwise kept word for word with the add dialog above: one rule, described
      // once, so an advisor reading both does not have to work out whether they differ.
      message: 'Separately Managed Accounts (SMAs) require 100% of the account and cannot be '
        + 'commingled with other models or assets. All buys and sells are executed by an '
        + 'external manager. The removal of this strategy requires a custodial Limited Power of '
        + 'Attorney (LPOA) on file for the client.',
      question: `Do you confirm that you want to remove ${model.name} from management of this account?`,
    };
  }

  /**
   * The SMA sleeve this account arrived holding and still has on the grid, or null.
   *
   * ⚠️ Read off the GRID, not off `data().sleeves`, and that is the whole design. The lock this
   * drives has to release the moment the advisor removes the row, which the loaded sleeve list
   * cannot express -- it is deliberately the immutable "what arrived" picture (see
   * {@link hasCurrentModelSleeves}). `resetAllocation` rebuilds the rows from the sleeves, so
   * the row comes back and the lock re-engages, which is correct.
   *
   * `orionConnectAccountId != null` is the established discriminator for "this row came from a
   * sleeve" -- the same one {@link modelSetChanged} uses and the template gates the read-only
   * model cell on. It is what keeps this off a row the advisor added.
   *
   * ⚠️ Silent when the sleeve's model does not resolve, because `isSma` lives only on CRM's
   * record and there is no other authority for it (see `ModelOption.isSma`). Such a row renders
   * "Not in your model list -- remove this row". Unavoidable rather than intended, and the same
   * gap {@link smaRemovalPrompt} has.
   */
  readonly currentSmaRow = computed<AllocationRow | null>(
    () => this.allocationRows().find(
      (r) => r.orionConnectAccountId != null && this.modelOption(r.modelId)?.isSma,
    ) ?? null,
  );

  /**
   * Whether the request is locked down to Allocate to Models by an SMA already on the account.
   *
   * An SMA is 100% of the account and cannot be commingled, so while one is on the grid there is
   * nothing to sell, buy or hold back as cash -- and building any of that would produce a request
   * the trade team cannot action. The advisor's way out is removing the SMA row, which asks for
   * confirmation of its own ({@link smaRemovalPrompt}).
   *
   * ⚠️ NOT the same fact as {@link smaCommitted}, and the two must not be merged. That latch means
   * "the advisor switched this request INTO an SMA mid-flight", and it already pins them to Review
   * from the other direction. This one means "the account was already in one when it loaded".
   * A row created by `commitToSma` carries no `orionConnectAccountId`, so the two cannot both fire
   * on the same row.
   */
  readonly smaLockActive = computed(() => this.currentSmaRow() !== null);

  /**
   * The account's own SMA sleeve, read from the LOADED sleeve list rather than the grid.
   *
   * 🔴 THE GRID CANNOT ANSWER THIS ON A CONTRIBUTION OR A DISTRIBUTION. {@link currentSmaRow}
   * reads `allocationRows`, and both move-money types set that list to empty and use
   * `sleeveAmountRows` instead -- so the existing detection searches an empty array and never
   * fires. That was the confirmed gap on ACMTECH-994: an SMA account could file a contribution,
   * a distribution or a close with nothing stopping it and nothing explained.
   *
   * ⚠️ READING THE SLEEVES IS RIGHT HERE AND WOULD BE WRONG THERE. A reallocation's lock has to
   * RELEASE the moment the advisor removes the SMA row, which only the grid can express; the
   * loaded sleeve list is deliberately the immutable "what arrived" picture. Neither move-money
   * type has a grid to remove anything from, so "what arrived" is the whole question.
   *
   * ⚠️ SUBJECT TO A KNOWN GAP, unchanged by this. CRM's `inv.InvestmentAccountModel.IsSMA` is
   * the only authority -- Eclipse's `isSMA` is unpopulated and Connect's `hasSMAModel` answers a
   * different question -- so a sleeve whose model does not resolve to a CRM model cannot be
   * detected at all. See `ModelOption.isSma`.
   */
  readonly accountSmaSleeve = computed(() => {
    const data = this.data();
    if (!data) return null;
    const modelFor = buildModelLookup(data.models, data.allModels);
    return (data.sleeves ?? []).find((sleeve) => modelFor(sleeve)?.isSma) ?? null;
  });

  /**
   * Whether a contribution or distribution is running on an account that is already an SMA.
   *
   * 🔴 IT CLOSES ONE STEP, NOT THE REQUEST. Moving money into or out of an SMA is a perfectly
   * ordinary act -- the external manager invests a contribution and liquidates for a distribution
   * -- so the request itself is legitimate and must remain fileable. What cannot happen is
   * COMMINGLING: hand-picking securities to buy alongside the SMA, or hand-picking positions to
   * sell out of it. Exactly one step does that on each type (AndyP, 2026-09-25).
   *
   * 🔴 IT MUST NEVER REACH {@link submitBlockers}, and this is the trap the existing
   * `smaLockActive` doc warns about from the other side. There, "already on an SMA" means nothing
   * has changed and Submit is correctly blocked until the row is removed. Here the request IS the
   * change. Wiring this into Submit would block the very request the advisor came to make.
   *
   * 🟢 Safe by construction today: `smaLockActive` reads `currentSmaRow`, which reads a grid
   * that is empty on these types, so it stays false and Submit is untouched. That is a property
   * worth not breaking rather than a coincidence to rely on quietly.
   */
  readonly moveMoneySmaLock = computed(
    () => this.isMoveMoney() && this.accountSmaSleeve() !== null,
  );

  /**
   * Whether the STEP ROW is locked down — by either route into an SMA.
   *
   * ⚠️ Deliberately wider than {@link smaLockActive} and deliberately narrower than merging the
   * two. An SMA is 100% of the account whichever way the request arrived at one, so the steps
   * that build sells, buys and protected cash are meaningless in both cases and should look it.
   *
   * ⚠️ IT MUST NOT REPLACE {@link smaLockActive} in {@link submitBlockers}, and that is the whole
   * reason this is a third signal rather than a widened one. Those two states differ on exactly
   * one question — is there a request to file?
   *
   *   already on an SMA   nothing has changed, so Submit is blocked until the row is removed
   *   just selected one   that selection IS the request, so Submit must work
   *
   * Widening `smaLockActive` would block Submit on the very request the advisor came to make.
   */
  readonly smaStepLock = computed(() => this.smaLockActive() || this.smaCommitted());

  /**
   * The 1-based steps the SMA lock closes, for the indicator to dim.
   *
   * Everything except Allocate to Models -- where the advisor removes the SMA -- and Review &
   * Submit, which stays open because `canSubmit` already refuses a request with no changes, so
   * nothing empty can be filed from it.
   *
   * Derived from `steps()` rather than hardcoded, so a flag-gated step that is not in this
   * configuration contributes nothing (`stepIndex` returns 0 for an absent step).
   */
  readonly smaLockedSteps = computed<ReadonlySet<number>>(() => {
    // 🔴 A CONTRIBUTION OR DISTRIBUTION CLOSES ONE STEP, NOT EVERYTHING BUT TWO. The rule below
    // keeps Allocate to Models open because that is where the advisor removes the SMA -- and
    // NEITHER move-money type has that step. Applying it there would close everything except
    // Review and land the advisor on a page telling them to remove a model with no screen on
    // which to do it. ACMTECH-994 named that as the reason the reallocation's lock could not
    // simply be switched on.
    //
    // ⚠️ So this names the step that would COMMINGLE, and only that one:
    //
    //   distribution   positions   hand-picking what to sell out of the SMA
    //   contribution   buys        hand-picking what to buy alongside it
    //
    // Everything else on those flows is about the SMA sleeve itself -- the amount, where it goes
    // -- which is exactly what the external manager acts on. Funding, Sleeve Amounts, Set-Aside
    // Cash and Review all stay open, and the request files normally (AndyP, 2026-09-25).
    //
    // 🟢 A CLOSE ACCOUNT NEEDS NO BRANCH. Its step list is Funding then Review, so
    // `stepIndex('positions')` returns 0 and the set comes out empty on its own. Liquidating an
    // SMA entirely is not commingling.
    if (this.moveMoneySmaLock()) {
      const commingling = this.stepIndex(this.isDistribution() ? 'positions' : 'buys');
      return commingling > 0 ? new Set([commingling]) : new Set<number>();
    }

    if (!this.smaStepLock()) return new Set<number>();
    const open = new Set([this.stepIndex('allocation'), this.stepIndex('review')]);
    return new Set(
      this.steps().map((_, i) => i + 1).filter((step) => !open.has(step)),
    );
  });

  /**
   * Why the request is locked, for the dialog the advisor gets from the dimmed controls.
   *
   * In the store with the rest of the SMA copy rather than in a component, because two surfaces
   * show it -- the Add Model button and the dimmed steps in the indicator -- and the same words
   * have to reach both. Empty string when there is no lock, so a caller cannot show it by mistake.
   */
  smaLockMessage(): string {
    // 🔴 THE SECOND PARAGRAPH IS WRONG ON A MOVE-MONEY REQUEST, which is why this returns before
    // the row lookup rather than after it. "The SMA model must be removed before you can add or
    // remove other models or assets" describes an action on the allocation grid, and neither a
    // contribution nor a distribution has one -- so it would tell the advisor to do something
    // there is no screen for.
    //
    // ⚠️ AND THERE IS NOTHING TO PUT IN ITS PLACE, deliberately. On a reallocation the lock is
    // a state the advisor can leave; here it is simply what the account IS. The step does not
    // apply, the rest of the request works, and saying more would imply a remedy that does not
    // exist (AndyP, 2026-09-25).
    //
    // 🟢 It also has no row to find -- `currentSmaRow` reads the grid -- so falling through
    // would return '' and the dialog would open blank.
    if (this.moveMoneySmaLock()) return SMA_COMMINGLE_RULE;

    // ⚠️ Either route, matching {@link smaStepLock}: the dimmed steps now appear for a
    // just-selected SMA too, and a dialog that opened blank would be worse than no dialog. A
    // row created by `commitToSma` carries no `orionConnectAccountId`, so `currentSmaRow` alone
    // does not see it.
    const row = this.currentSmaRow()
      ?? this.allocationRows().find((r) => this.modelOption(r.modelId)?.isSma)
      ?? null;
    // ⚠️ The row is still looked up even though the copy no longer names it: returning '' when
    // there is no lock is what stops a caller showing this dialog by mistake.
    if (!row) return '';

    // ⚠️ THE SAME FIRST SENTENCE as the add dialog, the remove dialog and the review page's
    // panel -- four surfaces, one rule, described once. It used to open "This account is
    // currently set up as a separately managed account (Van Hulzen SMA), which…", which was the
    // only one of the four phrased as a fact about THIS account rather than about SMAs, and the
    // only one saying "handled by" where the rest say "executed by".
    //
    // ⚠️ THE MODEL NAME IS THEREFORE GONE from this dialog, and nothing else in it carries one:
    // both callers title it "This account is a Separately Managed Account". Losing it costs
    // little here -- the advisor is looking at the grid row that caused the lock -- but it is a
    // real difference from the two confirm dialogs, which name the model in their question.
    return SMA_COMMINGLE_RULE
      // Second paragraph, unlike the other three: this dialog answers "why can't I do this?"
      // rather than "do you confirm?", so it has to say what would unblock them. Reallocation
      // only -- see the move-money branch at the top of this method.
      + '\n\nThe SMA model must be removed before you can add or remove other models or assets.';
  }

  /**
   * Switch the whole request to a single SMA model, then send the advisor to Review.
   *
   * ONE method, because it has to be atomic: an SMA is the whole account, so a half-cleared
   * request — sells gone but purchases still listed — is a state no step could describe.
   */
  commitToSma(modelId: string): void {
    // Sells. Rows the advisor cannot sell are already held at false, so this skips them.
    this.toggleSellAll(false);
    this.buyRows.set([]);
    // Set directly: the grid is replaced wholesale below, so re-stating the pot is wasted.
    this.modelFreeAmountSignal.set(0);
    // EMITTING, deliberately: `cashInput` reads through `formVersion`, which only advances
    // off the control's value stream. A silent write would leave the strip on the old figure.
    this.cashControl.setValue(0);
    // Inert while the Do Not Trade step is flagged off, but the state exists, and an
    // attestation collected against positions that no longer exist would be a lie.
    this.dnt.set(new Set<string>());
    this.setDntConfirmed(false);
    this.files.set([]);
    this.requestNotes.set('');

    // The WHOLE account, which is what an SMA is. Nothing is kept back: `sleevePositions`
    // empties the moment the latch is set, so Custom Holdings goes to zero and this figure
    // is the only place the account's value lives.
    //
    // Read directly rather than through `investable()`, which returns the same number but
    // only once the latch below is set.
    const funded = this.totalValue();

    // The latch goes up AFTER the clears and BEFORE the row: after, so nothing can observe
    // an account that has already given up its Custom Holdings while a sell or a buy is
    // still on the request; before, so the row is created into the world it is funded for.
    this.smaCommitted.set(true);

    // ⚠️ And the sleeve's cash goes with it. An SMA is 100% of the account, so nothing stays in
    // Custom Holdings -- `sleevePositions` empties off the latch, and cash has to say the same
    // thing or `cashKept` would leave a holding behind: a kept-cash line in the payload and a
    // non-zero Custom Holdings term in the strip, on a request that keeps nothing.
    //
    // RELEASED rather than zeroed, because for an SMA the cash really does go to the model.
    // `investable` short-circuits to the account total here, so this adds nothing twice.
    //
    // 🛑 AFTER THE LATCH, and it used to be before. Ticking a cash row now writes that row's
    // ceiling into the amount box, and the ceiling is only the WHOLE balance once the latch has
    // suppressed the cash target -- so releasing first left the target's share behind on a
    // request that keeps nothing, which is the one thing this call exists to prevent.
    //
    // 🟢 Moving it here does not reopen what the latch's ordering protects: that is about a sell
    // or a buy still sitting on a request whose sleeve has already gone, and both are cleared
    // above. Cash is released, not cleared.
    this.toggleUseAllCash(true);

    // A FRESH row, not the selected one edited: it drops every manual/seeded flag along with
    // the rest of the grid. Leaving those false is what makes the row read 100/100/100 —
    // distribution and contribution both read the target, where setting the flags would freeze
    // in whatever figures the replaced row happened to carry.
    this.allocationRows.set([{ ...this.createRow(100), modelId }]);
    // One row at 100% replaces whatever was stated per row.
    this.modelValuesEditedSignal.set(false);
    // ⚠️ The POT takes the whole account, and it has to be set explicitly now that a row's
    // dollars derive from it. Without this the single 100% row would be 100% of nothing.
    this.modelPotSignal.set(funded);

    // `currentStep.set`, NOT `goToStep` — goToStep runs canJumpTo, and canJumpTo is where
    // the lock below now refuses every target but this one.
    this.currentStep.set(this.stepIndex('review'));
  }

  /**
   * The SMA model this request has been switched to, or null when it has not been.
   *
   * Drives the notice on Review & Submit. Reads the latch rather than scanning the rows for
   * an SMA model, so it cannot fire on a dropdown the advisor is still deciding about.
   */
  readonly smaModelName = computed(() => {
    if (!this.smaCommitted()) return null;
    const modelId = this.allocationRows()[0]?.modelId ?? '';
    return modelId ? this.modelName(modelId) || null : null;
  });

  /**
   * Models still available to a row: the advisor's own list, minus any already used by
   * another row, plus this row's current model.
   *
   * ⚠️ That last part is load-bearing. A pre-loaded row can hold a model outside the
   * agent-scoped list, and a `<select>` whose value has no matching `<option>` renders
   * blank — the same empty dropdown the sleeve pre-load exists to avoid, one layer
   * down. The `m.id === current` guard alone cannot save it, because such a model is
   * not in `models` at all, so the current model is appended from the resolution list.
   */
  availableModels(rowId: string): ModelOption[] {
    const data = this.data();
    const models = data?.models ?? [];
    const rows = this.allocationRows();
    const current = rows.find((r) => r.id === rowId)?.modelId ?? '';
    const usedByOthers = new Set(rows.filter((r) => r.id !== rowId && r.modelId).map((r) => r.modelId));
    const selectable = models.filter((m) => m.id === current || !usedByOthers.has(m.id));

    if (!current || selectable.some((m) => m.id === current)) return selectable;

    const resolved = data?.allModels?.find((m) => m.id === current);
    return resolved ? [resolved, ...selectable] : selectable;
  }

  /**
   * Set one model's share of the go-forward target mix.
   *
   * ⚠️ Target % is a share of {@link modelPot} -- what the advisor has put into models -- and
   * NOT of the investable amount. They may not want the whole investable balance in models at
   * all; that is what the pot is for. Where it matters the two agree anyway: Submit will not
   * fire while any of the investable balance is unplaced.
   *
   * **ONE ROW MOVES. That is the rule (ACMTECH-969).**
   *
   * No other row's target is touched, so the column will usually leave 100 and the step will
   * block until the advisor states the rest. That is deliberate. `rebalanceUntouched` used to
   * absorb the remainder into the untouched rows -- at two rows or fewer, which was its own
   * inconsistency -- so a single keystroke silently rewrote a figure the advisor had not looked
   * at. No dollars move here either: they are derived from the pot, which this leaves alone.
   *
   * ⚠️ Distribution and Contribution SNAP BACK onto the target, discarding anything typed by
   * hand -- see {@link clearDerivedSplits}, which is where that reversal is argued.
   *
   * ⚠️ Dollars must never re-derive a target on LOAD. Targets are Orion's own
   * `targetAllocation` and are what Eclipse rebalances toward; deriving them from the values
   * once meant a DRIFTED account had its target quietly rewritten to its drift -- an advisor
   * opening a 70/30 account and submitting filed 71.23/28.77, a number nobody chose, just where
   * the market left it. {@link editValue} does rewrite one, but only because the advisor typed
   * a dollar figure into that row.
   */
  editTarget(id: string, value: number): void {
    this.updateRow(id, (r) => ({
      ...r,
      target: round2(value),
      targetExact: value,
      ...clearDerivedSplits(),
      touched: true,
    }));
  }

  editDistribution(id: string, value: number): void {
    this.editColumn('distribution', id, value);
  }

  editContribution(id: string, value: number): void {
    this.editColumn('contribution', id, value);
  }

  /**
   * State one model's dollars — **the second way of raising cash from the models**.
   *
   * Every other row keeps the dollars it had; the pot re-settles to what the rows now total,
   * and the whole Target % column is re-derived from the new split:
   *
   *     pot      = Σ (other rows' dollars) + typed
   *     target_i = value_i ÷ pot × 100
   *
   * so the column lands on 100 BY CONSTRUCTION and the difference leaves the models. Lower a
   * $20,000 sleeve to $10,000 on a 20/80 account and the other sleeve still holds $80,000,
   * reading 88.89% of a pot that is now $90,000, with $10,000 added to Left to Allocate.
   *
   * 🔴 THE PERCENTAGES MOVE AND THE OTHER ROWS' DOLLARS DO NOT (AndyP, 2026-10-29). That is the
   * whole mechanism: re-weighting the DOLLARS within the pot -- the other rows absorbing what
   * this one gave up -- would release nothing at all, which is the opposite of what this is for.
   *
   * ⚠️ IT USED TO LEAVE THE POT ALONE and re-derive only this row's target, which left the
   * column off 100 and BLOCKED the step. There was no way to raise cash this way: the only
   * route back to 100 was raising another model's target, which put the money straight back.
   * The ACMTECH-969 reasoning that a value edit must not renumber the column still holds for
   * the Target % box, which still moves one row; it does not hold here, because here the
   * renumbering is the account's own arithmetic rather than an inference about intent.
   *
   * ⚠️ REFUSES WHILE THE PRO-RATA BOX HOLDS AN AMOUNT -- see {@link modelValuesEdited} for why
   * the two cannot both be live. The template disables the input, so this is the backstop.
   *
   * 🔴 EVERY ROW WHOSE TARGET MOVED HAS ITS SEEDED SPLITS RE-DERIVED, not just the edited one
   * (AndyP, 2026-10-29) -- and the first cut of this method got it wrong in a way the codebase
   * had already recorded. `editTarget`'s rule note describes the reported bug verbatim: "only the
   * edited row's split followed, the other row's target was moved underneath its own
   * distribution, and the column stopped totalling 100 -- blocking the step on a row the advisor
   * had never touched." ACMTECH-969 answered that by making sure no other row's target moved.
   * This method moves them all, so that answer does not reach here and the bug came back: a
   * $120,000 edit left the survivor reading 20.73% target against a seeded 20.00% distribution,
   * and the two columns totalled 99.27%.
   *
   * ⚠️ SEEDED, NOT MANUAL. A seeded figure is the ACCOUNT's existing split, carried in at load --
   * not a statement about this request, and once the target it described has moved it is merely
   * stale. A MANUAL figure is the advisor's own word and survives, which is the ACMTECH-969
   * principle that a column off 100 should be their arithmetic showing rather than a number the
   * wizard invented. The edited row still clears both, exactly as `editTarget` does.
   *
   * ⚠️ Only where the share actually MOVED. Re-typing a row's existing figure is a no-op, and
   * clearing the flag anyway would snap two untouched columns onto the target for no reason.
   */
  editValue(id: string, value: number): void {
    if (this.isMoveMoney()) return;
    if (this.modelFreeAmount() > EPS) return;
    // ⚠️ STILL REFUSES ON AN EMPTY POT. Re-settling from nothing would quietly make this the
    // one control that can fund the models from zero -- a bigger change than raising cash out
    // of them -- and the template disables the box on the same condition, so the store would
    // be permitting what the screen forbids. The step names the pot in its blocked reason.
    if (this.modelPot() <= EPS) return;

    const view = this.allocation().rows;
    if (view.length === 0) return;

    // Read off the VIEW, not the rows: these are the derived dollars the advisor is looking
    // at, including the cent of drift the last row absorbs. Re-deriving targets from the
    // figures on screen is what keeps the grid from moving under them by a penny.
    const typed = round2(Math.max(0, value));
    const next = new Map(view.map((r) => [r.id, r.id === id ? typed : r.allocatedValue]));
    const pot = round2([...next.values()].reduce((total, v) => total + v, 0));

    this.modelPotSignal.set(pot);
    this.modelValuesEditedSignal.set(true);

    this.allocationRows.set(
      this.allocationRows().map((r) => {
        const share = pot > EPS ? ((next.get(r.id) ?? 0) / pot) * 100 : 0;
        const retargeted = { ...r, target: round2(share), targetExact: share };
        if (r.id === id) return { ...retargeted, ...clearDerivedSplits(), touched: true };

        const moved = Math.abs(share - (r.targetExact ?? r.target)) > EPS;
        return moved
          ? { ...retargeted, distributionSeeded: false, contributionSeeded: false }
          : retargeted;
      }),
    );
  }

  /**
   * Set the opening pot and settle the opening percentages.
   *
   * **The pot opens at what the models hold TODAY** -- Σ of the sleeves' current values -- so
   * the step arrives describing the account as it stands, and whatever else is investable shows
   * as still to place. Moving money into or out of models is then something the advisor does,
   * not something the wizard assumed. Each row's dollars follow from the pot and its target, so
   * they need no seeding of their own.
   *
   * ⚠️ ZERO on an account holding no models, and the step says so rather than guessing. The old
   * implicit pot fell back to `investable` here -- "the one moment sweeping the balance in IS
   * the ask" -- which is the assumption ACMTECH-945 removed everywhere else and ACMTECH-969
   * removes here. `useAllInvestable` is one click away and says what it does.
   *
   * The TARGET column keeps the account's own stated split where it totals 100; where it does
   * not, {@link retargetFromHoldings} derives one from what each sleeve holds. Distribution and
   * Contribution are deliberately not settled -- where they were seeded they are already right,
   * and where they were not the row READS the target and needs no figure written.
   *
   * ⚠️ EVERY caller of `buildAllocationRows` must call this straight afterwards -- the rows come
   * back with no pot and, on the fallback path, TARGET at 0, so a caller that skips it leaves
   * the whole column at 0% and blocks the step. `resetAllocation` did exactly that; see its note.
   */
  private seedOpeningRows(): void {
    const rows = this.allocationRows();

    // ⚠️ From `sleeveCurrentValue`, not from the grid's own dollars -- those are now DERIVED
    // from this very figure, so reading them here would be circular and would open every
    // account at a pot of zero.
    const held = round2(rows.reduce((total, r) => total + (r.sleeveCurrentValue ?? 0), 0));
    this.modelPotSignal.set(Math.max(0, held));

    if (rows.length === 0 || held <= EPS) return;

    const statedTargets = rows.reduce((total, r) => total + (r.targetExact ?? r.target), 0);
    // The account stated a usable split and `buildAllocationRows` has already put it on the
    // rows, so there is nothing left to do: the dollars derive from it.
    if (Math.abs(statedTargets - 100) < EPS) return;

    // No usable stated split, so derive one from what the sleeves actually hold. This is the
    // ACMTECH-945 fallback, kept: it cannot disagree with the account it came from, which is
    // what is wanted when there is nothing trustworthy to seed from.
    this.retargetFromHoldings();
  }

  /**
   * Derive the opening Target % column from what each sleeve actually holds.
   *
   * `target_i = held_i ÷ Σ held × 100`
   *
   * ⚠️ THE FALLBACK PATH ONLY -- for an account whose own stated targets do not total 100 and
   * so cannot be honoured. Where they DO total 100 they are left exactly as they loaded: they
   * are Orion's `targetAllocation`, they are what Eclipse rebalances toward, and deriving them
   * from the dollars instead once meant a DRIFTED account had its target quietly rewritten to
   * its drift -- an advisor opening a 70/30 account and submitting filed 71.23/28.77, a number
   * nobody chose. See {@link seedOpeningRows}.
   *
   * ⚠️ Reads `sleeveCurrentValue`, NOT the grid's own dollars, which are now derived from the
   * pot and these very targets. Deriving the targets from a figure derived from the targets is
   * circular; the sleeve's holding is the one figure outside that loop.
   *
   * Leaves the column alone when the sleeves hold nothing at all. A row holding nothing is 0%
   * of nothing, and writing zeros would wipe out the 100 a first row opens at.
   */
  private retargetFromHoldings(): void {
    this.applyTargetWeights(this.allocationRows().map((r) => r.sleeveCurrentValue ?? 0));
  }

  /**
   * Write the Target % column as each weight's share of their sum, to the cent.
   *
   * The share-and-drift rule, in one place. It was extracted when a second caller derived the
   * column from holdings-less-draws; that caller is gone (see below) and
   * {@link retargetFromHoldings} is the only one left. Kept separate anyway: the rounding rule
   * is worth being able to point at, and the fallback is not the obvious place to find it.
   *
   * ⚠️ Rounding each share to 2dp leaves the column a hundredth out, which would sit red at
   * 99.99 and block Next for a reason nobody could see. The last row absorbs it.
   *
   * Leaves the column alone when the weights come to nothing: a row is 0% of nothing, and
   * writing zeros would wipe out the 100 a first row opens at.
   *
   * 🔴 THE `exact` MODE IS GONE with the per-sleeve draw (2026-10-29). It stored the
   * unrounded share in `targetExact` so a draw came out to the cent: targets were being
   * DERIVED FROM DOLLARS then, and a 2dp target reconstructed $129,294.90 where $129,300.00
   * was meant, landing the $5.10 on a model nobody had touched. Nothing derives targets from
   * dollars any more -- freeing cash leaves the column alone -- so the 2dp split is once
   * again the only behaviour, and the only caller is the fallback below.
   */
  private applyTargetWeights(weights: readonly number[]): void {
    const rows = this.allocationRows();
    if (rows.length === 0) return;

    const sum = weights.reduce((total, v) => total + v, 0);
    if (sum <= EPS) return;

    const shares = weights.map((v) => round2((v / sum) * 100));
    const drift = round2(100 - shares.reduce((total, sh) => total + sh, 0));
    shares[shares.length - 1] = round2(shares[shares.length - 1] + drift);

    this.allocationRows.set(
      rows.map((r, i) => ({ ...r, target: shares[i], targetExact: shares[i] })),
    );
  }

  // ---- Freeing cash from the models (ACMTECH-1039) ---------------------------
  /**
   * Free cash out of the models, or put it back.
   *
   * ⚠️ THE FIGURE IS KEPT AS TYPED, not clamped to {@link modelFreeCap}. An over-cap entry
   * stands in its box, says so, and blocks the step -- the house rule everywhere on step 1,
   * because silently rewriting a number the advisor just typed is how a request comes to say
   * something nobody chose. The cap bites when the figure is APPLIED.
   *
   * Inert for a contribution/distribution, which raise money from sleeves through
   * {@link sleeveAmountRows} on a step of their own.
   */
  setModelFreeAmount(amount: number): void {
    if (this.isMoveMoney()) return;
    // The value boxes own the pot once one of them has been typed into. The template disables
    // this input, so this is the backstop rather than the message.
    if (this.modelValuesEdited()) return;
    this.modelFreeAmountSignal.set(Number.isFinite(amount) ? round2(Math.max(0, amount)) : 0);
    this.applyModelFreeAmount();
  }

  /** Put the whole balance back into the models. */
  clearModelFreeAmount(): void {
    if (this.modelFreeAmountSignal() === 0) return;
    this.modelFreeAmountSignal.set(0);
    // `seedOpeningRows`, not `applyModelFreeAmount`: both restore the pot, but this one also
    // leaves the target column alone whenever it totals 100 -- which is the whole point, and
    // what the advisor typed on Allocate to Models is not this control's to undo.
    this.seedOpeningRows();
  }

  /**
   * Re-state the pot as what the models hold LESS what is being freed.
   *
   * 🔴 THE TARGET COLUMN IS NOT TOUCHED, and that is the entire decision (trade team,
   * 2026-10-29). The money comes out of every sleeve in proportion to its target, so the
   * account's stated allocation survives the cash raise:
   *
   *     pot     = Σheld − F
   *     value_i = pot × target_i        (so each sleeve gives up F × target_i)
   *
   * ⚠️ THIS USED TO RE-DERIVE THE TARGETS, and the difference is the card. Taking the money
   * out of ONE named sleeve meant holding its dollars fixed and letting the percentages fall
   * where they may -- 20/80 became 17.94/82.06 on an account that gave up $5,000 from each
   * model. Targets are investment policy; raising cash is not a reason to restate them.
   *
   * 🟢 So there is nothing to warn the advisor about any more. Moving the pot cannot overwrite
   * a target they typed, which is why `modelDrawEditCheck` and its confirmation are gone.
   */
  private applyModelFreeAmount(): void {
    const pot = round2(this.modelSleeveValue() - this.totalDrawnFromModels());
    this.modelPotSignal.set(Math.max(0, pot));
  }

  /**
   * Whether Allocate Remaining would hand back money Raise Cash just freed.
   *
   * ⚠️ Both halves matter. With draws present but the pot already at or above `investable`,
   * the button is REDUCING the pot -- it re-absorbs nothing, and warning there would train the
   * advisor to click through a dialog that is usually wrong.
   */
  allocateRemainingCheck(): ConfirmCheck {
    const drawn = this.totalDrawnFromModels();
    if (drawn <= EPS || this.investable() <= this.modelPot() + EPS) return { kind: 'proceed' };

    return {
      kind: 'confirm',
      // ⚠️ NAMES THE CONTROL, not a step. Both sentences said "the Raise Cash step" while the
      // box lived over there; it is on this one now, a section above the button being pressed,
      // and sending the advisor to the previous step would send them somewhere it is not.
      message: `${formatMoney(drawn)} has been raised out of the models above. Allocating the `
        + 'remaining balance puts it straight back into them.',
      question: 'Allocate it and clear the Amount to Raise?',
    };
  }

  /**
   * Sweep whatever is unplaced into protected cash, or hand back whatever is over-committed.
   *
   * The Protected Cash counterpart of {@link useAllInvestable}, and deliberately the same
   * gesture: the advisor could do this by hand, but only by reading the strip and doing the
   * subtraction themselves — which is exactly the arithmetic that produces an off-by-a-cent
   * request nobody can see the cause of.
   *
   * ⚠️ Unlike that one it still works in a DELTA, because protected cash is a single figure with
   * no split under it: there is no pot to set, so sweeping what is left is the whole operation.
   *
   * ⚠️ Clamped at zero. Giving back more than has been set aside is not a thing the advisor can
   * do from this step: the excess would have to come off the models or the buys instead. When
   * the set-aside cannot absorb the whole over-commitment, this takes it to zero and leaves the
   * remainder showing — the step says so, and Submit still holds.
   *
   * Writes through `setValue`, which emits even though the control commits on blur, so the
   * strip and the totals move the moment the button is pressed.
   */
  applyRemainingToCash(): void {
    const remaining = this.unappliedValue();
    if (Math.abs(remaining) < EPS) return;

    const next = round2(Math.max(0, this.cashInput() + remaining));
    if (next === this.cashInput()) return;
    this.cashControl.setValue(next);
  }

  /**
   * Set how much of the account goes to models -- the figure every Target % is a share of.
   *
   * ⚠️ THE ADVISOR'S DECISION, and the whole point of ACMTECH-969. The pot used to be implicit:
   * whatever the rows happened to add up to. So the two editors pulled against each other --
   * a target edit held the total and moved everyone's dollars, a value edit moved the total --
   * and neither was a number anybody had stated. Now the total is named, typed and on screen,
   * and the grid below it only ever splits it.
   *
   * ⚠️ CLAMPED TO `totalValue`, THE ACCOUNT -- not to `investable`, what has been freed up so far
   * (AndyP, 2026-09-22, from advisor feedback). The distinction is the whole rule, and the two
   * ceilings behave completely differently:
   *
   *   above `investable`  a deficit the advisor can FIX, by going back to step 1 and selling.
   *                       Accepted, and the point of the change -- stating the allocation they
   *                       want and being shown the shortfall is how they work. Refusing it made
   *                       them do that arithmetic before the wizard would take the intent.
   *
   *   above `totalValue`  not satisfiable by anything. No amount of selling makes an account
   *                       worth more than it is, so the shortfall could never be closed and the
   *                       figure is simply a typo -- $600,000 into a $558,165 account.
   *
   * 🟢 The over-allocated path needs nothing added, and predates this: the clamp was always only
   * AT INPUT, because `investable` falls later when the advisor buys or sets cash aside, and
   * re-clamping then would quietly shrink an allocation they had already stated. The pot holds,
   * `unappliedValue` goes negative, the strip shows it in red, the step banners it and
   * `allocation` refuses to validate.
   *
   * 🟢 This is also what caps the percentage box at 100: it is the pot over `totalValue`, so a
   * clamp here is a clamp there, with no second rule to keep in step.
   *
   * Floored at zero. Nothing to allocate is a legitimate request (everything to custom holdings
   * or protected cash); a negative pot is not a thing.
   */
  setModelPot(value: number): void {
    this.modelPotSignal.set(round2(Math.min(Math.max(0, value), this.totalValue())));
  }

  /**
   * Put the whole investable balance into models, or pull the pot back to it.
   *
   * One control, both directions, off one figure: `investable` is the ceiling, so setting the
   * pot to it either sweeps in what was unplaced or gives back what was over-committed.
   *
   * ⚠️ Deliberately NOT gated on the grid being sound, which is what its pro-rata predecessor
   * had to be. That one divided the money across the rows, so it needed a model on every row
   * and a target column landing on 100 -- and was therefore unavailable exactly when a fresh
   * grid needed funding. This one moves the TOTAL and touches no row, so there is nothing about
   * the split it could get wrong.
   */
  useAllInvestable(): void {
    // 🔴 CLEARS FIRST, AND THE ORDER IS LOAD-BEARING (ACMTECH-1039). `investable`
    // INCLUDES whatever Raise Cash freed, so setting the pot before clearing would
    // read a figure the clear is about to withdraw and leave the request over-committed
    // on a click the advisor had just approved. Clearing re-seeds the pot to the gross
    // holdings and the column to the gross split; the line below then lifts the pot to
    // `investable` so released cash and sale proceeds land across it pro-rata, which is
    // what this has always done on an untouched grid.
    //
    // The component asks {@link allocateRemainingCheck} first and warns when this would
    // undo a draw. Unconditional here so the two can never disagree about what happened.
    this.clearModelFreeAmount();
    // Stating the pot wholesale overrides every figure the advisor typed into a row, so the
    // lock has nothing left to protect.
    this.modelValuesEditedSignal.set(false);
    this.setModelPot(this.investable());
  }

  /**
   * Add an empty allocation row.
   *
   * Funded at zero, NOT handed a share of the existing models' money.
   *
   * There is no "making room" any more, and there is nothing left for it to mean. This used
   * to work in percentages: a new row took whatever was left of 100, or — when the rows
   * already accounted for 100 — an even share, with the existing rows scaled proportionally
   * into the remainder. Both paths also moved dollars the advisor had already committed, merely
   * because they added a model to compare.
   *
   * ⚠️ The leftover path was the worse of the two, and it survived the first fix. On an
   * Initial Account nothing is allocated, so the leftover was the full 100 and the new row
   * arrived at 100% -- silently swallowing the whole investable amount, which is the exact
   * assumption the rest of this card removes.
   *
   * Now the row starts at 0% and the advisor states its share. Nothing else on the grid moves,
   * and the pot does not move either, so the money already placed stays placed -- see
   * {@link setModelPot} for how the total changes.
   *
   * 🟢 Except the FIRST row, which opens at 100. The grid no longer pre-builds a row when the
   * account holds no models (see `buildAllocationRows`), so this is now the only way one
   * appears there -- and the convenience that row carried is worth keeping: one model taking
   * the whole account is the common shape, and it saves typing 100 into three boxes since
   * distribution and contribution follow a fresh row's target for free.
   *
   * The zero-target argument below does not apply to it. There is nothing for a first row to
   * compete with, so nothing gets scaled down around it.
   */
  addRow(): void {
    const rows = this.allocationRows();
    this.allocationRows.set([
      ...rows,
      // Target ZERO once there is a row already, and it matters: a SECOND row at 100% takes the
      // whole target column with it, leaving the existing rows to be scaled down around a
      // figure the advisor never chose. It arrives empty and its percentage follows the dollars
      // once it is funded.
      //
      // ⚠️ No dollars to pin any more. A row's value is `pot × target%`, so a second row at
      // 0% is worth $0 by arithmetic rather than by a `valueCap: 0` that had to be remembered
      // -- and the pot does not move, so adding a model can no longer disturb money the advisor
      // has already placed.
      this.createRow(rows.length === 0 ? 100 : 0),
    ]);
  }

  /**
   * Put the allocation back exactly as it loaded.
   *
   * Rebuilt from `data()`, which is the untouched Orion payload, so every row returns to the
   * dollars it actually holds and the grid re-derives the same opening TARGET split it loaded
   * with — including rows the advisor added or removed, which simply disappear or come back.
   *
   * ⚠️ `buildAllocationRows` leaves TARGET at 0, and {@link seedOpeningRows} is what
   * fills it. Reset used to omit that second call, so it rebuilt the rows with every percentage
   * at zero: remove a sleeve, press Reset, and the sleeve came back while the whole Target
   * column read 0% and the step sat blocked with nothing obviously wrong on screen. The two
   * calls belong together — which is why the prefill is now a named method both callers share
   * rather than a block of logic sitting in `initialize`.
   *
   * ⚠️ Does NOT re-query Orion. It restores the snapshot this wizard loaded with; if the
   * account has moved since, this will not show it.
   *
   * ⚠️ THE SMA LATCH IS RELEASED HERE, and it is the one thing besides the rows that has to be.
   * `smaCommitted` means "the advisor switched this request into an SMA mid-flight" — it closes
   * the other steps, masks the cash rows and pins the pot to the whole account. Reset puts the
   * grid back to what LOADED, which by definition is not that request, so leaving it set would
   * restore the account's original sleeves while still locking every step that could act on
   * them. Same stranding `removeRow` guards against, reached by the other control on this step.
   *
   * 🟢 Unconditional, and it does not need to ask whether an SMA is coming back. `smaLockActive`
   * reads the GRID, so if the loaded sleeves include an SMA the lock re-engages on its own a
   * moment later — correctly, because that is then an account that already holds one.
   *
   * `allocationBaseline` needs no resetting: it was captured from exactly this state at load
   * (see `initialize`), so `allocationChanged` goes quiet on its own afterwards — the row ids
   * differ but the fingerprint compares values, not identity.
   *
   * ⚠️ It DOES reach step 1 now, for Amount to Use (ACMTECH-1039) -- see the body. That is
   * the second thing it reaches across for, after `releaseSmaCommit`, and for the same kind
   * of reason: both are step-1 state whose whole effect is on this grid.
   */
  resetAllocation(): void {
    const data = this.data();
    if (!data) return;
    this.releaseSmaCommit();
    // ⚠️ The freed amount goes too, or the money is claimed TWICE: this restores the pot to
    // the gross holdings while Raise Cash still shows an amount being freed, so the same
    // dollars sit in the models and in Left to Allocate at once. Set directly rather than
    // through `clearModelFreeAmount`, whose re-seed would be thrown away by the two lines
    // below (ACMTECH-1039).
    this.modelFreeAmountSignal.set(0);
    // Both halves of the lock, or Reset leaves the pro-rata box disabled with nothing on the
    // grid explaining why -- and Reset is the documented way BACK to it (ACMTECH-1039).
    this.modelValuesEditedSignal.set(false);
    this.allocationRows.set(this.buildAllocationRows(data));
    this.seedOpeningRows();
  }

  /**
   * Drop a model row.
   *
   * ⚠️ **The survivors are left exactly as they are** -- same targets, so same dollars. The pot
   * does not shrink and nothing is handed to the other rows, so the target column falls below
   * 100 by the removed row's share and the step blocks until the advisor says where that share
   * goes. One row moves, as everywhere else on this step (ACMTECH-969); `rebalanceUntouched`
   * used to spread it for them, which is the inference that has been removed.
   *
   * The freed money then shows as still to place, which Submit refuses to leave unplaced (see
   * {@link submitBlockers}); the step itself lets the advisor move on and resolve it elsewhere.
   *
   * Removing the LAST row is allowed (ACMTECH-945). An empty grid means the account holds no
   * models, which {@link allocation} treats as valid.
   */
  /**
   * Undo the SMA commitment: drop the latch and put step 1's cash back as it opens.
   *
   * ⚠️ THE CASH IS THE HALF THAT IS EASY TO MISS. `commitToSma` moves the two lists in OPPOSITE
   * directions, both deliberately:
   *
   *   toggleSellAll(false)    nothing is sold individually -- the SMA takes the whole account
   *   toggleUseAllCash(true)  the cash really does go to the model, so it is RELEASED
   *
   * So on release the positions land back on their opening state by coincidence — unchecked is
   * how step 1 opens — while the cash does not: it opens opt-in, so every row left ticked is an
   * instruction from a request that no longer exists. Unticking it is what makes the two lists
   * agree again.
   *
   * ⚠️ Not a restore, and it cannot be. If the advisor had released cash before choosing the SMA,
   * `commitToSma` has already overwritten that choice and nothing records what it was; this
   * returns to the documented opening state rather than to their earlier one. The sells and buys
   * `commitToSma` discarded do not come back either.
   *
   * 🟢 A no-op unless the latch is actually set, which is what lets {@link resetAllocation} call
   * it unconditionally: an ordinary Reset with no SMA in sight must not go and untick step 1.
   */
  private releaseSmaCommit(): void {
    if (!this.smaCommitted()) return;
    this.smaCommitted.set(false);
    this.toggleUseAllCash(false);
  }

  removeRow(id: string): void {
    // ⚠️ RELEASE THE LATCH when the row going is the SMA -- checked BEFORE the row leaves the
    // list, because after it there is nothing left to ask. `smaLockActive` needs no equivalent:
    // it reads the grid, so it releases on its own.
    if (this.modelOption(this.allocationRows().find((r) => r.id === id)?.modelId ?? '')?.isSma) {
      this.releaseSmaCommit();
    }

    this.allocationRows.set(this.allocationRows().filter((r) => r.id !== id));
  }

  /**
   * Set one row's Distribution % or Contribution % by hand.
   *
   * MANUAL is the whole point of the flag: it is what stops the figure being read straight off
   * the target, and it survives until this row's target is edited -- see {@link editTarget},
   * which clears it.
   *
   * **ONE ROW MOVES**, as everywhere else on this step since ACMTECH-969. No other row's
   * distribution is adjusted to make the column reach 100; the advisor states the rest. The
   * absorption arithmetic that used to run here -- and, in a second copy weighted differently,
   * in `rebalanceDerivedColumns` -- is gone with it.
   */
  private editColumn(col: 'distribution' | 'contribution', id: string, value: number): void {
    const edited = Math.max(0, value);

    this.updateRow(id, (r) =>
      col === 'distribution'
        ? { ...r, distribution: edited, distributionManual: true, distributionSeeded: false }
        : { ...r, contribution: edited, contributionManual: true, contributionSeeded: false });
  }

  private updateRow(id: string, fn: (row: AllocationRow) => AllocationRow): void {
    this.allocationRows.set(this.allocationRows().map((r) => (r.id === id ? fn(r) : r)));
  }

  /**
   * Step 4's opening state: one row per existing model sleeve, or a single blank row
   * when the account has none.
   *
   * This is the difference from Initial Account Setup — a reallocation starts from
   * what the account already holds rather than from nothing. Each sleeve supplies all
   * three percentages, which are **independent** of one another and so are all marked
   * manual; leaving them derived would have the grid quietly rewrite the account's
   * actual distribution and contribution splits to match its target.
   *
   * Rows open with the account's real percentages and are left adjustable — nothing
   * rebalances on load, so the pre-load survives until the advisor edits something.
   * A sleeve whose model has no counterpart in the CRM list keeps its percentages but
   * opens with no selection, so the advisor resolves it rather than the row vanishing.
   */
  /**
   * Resolve an Orion sleeve to a CRM model id, or `''` when nothing matches.
   *
   * The matching rules live in the SERVICE (`buildModelLookup`), shared with the load-time
   * SMA block. They are not reimplemented here: if the two diverged, an SMA account could
   * clear the block and then be offered a mixed request.
   *
   * `''` for an unresolved sleeve is what keeps a row on a model CRM does not list — it
   * opens with its real percentages and no selection, so the advisor resolves it rather
   * than the row vanishing.
   */
  private buildModelIdLookup(data: ReallocationV2Data): (sleeve: SleeveAllocation) => string {
    const lookup = buildModelLookup(data.models, data.allModels);
    return (s) => lookup(s)?.id ?? '';
  }

  /**
   * What the account's model allocation looks like RIGHT NOW, as fingerprints.
   *
   * The baseline `allocationChanged()` compares against. Built from Orion's sleeves rather
   * than from the grid, because since ACMTECH-945 the grid opens blank — snapshotting it
   * would set the baseline to zeros, and an advisor who typed the account's own numbers back
   * in would read as having changed it, letting a no-op request through Submit.
   *
   * Shaped to match {@link fingerprintAllocation} exactly, including the rounding: the
   * comparison is field-by-field, so a baseline built to different precision than the live
   * rows would report a change on every render.
   */
  private buildAllocationBaseline(data: ReallocationV2Data): AllocationFingerprint[] {
    const sleeves = data.sleeves ?? [];
    if (sleeves.length === 0) {
      // ⚠️ An unsleeved account still has a "before" when it carries a model at ACCOUNT level, and
      // the baseline has to say so. `allocationChanged` treats a model appearing on an untouched
      // grid as a proposal — rightly, since choosing one is a request even before money is put
      // against it — so with an empty baseline the wizard's OWN pre-selection read as the advisor
      // having asked for something, and "no changes to submit" never appeared.
      //
      // The account-level model is a synthetic sleeve now, so it is baselined by the normal
      // path below with the account's real figures -- no zero-filled special case, and no second
      // definition of what the account holds.
      return [];
    }

    const lookup = buildModelLookup(data.models, data.allModels);
    return sleeves.map((s) => ({
      modelId: lookup(s)?.id ?? '',
      target: round2(s.target),
      distribution: round2(s.distribution),
      contribution: round2(s.contribution),
      allocatedValue: round2(s.currentValue),
    }));
  }

  private buildAllocationRows(data: ReallocationV2Data): AllocationRow[] {
    const sleeves = data.sleeves ?? [];
    // No sleeves -- an Initial Account, or an account with nothing under model management.
    //
    // ⚠️ NO ROW AT ALL when the account holds no model either. This used to manufacture one
    // blank row, so that there was something to fill in rather than a bare table. It read as an
    // instruction instead: an empty model picker looks like a question that has to be answered,
    // when allocating to models is optional -- the money can go to custom holdings or be set
    // aside. Advisors filed models they did not want because the screen appeared to require
    // one. The step now shows Add Model and nothing else, and the template drops the table
    // entirely (see its note); `allocation` has always treated an empty grid as valid, and
    // `addRow` produces exactly the row this used to pre-build.
    //
    // 🟢 An account that DOES hold a model at account level keeps its row -- that is real
    // information about the account (ACMTECH-951), not a prompt, and dropping it would send the
    // advisor to an empty screen on an account that already holds something.
    //
    // Target 100 is a sensible default for the one-model shape that is, and it saves typing
    // 100 into three boxes -- distribution and contribution follow it for free, since a fresh
    // row has neither manual nor scaled set.
    //
    // ⚠️ A row carries no dollars of its own any more, so there is nothing to pin at zero:
    // its value is `pot × target%`, and the pot is stated separately. This row once opened at
    // 100% with a null `valueCap`, which fell through to `investable × 100%` and silently
    // swallowed the whole account -- the bug that whole field existed to contain.
    //
    // ⚠️ An account-level model no longer arrives here. It used to be pre-selected on a blank
    // row by this branch, editable, unfunded and with target 100 -- the service now hands it
    // over as a synthetic SLEEVE instead, so it flows through the normal path below and comes
    // out read-only, funded at the account's value, with the CURRENT columns populated. See
    // `resolveAccountLevelSleeve`.
    //
    // So no sleeves genuinely means no models: an Initial Account, an account whose only
    // holdings are hand-traded, or one the advisor has emptied.
    if (sleeves.length === 0) return [];

    const modelIdFor = this.buildModelIdLookup(data);

    // Whether a stored column can be used AS A COLUMN: it has to total 100 across the sleeves.
    //
    // A column that does not (0 everywhere on an account nobody has set up, or the 150% a
    // half-migrated account can carry) cannot be honoured as written -- seeding it would open
    // the step already blocked, on figures the advisor never entered and cannot be expected to
    // recognise. Those fall back to being derived, which is a repair rather than an override.
    //
    // Judged PER COLUMN, because they drift independently.
    const columnTotals100 = (pick: (sl: SleeveAllocation) => number): boolean =>
      Math.abs(sleeves.reduce((total, sl) => total + pick(sl), 0) - 100) < EPS;
    const targetsUsable = columnTotals100((sl) => sl.target);
    const keepDistribution = columnTotals100((sl) => sl.distribution);
    const keepContribution = columnTotals100((sl) => sl.contribution);

    // What the account holds in models TODAY -- the pool each stated target is a share of.
    //
    // The sleeves' own current values, not `investable` and not the account total: a target of
    // 20% means a fifth of the money under model management, not a fifth of everything.
    const currentModelPot = sleeves.reduce((total, sl) => total + sl.currentValue, 0);

    return sleeves.map((s) => {
      const modelId = modelIdFor(s);
      if (!modelId) {
        // eslint-disable-next-line no-console
        console.warn(
          `[reallocation-v2] Sleeve model "${s.modelAggName}" has no match in the CRM model list; `
          + 'pre-loading its allocations with no model selected.',
        );
      }
      return {
        // SEEDED with the sleeve's own target, when the account states a usable one.
        //
        // ⚠️ This has now moved three times, so the history is worth having in one place:
        //
        //   1. Originally the sleeve's target, verbatim.
        //   2. ACMTECH-945 derived it from the VALUE instead -- "what the split actually IS,
        //      rather than the target Orion has stored, which may have drifted from it" --
        //      because the grid had just started opening blank and a value-derived target was
        //      the only figure that could not disagree with the dollars beside it.
        //   3. Back to the stored target, here. The read-only CURRENT columns made the drift
        //      visible, and once it is visible the derived figure stops being the useful one:
        //      the advisor is not trying to restate where the account already is, they are
        //      editing where it should go next. The stored target is that starting point.
        //
        // The requested value that follows will usually differ from the current one -- the pot
        // is split by these targets rather than by what each sleeve happens to hold -- and that
        // is intended. It is the account's own stated intent applied to the money it actually
        // holds, which is where the advisor starts work rather than where they finish it.
        ...this.createRow(targetsUsable ? s.target : 0),
        modelId,
        sleeveModelName: s.modelAggName,
        // ⚠️ NO per-row dollar figure. A row's value is `pot × target%`, and the pot opens at
        // Σ of these very `sleeveCurrentValue`s (see `seedOpeningRows`) -- so the grid still
        // opens describing the account, but through one figure instead of one per row.
        //
        // The account as it stands, all four figures, for the read-only CURRENT group beside
        // the inputs. Absent on a row the advisor adds -- there is no sleeve behind it.
        sleeveCurrentValue: s.currentValue,
        sleeveCurrentTarget: s.target,
        sleeveCurrentDistribution: s.distribution,
        sleeveCurrentContribution: s.contribution,
        // How far this sleeve has drifted from its own stated target. See the field's own note
        // for why it is computed here, from load data, rather than against the grid.
        //
        // ⚠️ Only when the stated targets could be honoured. On the fallback path the targets
        // were DERIVED from these very values, so the drift would be zero by construction --
        // and a row of "Underfunded $0.00" would assert a perfectly balanced account when the
        // truth is there was nothing to compare against.
        sleeveDrift: targetsUsable
          ? s.currentValue - (currentModelPot * s.target) / 100
          : undefined,
        // The share this sleeve actually holds, against the share its target claims.
        //
        // ⚠️ NOT gated on `targetsUsable`, unlike the drift above. This is a share of the pot
        // with no target in it, so it is meaningful whatever the stated targets say -- and on
        // exactly the accounts whose targets are unusable it is the only honest statement of
        // the mix available. The template withholds the DIFFERENCE there, not the figure.
        //
        // Guarded on a non-zero pot: a share of nothing is not 0%, it is undefined, and
        // rendering 0.00% would assert an empty sleeve in an empty account.
        sleeveActualPct: currentModelPot > EPS
          ? (s.currentValue / currentModelPot) * 100
          : undefined,
        // What the sleeve holds TODAY. Compared against modelId at submit to tell a model swap
        // from an untouched row -- the two carry different Connect model aggs.
        sleeveModelId: modelId,
        // The { accountId, modelAggId } pair as it stood at load. Rows the advisor adds
        // later keep the createRow() defaults of null, which is what marks them new.
        orionConnectAccountId: s.accountId,
        orionConnectModelAggId: s.modelAggId,
        // DISTRIBUTION and CONTRIBUTION are seeded the same way, and for the same reason: a
        // live sleeve reports its own 41 / 44 / 41 and that is the advisor's starting point.
        //
        //   manual  -> the advisor typed it
        //   seeded  -> it came from the sleeve, and holds until this row's target is edited
        //   neither -> it has no value of its own and reads the target % directly
        //
        // SEEDED, not MANUAL: the figures are real but the advisor did not choose them, so the
        // moment they move a target the splits follow it (ACMTECH-953). That is what a starting
        // point means here -- it holds until the advisor states otherwise, and changing the mix
        // IS stating otherwise. {@link editTarget} clears both flags for exactly that reason.
        //
        // ⚠️ Which is why nothing settles these columns at load: a column left NEITHER needs no
        // write, because `effectiveDistribution` reads the target directly. The flag was named
        // `*Scaled` while an auto-balancer might adjust it; nothing adjusts it now, so the name
        // says where it came from instead. See `seedOpeningRows`.
        distribution: keepDistribution ? s.distribution : 0,
        distributionSeeded: keepDistribution,
        contribution: keepContribution ? s.contribution : 0,
        contributionSeeded: keepContribution,
        touched: false,
      };
    });
  }

  /**
   * Minimum investment for a row's selected model. **Zero when none is on record.**
   *
   * By Connect model-aggregate id where CRM has mapped one, else by name. The name path
   * is not dead code: it covers every model the mapping has not reached yet.
   *
   * ⚠️ NO MINIMUM ON RECORD MEANS NO MINIMUM. It used to mean "we cannot check", which
   * raised a row error and blocked Next -- deliberately, on the reasoning that waving an
   * unknown minimum through would build a request Orion then rejects. Reversed on AndyP's
   * call: a null `minimumInvestmentAmount` in Connect is how an unrestricted model is
   * recorded, not a data gap, so blocking on it refused perfectly good models and gave the
   * advisor nothing they could act on. Anything genuinely absent is a Connect data problem
   * and gets fixed there, not guessed at here.
   *
   * 🟢 Returning 0 rather than null is what removes the whole "unresolved" branch: every
   * comparison is `value + EPS < minimum`, and no non-negative allocation is below zero.
   * Keeping the null and coercing at each use site would have left three places to forget.
   *
   * Also covers the case where the minimums source itself failed to load (`modelMinimums`
   * null) -- which never blocked anyway, since a reference lookup being down must not stop
   * every allocation in the portal.
   */
  private minimumFor(modelId: string): number {
    const minimums = this.data()?.modelMinimums;
    if (!minimums || !modelId) return 0;

    const model = this.modelOption(modelId);
    if (model?.modelAggId != null) {
      const byId = minimums.byModelAggId[model.modelAggId];
      if (byId != null) return byId;
    }

    const name = (model?.name ?? '').trim().toLowerCase();
    return (name ? minimums.byName[name] : undefined) ?? 0;
  }

  /** Look a model up across both lists — the selectable one and the wider resolution one. */
  private modelOption(modelId: string): ModelOption | undefined {
    const data = this.data();
    return (
      data?.models.find((m) => m.id === modelId)
      ?? data?.allModels?.find((m) => m.id === modelId)
    );
  }

  private createRow(target: number): AllocationRow {
    return {
      id: `row-${++this.rowSeq}`,
      modelId: '',
      target: round2(target),
      targetExact: target > EPS ? target : null,
      distribution: 0,
      distributionManual: false,
      distributionSeeded: false,
      contribution: 0,
      contributionManual: false,
      contributionSeeded: false,
      touched: false,
      // No sleeve behind a freshly-created row. buildAllocationRows() overwrites these
      // for rows pre-loaded from an existing sleeve; anything still null at submit is a
      // sleeve that has to be created.
      orionConnectAccountId: null,
      orionConnectModelAggId: null,
    };
  }

  /**
   * Display name for a model id. Looks across both lists, so a pre-loaded row on a
   * model outside the advisor's own list still names it on Review & Submit rather than
   * rendering an empty cell.
   */
  modelName(id: string): string {
    return this.modelOption(id)?.name ?? '';
  }

  // --------------------------------------------------------------------------
  // Navigation + submit
  // --------------------------------------------------------------------------
  goNext(): void {
    if (!this.canProceed()) return;
    const target = this.nextNavigableStep();
    if (target === null) return;
    this.leaveCurrentStep();
    this.currentStep.set(target);
    this.enterCurrentStep();
  }

  /**
   * The next step forward the gates actually permit, or null when there is none.
   *
   * ⚠️ Next used to be a bare `currentStep + 1`, which walks straight into a step the SMA lock
   * has closed: Allocate to Models is second, and Buy Additional Securities is third. With an SMA
   * on the account the advisor would land on a step they can neither use nor leave forwards.
   *
   * 🟢 Ordinary flow is unchanged, by construction rather than by special case. `canProceed()` and
   * `canJumpTo(current + 1)` consult the same per-step reasons, so with nothing closed the first
   * permitted step is always the next one. Written generically because the same trap waits for any
   * future gate -- a flag-gated step that becomes conditionally unreachable would hit it too.
   */
  private nextNavigableStep(): number | null {
    for (let step = this.currentStep() + 1; step <= this.totalSteps; step++) {
      if (this.canJumpTo(step)) return step;
    }
    return null;
  }

  /**
   * Tidy-up that runs however a step is left — Next, Back, or a jump from the indicator.
   *
   * Leaving the buys step discards rows the advisor started and abandoned. They are not
   * errors — blocking on an empty row would make someone hunt for a delete button to get
   * past it — so they are simply tidied away. It hangs off *leaving* rather than off Next
   * because the indicator is now another way out of the step.
   */
  private leaveCurrentStep(): void {
    if (this.currentStepId() === 'buys') this.discardOrphanBuyRows();
  }

  /**
   * The counterpart to {@link leaveCurrentStep}, run however a step is ARRIVED at — Next, Back,
   * or a jump from the indicator.
   *
   * Paired with it deliberately: there are three ways into a step and they must all behave the
   * same, which is the lesson `leaveCurrentStep` already learned when the indicator became a
   * second way out of the buys step.
   */
  private enterCurrentStep(): void {
    if (this.currentStepId() === 'sleeveAmounts') this.prefillSleeveAmountsOnce();
  }

  /**
   * Apportion the funding amount across the model sleeves the first time the advisor arrives,
   * so step 2 opens filled in rather than at zero (AndyP, 2026-10-01).
   *
   * Exactly what pressing "Allocate remaining" would do — the same call, not a copy of its
   * arithmetic — so the prefill cannot drift from the button, and every guard the button honours
   * is honoured here: percentages that do not total 100 are refused, a distribution is capped per
   * sleeve at what that sleeve holds, and rounding drift lands on the last row that can take it.
   *
   * 🔴 ONCE PER REQUEST, via {@link sleeveAmountsPrefilled}. The grid being empty is NOT the
   * test — an advisor who pressed Clear leaves it in the same state as one who has never seen it,
   * and refilling theirs would undo a decision rather than save them a click.
   *
   * ⚠️ The empty check below is a second guard, not the first. It stops a prefill landing on top
   * of amounts that are already there, which the latch alone would not catch if this ever gains
   * another caller.
   *
   * 🟢 Nothing else needs to change for Clear to light up: it is disabled on a zero total, which
   * the prefill makes non-zero.
   */
  private prefillSleeveAmountsOnce(): void {
    if (this.sleeveAmountsPrefilled()) return;
    this.sleeveAmountsPrefilled.set(true);
    if (Math.abs(this.sleeveAmounts().total) >= EPS) return;
    this.applyRemainingToSleeves();
  }

  /** Drop buy rows with neither a security nor a share count. */
  private discardOrphanBuyRows(): void {
    const orphans = new Set(this.buys().filter((b) => b.isOrphan).map((b) => b.id));
    if (orphans.size === 0) return;
    this.buyRows.update((rows) => rows.filter((r) => !orphans.has(r.id)));
  }

  /** Whether Back is available — false while an attestation holds the advisor here. */
  readonly canGoBack = computed(
    () => this.currentStep() > 1 && this.canJumpTo(this.currentStep() - 1),
  );

  goBack(): void {
    if (this.canGoBack()) {
      this.leaveCurrentStep();
      this.currentStep.update((s) => s - 1);
      this.enterCurrentStep();
    }
  }

  /**
   * Whether the advisor may jump straight to a 1-based step from where they are.
   *
   * **Steps are not gated on having been visited.** Everything before Allocate to Models is
   * optional — a reallocation that only changes the model mix sells nothing, buys nothing
   * and protects no cash — so requiring a walk through three steps to reach the one that
   * matters is a toll, not a safeguard. Going backwards is always free.
   *
   * What a jump cannot do is *carry a problem past the step that shows it*. Each gate is
   * a state that would make the request wrong, paired with the step the advisor fixes it
   * on; skipping forward over one would land them on Review with no indication of what is
   * wrong or where. Gates are expressed against step ids rather than numbers so they keep
   * meaning the same thing when an optional step shifts the ones after it.
   */
  canJumpTo(target: number): boolean {
    if (target < 1 || target > this.totalSteps) return false;

    // The Do Not Trade attestation holds the advisor on the step in BOTH directions, and
    // is the only gate that does. Every other gate stops a bad state travelling forward;
    // this one is a statement about what is on this screen, so leaving the screen at all
    // without it — even backwards — would collect the tick under false pretences. There is
    // always a way out: confirm, or unmark the positions.
    if (this.currentStepId() === 'doNotTrade' && this.dntConfirmationRequired()) return false;

    // A confirmed SMA holds the advisor on Review in BOTH directions, for the same reason
    // the attestation does: there is nothing left on the earlier steps to change. Sells,
    // buys and protected cash were all cleared by the switch, so re-entering those steps
    // would only offer a way to rebuild a request that cannot be commingled. The ways out
    // are Submit and the X.
    // An SMA closes every step but Allocate to Models -- where it can be removed -- and Review &
    // Submit. Above the "backwards is free" line on purpose: the point is that the advisor cannot
    // go and sell positions on an account that cannot be commingled, and those steps sit BEHIND
    // the one they land on.
    //
    // ⚠️ A JUST-SELECTED SMA used to be pinned to Review alone, by a separate rule reading
    // `smaCommitted` -- so Allocate to Models was shut, and the advisor who picked the wrong SMA
    // had no way back to change it without closing the request. Worse, nothing was marked LOCKED
    // in that state, so the indicator drew all five steps as visited mint checks: it looked like
    // a finished request rather than a closed one. Both routes now take this single rule, so the
    // step row reads the same whichever way the account got to an SMA.
    //
    // 🟢 Everything else follows from this. `navigableSteps` is derived from this method, so the
    // indicator dims itself; `canGoBack` reads it, so Back cannot reach a closed step either.
    if (this.smaLockedSteps().has(target)) return false;

    if (target <= this.currentStep()) return true;

    // 0 means the step is not in this configuration, so it gates nothing.
    const past = (id: ReallocationV2StepId): boolean => {
      const index = this.stepIndex(id);
      return index > 0 && target > index;
    };

    // Funding gates everything after it, and unlike the others it gates hard: with no amount
    // there is no pool, so every later step would be apportioning against zero.
    if (past('funding') && this.fundingStepBlockedReason() !== null) return false;
    if (past('sleeveAmounts') && this.sleeveAmountsStepBlockedReason() !== null) return false;
    if (past('positions') && this.positionsStepBlockedReason() !== null) return false;
    if (past('buys') && this.buyStepBlockedReason() !== null) return false;
    if (past('protectedCash') && this.protectedCashStepBlockedReason() !== null) return false;
    if (past('allocation') && !this.allocation().valid) return false;
    return true;
  }

  /** The 1-based steps the indicator should render as clickable right now. */
  readonly navigableSteps = computed<ReadonlySet<number>>(() => {
    const reachable = new Set<number>();
    for (let step = 1; step <= this.totalSteps; step++) {
      if (this.canJumpTo(step)) reachable.add(step);
    }
    return reachable;
  });

  /** Jump directly to a step via the step indicator. */
  goToStep(step: number): void {
    const target = Math.round(step);
    if (target === this.currentStep() || !this.canJumpTo(target)) return;
    this.leaveCurrentStep();
    this.currentStep.set(target);
    this.enterCurrentStep();
  }

  /**
   * The selected model allocations for the submit payload. Each carries the
   * numeric `investmentAccountModelID`; the model name is not included.
   */
  buildModelAllocations(): ModelAllocationSelection[] {
    return this.allocation().rows.map((r) => ({
      investmentAccountModelID: Number(r.modelId),
      targetPercent: r.effectiveTarget,
      distributionPercent: r.effectiveDistribution,
      contributionPercent: r.effectiveContribution,
      allocatedValue: r.allocatedValue,
    }));
  }

  /**
   * Assemble the request payload for the POST. Shape matches the generated
   * `AccountRequestInitialAccountModelBase`; derived aggregates (custom-holdings value,
   * investable) are intentionally omitted — the CRM recomputes them / doesn't persist them.
   *
   * `isPartialSell` is still omitted, but `isSellAll` is NOT: CRM writes a different
   * `req.TradeAction` for a liquidation, and it cannot derive which is which from a sell row
   * that carries the shares sold but never the shares held.
   *
   * Cash is emitted as a `CASH` / "Custodial Cash" line of `customHoldings`
   * rather than as the `protectedCash` scalar. `protectedCash` is kept at 0 for
   * now (the CRM contract still carries the field); it is retired end-to-end in a
   * later CRM pass. 1 share = $1 for the cash line.
   */
  /**
   * The account's sleeves as they stood when the wizard loaded.
   *
   * Sent alongside {@link buildModelAllocations} so the request records a *change* rather
   * than just a destination: "put 60% in Model X" alone gives the trade team no way to
   * tell a re-weight from a brand-new sleeve. Positions do not need this because their
   * delta is already explicit in the sells and buys.
   *
   * Read from the loaded sleeve data rather than from the grid, because the grid is what
   * the advisor has been editing — by submit time it no longer describes the before-state.
   * `allocatedValue` is each sleeve's actual `currentValue`, not a target-derived figure.
   */
  buildCurrentModelAllocations(): AccountRequestV2ModelAllocation[] {
    const data = this.data();
    const sleeves = data?.sleeves ?? [];
    if (!data || sleeves.length === 0) return [];

    // The whole ModelOption, not just its id: `isSma` below has to describe the same model the
    // id names, and resolving twice invites them to disagree. `buildModelIdLookup` is this plus
    // `?.id ?? ''`, so nothing about the matching rules changes.
    const modelFor = buildModelLookup(data.models, data.allModels);

    // What Raise Cash took out of each sleeve (ACMTECH-1040). Shared with the Cash Raised
    // figure on Review & Submit -- see the computed for why it is keyed this way.
    const cashRaisedFor = this.cashRaisedByConnectAccountId();

    return sleeves.map((s) => {
      const model = modelFor(s);
      const modelId = model?.id ?? '';
      return {
        investmentAccountModelID: modelId ? Number(modelId) : undefined,
        allocatedValue: s.currentValue,
        // ⚠️ `undefined`, NOT 0, where nothing was freed -- `JSON.stringify` drops it and the
        // column stays null. Three different things produce a null on the way back out:
        // nothing was freed, the row is a Requested row, or the request predates the field.
        // None of them is "zero was freed", and every screen shows a dash for all three.
        cashRaised: s.accountId != null ? cashRaisedFor.get(s.accountId) : undefined,
        targetPercent: s.target,
        distributionPercent: s.distribution,
        contributionPercent: s.contribution,
        orionConnectAccountID: s.accountId ?? undefined,
        orionConnectModelAggID: s.modelAggId ?? undefined,
        accountNumber: undefined,
        // Never true: by definition these sleeves already exist.
        isNew: false,
        // What the account was ALREADY in, and routinely TRUE now.
        //
        // ⚠️ This used to say "always false in practice — the wizard refuses to open on an account
        // already under an SMA". ACMTECH-956 removed that refusal, so an SMA sleeve reaches this
        // line for real, and it is the only place the "before" picture is written.
        isSMA: model?.isSma ?? false,
      };
    });
  }

  /**
   * The "before" picture the request was built against.
   *
   * Sent from here rather than rebuilt server-side: re-reading Orion at submit time would
   * produce a different picture at a different moment, which is exactly what this exists
   * to pin down. `capturedOn` is when the data was LOADED, not when Submit was pressed —
   * the wizard can sit open for an hour, and ACMTECH-903's staleness check needs the
   * former.
   *
   * `valueBasis` is stated rather than assumed. Every figure here is `pendingValue`, which
   * differs from the market basis by ~0.05%; a snapshot that does not say which basis it
   * used cannot be compared against anything later with confidence.
   */
  buildSnapshot(): AccountRequestV2Snapshot {
    const data = this.data();
    const context = data?.portfolio ?? null;
    const positions = data?.positions ?? [];
    const cashPositions = data?.cashPositions ?? [];
    return {
      capturedOn: this.loadedAt() ?? undefined,
      valueBasis: 'Pending',
      json: JSON.stringify({
        valueBasis: 'Pending',
        accountTotal: this.totalValue(),
        // The Custom Holdings sleeve as loaded, cash included — summed from the rows
        // rather than read off a running total, so the snapshot is internally consistent
        // even if a later change alters how the header figure is derived.
        sleeveTotal: [...positions, ...cashPositions].reduce((sum, p) => sum + p.marketValue, 0),
        portfolio: context
          ? {
            portfolioId: context.portfolioId,
            registrationId: context.registrationId,
            reportedTotalValue: context.reportedTotalValue,
            doNotTrade: context.doNotTrade,
            setAsideCash: context.setAsideCash,
          }
          : null,
        positions: positions.map((p) => ({
          securityId: p.securityId,
          ticker: p.ticker,
          description: p.description,
          shares: p.shares,
          price: p.price,
          marketValue: p.marketValue,
          totalCost: p.totalCost,
          reportedGainLoss: p.reportedGainLoss,
          reportedPctOfAccount: p.reportedPctOfAccount,
        })),
        sleeves: (data?.sleeves ?? []).map((s) => ({
          orionConnectAccountId: s.accountId,
          orionConnectModelAggId: s.modelAggId,
          modelName: s.modelAggName,
          target: s.target,
          distribution: s.distribution,
          contribution: s.contribution,
          minimum: s.minimum,
          currentValue: s.currentValue,
        })),
      }),
    };
  }

  /**
   * How the money travels — the six fields `req.Contribution` and `req.Distribution` share.
   *
   * ⚠️ Two of them are sent ONLY when the step actually asked for them, which is not the same as
   * sending whatever the control happens to hold. Both controls persist after the choice that
   * revealed them is reversed: pick a monthly schedule, set a start date, switch back to one-time,
   * and the date is still there — unseen, because the step hides it. Filing it would give the
   * trade team a start date for a transfer that has no schedule, and an overnight instruction on
   * an ACH that cannot be couriered. The conditions are the step's own, so what is stored is what
   * the advisor was shown.
   *
   * `deliveryInstruction` is the authorization's `displayValue` — the string itself, because
   * Schwab supplies no id and that string is what the trade team reads to know where the money
   * goes. Undefined is legitimate: Leave in Cash and Move to Retail keep the money at the
   * custodian, so there is no account to name.
   *
   * ⚠️ `hasIaAuthority` is forwarded INCLUDING null — `?? undefined` and not `?? false`. Null is
   * "Schwab did not say", which is a different claim from "no authority on file", and v1's column
   * already mixes the two because the v1 portal defaults it. The warning on this step is drawn on
   * `=== false` for the same reason.
   */
  private buildDelivery(): {
    deliveryMethodID: number | undefined;
    deliveryFrequencyID: number | undefined;
    deliveryInstruction: string | undefined;
    deliveryStartDate: Date | undefined;
    isOvernightDelivery: boolean;
    hasIaAuthority: boolean | undefined;
  } {
    const controls = this.fundingForm.controls;
    return {
      deliveryMethodID: controls.deliveryMethodId.value ?? undefined,
      // 🔴 ONE TIME ON A CLOSE, and the control already says so -- `onCloseAccountChanged`
      // rewrites it, which is what the locked select on the step is displaying. This re-states it
      // at the payload anyway, because the two must agree and only one of them is what CRM sees:
      // if that write ever no-ops (an empty frequency list), the step would show a stale Monthly
      // and this would still file the truth, rather than handing the trade team a recurring
      // instruction to empty an account that will not exist next month.
      //
      // SETTLED, not a workaround (AndyP, 2026-09-23). Null was the first instinct and the column
      // will not take one -- `DeliveryFrequencyID` is NOT NULL on both `req.Contribution` and
      // `req.Distribution`, and `RequestHelper` throws "DeliveryFrequencyID is required" before
      // SaveChanges is reached. One Time is not a stand-in for that null either: a close pays out
      // once, so it is simply what the frequency IS. The column stays NOT NULL.
      deliveryFrequencyID: this.deliveryFrequencyLocked()
        ? this.oneTimeFrequency()?.id ?? controls.deliveryFrequencyId.value ?? undefined
        : controls.deliveryFrequencyId.value ?? undefined,
      deliveryInstruction: this.filedDeliveryInstruction(),
      // 🔴 SENT AS A CALENDAR DATE, NOT AN INSTANT -- "2026-10-11", no time and no zone. This is
      // the one field in the payload that is a DATE rather than a moment, and the distinction is
      // load-bearing (AndyP, 2026-10-02). `priceAsOf` and `capturedOn` are genuine instants and
      // must keep their UTC handling; this one must not acquire any.
      //
      // ⚠️ WHAT GOES WRONG OTHERWISE, because every step looks innocent. The picker yields local
      // midnight; `JSON.stringify` calls `Date.prototype.toJSON`, which is `toISOString`, so it
      // leaves as UTC with a Z; CRM's `MyDateTimeConverter.ReadJson` runs `DateTime.TryParse`, and
      // a trailing Z makes TryParse convert to SERVER-local. A date picked in Central was being
      // stored as 01:00 Eastern -- right day, wrong by an hour, and one zone change away from
      // landing on the day before. Advisors east of the server lose a day outright.
      //
      // 🟢 A zone-less string parses to `Kind=Unspecified` at 00:00, so nothing converts it:
      // `toLocal()` in RequestHelper only acts on `Kind=Utc`. CRM needs no change for this.
      //
      // ⚠️ THE CAST IS DELIBERATE, not a type bug. The generated client types this `Date` because
      // the server says `DateTime?`, and `"2026-10-11"` deserialises to that perfectly well -- it
      // is the same contract, sent in the form that cannot be re-zoned. Regenerating the client
      // would not help: the field has to stay `DateTime?` server-side.
      deliveryStartDate: this.startDateRequired() && controls.startDate.value
        ? (toCalendarDate(controls.startDate.value) as unknown as Date)
        : undefined,
      isOvernightDelivery: this.allowsOvernightDelivery() && controls.isOvernightDelivery.value,
      hasIaAuthority: this.selectedStandingInstruction()?.hasIaAuthority ?? undefined,
    };
  }

  /**
   * How the amount is split across the account's model sleeves (→ `req.ContributionSleeve` /
   * `req.DistributionSleeve`) — step 2 of a contribution or distribution.
   *
   * 🔴 THIS IS NOT AN ALLOCATION, and the distinction is the whole bug it fixes. A reallocation
   * states where the account should END UP, as a target percentage per model, in
   * `modelAllocations`. This states how much money moves INTO or OUT OF each sleeve on this one
   * request and changes nothing about the account's targets.
   *
   * ⚠️ Until this existed, step 2's amounts were collected, validated and blocked on — and then
   * DISCARDED at submit, exactly as the funding block was. Worse, `modelAllocations` was still
   * sent, empty, which CRM wrote as an empty Requested allocation: that asserts "the account
   * should end up holding no models", so the CRM tracker correctly badged every model REMOVED and
   * showed an account value missing everything the sleeves held. CRM now writes no requested
   * allocation for these two types at all.
   *
   * ⚠️ `percent` is the sleeve's share OF THE REQUEST, never Orion's standing
   * contribution/distribution percentage. The account holds those per sleeve and this request does
   * not change them; the two commonly differ, which is the reason the advisor is typing amounts
   * rather than accepting the default split.
   *
   * Rows left at zero are dropped: a sleeve no money moves through is not part of the request.
   * An empty list is legitimate — the whole amount can come from Custom Holdings.
   */
  private buildFundingSleeves(): AccountRequestV2FundingSleeve[] {
    const data = this.data();
    if (!data) return [];

    // The same lookup `buildCurrentModelAllocations` uses, so a sleeve resolves to the same model
    // id on both collections. Resolving twice by different rules is how one request ends up
    // describing the same sleeve two ways.
    const modelFor = buildModelLookup(data.models, data.allModels);
    const amountFor = new Map(this.sleeveAmountRows().map((r) => [r.sleeveKey, r.amount] as const));
    const pool = this.fundingAmount();

    return (data.sleeves ?? [])
      .filter((s) => s.accountId != null)
      .map((s) => ({ sleeve: s, amount: amountFor.get(String(s.accountId)) ?? 0 }))
      .filter(({ amount }) => amount > EPS)
      .map(({ sleeve, amount }) => {
        const modelId = modelFor(sleeve)?.id ?? '';
        return {
          // Guarded, as the allocation rows are: `Number('')` is 0, and a 0 would persist as a
          // sleeve pointing at a model that does not exist. Absent is the honest answer.
          investmentAccountModelID: modelId ? Number(modelId) : undefined,
          // Not carried on `SleeveAllocation`; the trade team reads the model name. Sent as
          // undefined rather than an empty string so the column stays null.
          accountNumber: undefined,
          amount: round2(amount),
          // Derived from what the rows actually hold, not read off the grid's display figure, so
          // it cannot disagree with `amount` above.
          percent: pool > 0 ? round2((amount / pool) * 100) : undefined,
        };
      });
  }

  /**
   * A contribution's funding (→ `req.Contribution`). One amount and nothing else: nothing is
   * withheld from money arriving, so there is no gross/net pair and no tax to record.
   */
  buildContribution(): AccountRequestV2Contribution {
    return {
      amount: this.fundingAmount(),
      ...this.buildDelivery(),
      sleeves: this.buildFundingSleeves(),
    };
  }

  /**
   * A distribution's funding (→ `req.Distribution`).
   *
   * 🔴 BOTH FIGURES ARE SENT, whichever box the advisor typed in. `fundingAmount()` is always the
   * gross — what leaves the account — because a net entry is converted up at entry; `netAmount()`
   * is what the client receives. Neither is recoverable from the other afterwards, since the
   * account's withholding elections can be changed once the request is filed, so storing one and
   * deriving the other later would answer today's question with tomorrow's percentages.
   *
   * ⚠️ `netAmount` was documented as display-only and never sent. That changed here, deliberately
   * (AndyP, 2026-09-22): the trade team acts on the gross, but the client was QUOTED the net, and
   * the request is the record of what the advisor was shown.
   *
   * ⚠️ On a close, `fundingAmount()` is the whole account rather than anything typed — the amount
   * box is not shown at all — and `isCloseDateBilled` rides along. It defaults to **true** on the
   * form, matching v1's `billAccountOnClosing`, and is sent unconditionally rather than only when
   * the close is ticked: the column is `bit NOT NULL`, and a value that appears only sometimes is
   * indistinguishable from one that was answered "no".
   */
  buildDistribution(): AccountRequestV2Distribution {
    const controls = this.fundingForm.controls;
    return {
      grossAmount: this.fundingAmount(),
      netAmount: this.netAmount(),
      federalTaxPercent: this.federalTaxPct(),
      stateTaxPercent: this.stateTaxPct(),
      // What was on file when the wizard opened (ACMTECH-1031), so the CRM tracker can say
      // "10% → 20%" rather than only going red. Already in hand: `funding()` holds it for
      // `taxWithholdingChanged`, which drives the standing-election notice on this step.
      //
      // ⚠️ `?? undefined` converts NULL ONLY. A 0 on file is a real election -- the advisor
      // elected no withholding -- and must travel as 0, because raising it to 10% is exactly
      // the change this is here to show. Null means Schwab gave us no baseline at all.
      priorFederalTaxPercent: this.funding()?.federalTaxWithholding ?? undefined,
      priorStateTaxPercent: this.funding()?.stateTaxWithholding ?? undefined,
      isCloseAccount: this.isCloseAccount(),
      isCloseDateBilled: controls.isCloseDateBilled.value,
      // WHO THE MONEY IS GOING TO. Null wherever the question did not arise -- which is every
      // method whose AsksRecipientType flag is off, and every destination already on file.
      //
      // ⚠️ SENT AS NULL RATHER THAN OMITTED, so the payload's shape does not depend on which
      // branch the advisor took. It spent a day on `buildPayee`; a wire asks the same question
      // and builds no payee, so it is back here where both methods can reach it.
      deliveryRecipientTypeID: this.recipientTypeApplies()
        ? controls.deliveryRecipientTypeId.value ?? undefined
        : undefined,
      ...this.buildDelivery(),
      sleeves: this.buildFundingSleeves(),
    };
  }

  /**
   * Where a third-party check is posted (→ `req.AccountRequestPayee`), or undefined.
   *
   * 🔴 ON THE REQUEST ROOT, not inside `buildDistribution`, because the table hangs off
   * `req.AccountRequest` — a contribution could want the same block one day and that would then
   * cost a screen rather than a second table.
   *
   * ⚠️ Gated on {@link payeeRequired}, so switching the method or the destination after typing
   * an address leaves nothing behind. A posting address filed against a wire would be read by the
   * trade team as an instruction, and there is no check for them to post.
   *
   * Trimmed, and `addressContinued` collapses to undefined when blank rather than storing an
   * empty string: the column is nullable and "no apartment line" is an absence, not a value.
   */
  private buildPayee(): AccountRequestV2Payee | undefined {
    if (!this.payeeRequired()) return undefined;
    const p = this.fundingForm.controls.payee.controls;
    const trimmed = (v: string): string | undefined => {
      const s = v.trim();
      return s.length > 0 ? s : undefined;
    };
    return {
      payeeName: trimmed(p.payeeName.value),
      streetAddress: trimmed(p.streetAddress.value),
      addressContinued: trimmed(p.addressContinued.value),
      city: trimmed(p.city.value),
      state: trimmed(p.state.value),
      zip: trimmed(p.zip.value),
    };
  }

  buildRequest(): AccountRequestV2ModelBase {
    const cashAmount = this.cashInput();
    const context = this.data()?.portfolio ?? null;
    return {
      investmentAccountID: this.investmentAccountId() ?? 0,
      // 🔴 NOT SENT AT ALL, DELIBERATELY (AndyP, 2026-10-05). The picker no longer asks who
      // initiated the request, so there is no answer to file and we do not invent one. `false`
      // would assert the client asked for it and `true` that the advisor did -- both are
      // regulatory statements about a conversation nobody recorded, and the old default made one
      // of them on every request. The key is omitted, `AccountRequest*ModelBase` takes it as
      // `bool?`, and it reaches [req] as NULL -- `bit` with no NOT NULL -- reading as "not asked".
      //
      // ⚠️ `undefined`, NOT `null`, and not a cast. The API models were widened to `bool?`
      // under ACMTECH-1014, so the generated client now types this `boolean | undefined` and the
      // honest value type-checks on its own. A cast here would hide the next contract change.
      isSolicited: undefined,
      // Sent rather than inferred server-side: one endpoint and one CRM writer serve both
      // request types now, and this is the only field that tells them apart. It decides
      // the AccountRequestTypeID, the CRM task category, and the name the trade team reads
      // in their queue.
      accountRequestTypeID: this.requestType(),
      // Captured as the advisor's session saw them; they can drift before the trade team
      // actions the request, and ACMTECH-903 needs the ones it was built against.
      orionEclipsePortfolioID: context?.portfolioId ?? undefined,
      orionConnectRegistrationID: context?.registrationId ?? undefined,
      sells: this.soldPositions().map((p) => ({
        ticker: p.ticker,
        // Carried so CRM can name the security on a sell row, as it already does on a buy.
        // req.TradeOrder.Description is shared by both actions; only this mapping was missing it.
        description: p.description || undefined,
        sharesToSell: p.sharesToSell,
        soldValue: p.soldValue,
        price: p.price ?? undefined,
        orionEclipseSecurityID: p.securityId ?? undefined,
        // ECLIPSE's type id; CRM resolves it against req.SecurityType.OrionEclipseSecurityTypeID.
        // Undefined when Orion did not type the holding -- CRM files those as Unknown, which is
        // NOT the same as the Custom it files a hand-typed row under. See ACMTECH-976.
        orionEclipseSecurityTypeID: p.securityTypeId ?? undefined,
        priceAsOf: this.loadedAt() ?? undefined,
        // Which req.TradeAction the row is filed under -- Sell All (3) rather than Sell (2).
        // Sent rather than left for CRM to work out: the payload carries the shares being SOLD
        // and never the shares HELD, so there is nothing on a sell row to compare against, and
        // a position with no share count reports 0 sold against a full value.
        isSellAll: p.isSellAll,
      })),
      // Always sent; `buys()` is already empty when the step is flagged off, so the
      // payload's shape never depends on a flag. An empty list writes nothing.
      buys: this.actionableBuys().map((b) => ({
        ticker: b.security?.ticker,
        description: b.security?.description || undefined,
        // Undefined, never 0, on a hand-typed row. That row was entered as an amount
        // because nothing priced the ticker, so there is no share count to send - and a 0
        // would read as an order for no shares rather than as one still to be worked out.
        sharesToBuy: b.needsTradeTeamReview ? undefined : b.shares,
        // Carries the advisor's own figure on a hand-typed row and the derived estimate
        // on a priced one. Same field either way: it is what the money is, and the missing
        // OrionEclipseSecurityID below is what says which kind of row produced it.
        estimatedValue: b.estimatedAmount,
        price: b.security?.price ?? undefined,
        // Null on a hand-typed ticker, and that absence IS the signal: a trade order with
        // no OrionEclipseSecurityID is one the trade team has to resolve by hand. No
        // separate flag is sent, so nothing can claim "reviewed" while the id is missing.
        orionEclipseSecurityID: b.security?.securityId ?? undefined,
        // Undefined on a hand-typed row, alongside the security id above -- and it is the PAIR
        // that identifies it. CRM files a row with neither as Custom; one with a security id but
        // no type as Unknown. Sending a stand-in type here would erase that distinction.
        orionEclipseSecurityTypeID: b.security?.securityTypeId ?? undefined,
        // The security's OWN priceAsOf, not the account load time. This is when ORION
        // priced the security, so it is independent of the wizard session and can fall
        // either side of it — observed 17:05 against holdings read at 18:20.
        priceAsOf: b.security?.priceAsOf ?? undefined,
      })),
      // CUSTODIAL CASH THE ADVISOR FREED to fund this request.
      //
      // ⚠️ ITS OWN LIST, not rows added to `sells`. CRM files each list under its own
      // req.TradeAction, and these go under 4 (Cash) rather than 2/3 -- nobody executes a cash
      // release, and anything summing sells must not pick them up. Putting them in `sells` with a
      // marker would have made every existing reader's "is this a sell" test wrong by default.
      //
      // ⚠️ The value is what this request USES. The remainder of the same position files below as
      // a custom holding at its kept value, so the two add back to what the account held -- which
      // is what lets the trade team see the request is funded.
      //
      // Always sent; empty when nothing was freed, so the payload's shape never depends on what
      // the advisor happened to do.
      releasedCash: this.releasedCashRows().map((c) => ({
        ticker: c.ticker,
        description: c.description,
        releasedValue: c.released,
        orionEclipseSecurityID: c.securityId ?? undefined,
        // The REAL type, not an override to CASH. See `releasedCashRows`.
        orionEclipseSecurityTypeID: c.securityTypeId ?? undefined,
        // When the balance was read, which is the account load -- a cash figure is point-in-time
        // like every other figure here.
        priceAsOf: this.loadedAt() ?? undefined,
      })),
      // The resulting Custom Holdings sleeve: positions kept, merged with anything bought,
      // plus custodial cash as its own line.
      //
      // `resultingHoldings()` rather than `sleevePositions()` — safe now, and only now,
      // because `buys` above carries the instructions that create the extra shares. Before
      // that existed this asserted a position with nothing to produce it: a row reading
      // 150 AAPL while nothing told the trade team to acquire the extra 50. With the buy
      // step off both collapse to the same thing, so this is correct in every flag state.
      customHoldings: [
        ...this.resultingHoldings().map((p) => ({
          ticker: p.ticker,
          description: p.description,
          keptShares: p.shares ?? undefined,
          keptValue: p.value,
          price: p.price ?? undefined,
          isDoNotTrade: p.isDoNotTrade,
          orionEclipseSecurityID: p.securityId ?? undefined,
          // Merged across the kept and bought sides, preferring whichever had one.
          orionEclipseSecurityTypeID: p.securityTypeId ?? undefined,
          priceAsOf: this.loadedAt() ?? undefined,
          // ⚠️ FALSE, not undefined. These are the securities, and both trackers read null as
          // "filed before the column existed" and fall back to their ticker lists -- so a null
          // from a request that could have answered puts a modern request on the legacy path.
          isCash: false,
        })),
        // SLEEVE CASH THE ADVISOR KEPT -- one line per cash row, at what is left of it.
        //
        // ⚠️ Kept sleeve cash and structured cash are DIFFERENT INSTRUCTIONS and are filed as
        // separate lines. This one says "this much of the account's own cash stays where it is";
        // the synthetic line below says "park this much, from outside the sleeve". One combined
        // figure would hide which is which from the trade team, and the two have different
        // origins and different follow-up.
        //
        // 🟢 These carry their REAL ticker, description and Eclipse security id, because they are
        // real holdings -- so they need no new field to be told apart from the synthetic line,
        // which has no security id at all. That is what let "separate lines" happen without a
        // client or contract change.
        //
        // ⚠️ Rows released in full are omitted rather than filed at zero: a $0.00 holding is not
        // a holding, and the sleeve no longer contains it.
        //
        // ⚠️ NOT routed through `resultingHoldings()`, which merges on { securityId, ticker }.
        // An account's cash row is commonly tickered CASH, and merging it with the structured
        // line below is exactly the collapse these two lines exist to avoid.
        ...this.keptCashRows().map((c) => ({
          ticker: c.ticker,
          description: c.description,
          // 1 share = $1 for cash, as the structured line already assumes.
          keptShares: c.kept,
          keptValue: c.kept,
          price: 1,
          isDoNotTrade: false,
          orionEclipseSecurityID: c.securityId ?? undefined,
          // Whatever Orion typed the cash holding as -- normally 2 (CASH), but read from the row
          // rather than assumed, because this line IS a real holding and should say what it is.
          orionEclipseSecurityTypeID: c.securityTypeId ?? undefined,
          priceAsOf: this.loadedAt() ?? undefined,
          // ⚠️ THE LINE THIS FLAG EXISTS FOR. It files under the custodian's own ticker --
          // CUSTODIAL_CASH, FDRXX, SWGXX -- so no ticker list could ever recognise it, and the
          // type above cannot either: Orion types custodial cash by its VEHICLE, which is why a
          // sweep money market reads as a Mutual Fund. Without this it was drawn among the
          // advisor's securities on both trackers (ACMTECH-983).
          isCash: true,
        })),
        // STRUCTURED CASH from step 4 -- outside today's sleeve. Synthetic, so no security id.
        //
        // ⚠️ Tickered PROTECTED CASH, not CASH, and that is worth keeping. The account's own cash
        // row is commonly tickered CASH by Orion, so this line used to share a ticker with a real
        // holding and the two were told apart ONLY by `orionEclipseSecurityID` being absent here.
        // A distinct ticker means they cannot be confused by anything downstream that groups or
        // upserts by ticker -- which is the open question this closes (see ACMTECH-973) -- and it
        // stops the word CASH appearing twice in one group on Review & Submit.
        //
        // Not a real exchange ticker, but neither is CASH, and neither is the CUSTODIAL_CASH that
        // Orion itself sends for the account's cash row. This column names the line for a human.
        // CASH SET ASIDE FROM REVIEWABLE SALES -- proceeds this request deliberately does not
        // spend.
        //
        // ⚠️ A THIRD kind of cash line, and it is not either of the other two. Kept sleeve cash is
        // money nobody touched; PROTECTED CASH is money the advisor chose to hold back; this is
        // money the FIRM holds back, because a sale of anything outside Mutual Fund / CASH /
        // Equity cannot be committed to a model or a purchase inside one request.
        //
        // ⚠️ It must reach CRM. The custom-holding lines are what CRM sums for the tracker's
        // Custom Holdings figure, so omitting this would leave the two surfaces disagreeing about
        // the account's composition -- which looks exactly like the ACMTECH-973 class of bug even
        // though nothing is wrong.
        //
        // ⚠️ Ticker is 20 of the 25 characters `req.CustomHolding.Ticker` allows. Any rewording
        // has five to play with before it truncates at insert.
        //
        // ⚠️ "PENDING", not "PROTECTED" (AndyP, 2026-09-14). Both the portal and CRM trackers
        // recognise their synthetic cash lines by LITERAL ticker, so a rename here has to be
        // matched in both -- that mismatch is exactly what badged the protected-cash line as
        // Trade Team work when CASH became PROTECTED CASH.
        ...(this.reviewSoldValue() > EPS
          ? [{
            // ⚠️ RENAMED from PENDING CASH - SELLS on 2026-09-15. Both request trackers keep
            // every earlier spelling in their cash-ticker lists -- a synthetic line missing from
            // one has no security id, reads as hand-typed and is badged for Trade Team review,
            // inventing work on a row nobody entered. Requests filed before today carry the old
            // ticker for good, so the old string is retained there rather than replaced.
            ticker: 'PENDING SELLS',
            description: 'Estimated liquidated proceeds from alternative securities being sold.',
            keptShares: round2(this.reviewSoldValue()),
            keptValue: round2(this.reviewSoldValue()),
            price: 1,
            isDoNotTrade: false,
            orionEclipseSecurityID: undefined,
            // ⚠️ CASH (2), stated for the same reason the line below states it: with no type AND
            // no security id, CRM's resolver reads this as a HAND-TYPED row and files it as
            // `Custom` -- putting "Custom" on the tracker against a line nobody entered.
            orionEclipseSecurityTypeID: 2,
            priceAsOf: undefined,
            // Cash, and stated even though the ticker lists already recognise this line. The
            // flag is the test now; the lists are the fallback for requests filed before it
            // existed, and a synthetic line that says nothing would depend on that fallback.
            isCash: true,
          }]
          : []),
        ...(cashAmount > 0
          ? [{
            // ⚠️ RENAMED from PROTECTED CASH / "Protected Cash" on 2026-09-15, and the TICKER is
            // the half that matters: both request trackers keep a list of the synthetic cash
            // tickers the wizard invents, and a line missing from that list has no security id,
            // reads as hand-typed, and gets badged for Trade Team review -- work invented on the
            // one row nobody entered. That has already happened once, when CASH became PROTECTED
            // CASH and the lists were not updated. Both trackers keep ALL the old spellings, so
            // requests filed before today still read correctly.
            ticker: 'SET-ASIDE CASH',
            description: 'Set-Aside Cash',
            keptShares: cashAmount,
            keptValue: cashAmount,
            price: 1,
            isDoNotTrade: false,
            orionEclipseSecurityID: undefined,
            // ⚠️ CASH (Eclipse type 2), and the ONE place this payload states a type rather than
            // forwarding one. Everywhere else an absent type plus an absent security id means
            // "hand-typed", which CRM files as Custom -- and this line matches that shape exactly
            // while meaning nothing like it. Nobody typed it; the wizard synthesised it. It is not
            // a security, but it IS cash, so saying so is both true and the only way to keep the
            // one row nobody entered from being labelled as an advisor's manual entry.
            orionEclipseSecurityTypeID: 2,
            priceAsOf: undefined,
            // Cash, for the same reason as the line above: the flag is the test, the ticker
            // lists are only the fallback for requests that predate it.
            isCash: true,
          }]
          : []),
      ],
      // ⚠️ EMPTY ON A CONTRIBUTION OR DISTRIBUTION, and that emptiness is NOT harmless. Those two
      // types state no target allocation — their step 2 says how much moves through each sleeve,
      // which goes in the funding block's `sleeves` instead. CRM writes no Requested allocation
      // row for them at all now, because an EMPTY one asserts "the account should end up holding
      // no models" and every reader diffing it against the before-state said exactly that.
      modelAllocations: this.allocation().rows.map((r) => ({
        // Guarded, because `Number('')` is 0 and a row whose model never resolved carries
        // an empty modelId. req.AllocationSleeve has no FK to inv.InvestmentAccountModel —
        // the CRM read model resolves names by id by hand — so a 0 would persist silently
        // and read back as a sleeve pointing at a model that does not exist. Absent is the
        // honest answer, and it matches what buildCurrentModelAllocations already does.
        investmentAccountModelID: r.modelId ? Number(r.modelId) : undefined,
        allocatedValue: r.allocatedValue,
        targetPercent: r.effectiveTarget,
        distributionPercent: r.effectiveDistribution,
        contributionPercent: r.effectiveContribution,
        orionConnectAccountID: r.orionConnectAccountId ?? undefined,
        // The agg for the model this row carries NOW.
        //
        // A row being NEW means no Connect ACCOUNT exists yet — it does not mean the model
        // is unknown. Leaving this null on an added sleeve would tell ACMTECH-903 to create
        // a sleeve without saying which model to put in it, which is the one thing it needs.
        //
        // An UNCHANGED pre-loaded row prefers the value Orion gave us for that sleeve: it is
        // ground truth for the sleeve, whereas the CRM model-to-agg mapping (ACMTECH-909) is a
        // maintained lookup that can be missing or stale.
        //
        // A CHANGED row must not use it at all -- the retained value belongs to the model being
        // REPLACED. Sending it hands ACMTECH-903 a row meaning "this sleeve, which holds model A,
        // and the model is B", and nothing downstream can tell which half to believe. The ACCOUNT
        // id above is deliberately kept: the sleeve is repurposed, not replaced.
        //
        // Compared against sleeveModelId rather than cleared on change, so a swap-and-swap-back
        // returns to Orion's own value instead of permanently degrading to the CRM mapping.
        orionConnectModelAggID:
          r.sleeveModelId != null && r.modelId === r.sleeveModelId
            ? r.orionConnectModelAggId ?? this.modelOption(r.modelId)?.modelAggId ?? undefined
            : this.modelOption(r.modelId)?.modelAggId ?? undefined,
        accountNumber: undefined,
        // A row with no Connect account behind it is a sleeve that does not exist yet.
        // Derived rather than tracked separately so the flag and the ids cannot disagree.
        isNew: r.orionConnectAccountId == null,
        // Sent rather than resolved server-side, and stored per row rather than joined at read
        // time, because a filed request is an ATTESTATION: the advisor confirmed 100% of the
        // account, an external manager and an LPOA against the model as it stood today. Someone
        // flipping the model's flag later must not be able to rewrite what was agreed.
        //
        // ⚠️ `isSMA`, not `isSma` — nswag camel-cases the C# `IsSMA` acronym as a unit.
        // `InvestmentModel.isSma` is a different DTO spelled the other way; they are not
        // interchangeable and TypeScript will not catch a mix-up on an optional field.
        isSMA: this.modelOption(r.modelId)?.isSma ?? false,
        // 🔴 NEVER ON A REQUESTED ROW, and stated rather than omitted -- the generated
        // property is `number | undefined`, which is REQUIRED-but-nullable, so leaving it
        // out is a compile error rather than a quiet default.
        //
        // A draw can only apply to a sleeve the account ALREADY HOLDS, so the current
        // collection always has a row for it while this one may not: free money from a
        // model on Raise Cash, then remove that model here, and no requested row is
        // left to carry the figure. One collection owns it, and it is the one that cannot
        // lose the row (ACMTECH-1040).
        cashRaised: undefined,
      })),
      currentModelAllocations: this.buildCurrentModelAllocations(),
      snapshot: this.buildSnapshot(),
      // `?? undefined` only because the generated model types it that way; the field is
      // always populated deliberately — see requestNotesForPayload.
      notes: this.requestNotesForPayload() ?? undefined,
      // FUNDING — the ONLY type-specific part of this payload, and the reason a contribution or
      // distribution could not be filed until now. Everything above is shared by all four request
      // types; the amount, the delivery method, the bank account, the frequency, the withholding,
      // the start date and the close flags were collected, validated and blocked on by the wizard
      // and then thrown away at submit, because nothing carried them.
      //
      // ⚠️ EXACTLY ONE, and never both. The API rejects a mismatched pair and so does CRM —
      // deliberately, rather than ignoring the surplus: a distribution carrying contribution
      // funding is a caller bug, and the missing-block case is worse still, because every other
      // part of the request writes happily without it and the result reads as a complete request
      // whose money never moves.
      //
      // Reallocation and Initial Account send neither: the account itself is the pool, there is
      // no amount and nothing to deliver.
      contribution: this.isContribution() ? this.buildContribution() : undefined,
      distribution: this.isDistribution() ? this.buildDistribution() : undefined,
      payee: this.buildPayee(),
    };
  }

  submit(): void {
    if (this.investmentAccountId() == null) {
      this.snackbar.openSnackbar(
        'This request has no linked account, so it cannot be submitted.',
        ToastClassEnum.warning,
      );
      return;
    }

    // Guarded here as well as on the button: a request that instructs nothing costs the
    // trade team a review and is indistinguishable from a mistake.
    if (!this.hasChanges()) {
      this.snackbar.openSnackbar(
        'Nothing has been changed, so there is nothing to submit.',
        ToastClassEnum.warning,
      );
      return;
    }

    // sent as the multipart files part; null when nothing was attached
    const attachments = this.filesForPayload().map((file) => ({ data: file, fileName: file.name }));

    this.isSubmitting.set(true);
    this.requestClient
      .createAccountRequestV2(attachments.length ? attachments : null, this.buildRequest())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSubmitting.set(false);
          this.isSubmitted.set(true);
          this.isComplete.set(true);
        },
        error: () => {
          this.isSubmitting.set(false);
          this.snackbar.openSnackbar(
            'There was an error submitting the request. Please try again.',
            ToastClassEnum.warning,
          );
        },
      });
  }
}

/**
 * A `Date` as the calendar date it represents locally — `2026-10-11`.
 *
 * 🔴 LOCAL GETTERS, NEVER `toISOString()`. The whole point is to emit the day the advisor saw on
 * the picker, and `toISOString` re-expresses the instant in UTC — which for any zone west of
 * Greenwich is how a date picked late in the day becomes the next one. `getFullYear`/`getMonth`/
 * `getDate` read the local fields the picker set, so the string says what the advisor chose.
 *
 * ⚠️ `getMonth()` is 0-based; the `+ 1` is not an off-by-one.
 */
function toCalendarDate(value: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
}

/** Round to 2 decimal places (matches the mockup's percentage precision). */
function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * "a", "a and b", "a, b and c" — for naming what an action is about to discard.
 *
 * Serial comma omitted to match the rest of the wizard's copy.
 */
function joinPhrases(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** Currency for messages built in the store, where the template's pipes are unavailable. */
const MONEY = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
function formatMoney(value: number): string {
  return MONEY.format(value);
}
